import React, { useEffect, useMemo, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import api from "@/lib/api";
import { toast } from "sonner";
import AdminAmendDialog from "@/components/AdminAmendDialog";
import HoldReasonDialog from "@/components/HoldReasonDialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  PlusIcon, TrashIcon, PencilSimpleIcon, PrinterIcon, LinkIcon,
  GlobeIcon, PackageIcon, CoinsIcon, FileTextIcon, MagnifyingGlassIcon,
  EyeIcon, DownloadSimpleIcon, XIcon, CheckCircleIcon, ProhibitIcon,
  PaperPlaneTiltIcon,
} from "@phosphor-icons/react";
const InvoiceIcon = CoinsIcon;
const DocIcon = FileTextIcon;
import { useAuth } from "@/lib/auth";
import { exportToCSV } from "@/lib/csv";
import TwoLineDocNo from "@/components/TwoLineDocNo";
import StatusBadge from "@/components/StatusBadge";
import { baseArticle } from "@/lib/article";
import { printDocument, printSignedDocument } from "@/lib/print";
import { ArticleCombobox } from "./SalesOrders";
import { fmtDateTimeIST, fmtDateIST } from "@/lib/datetime";
import DocumentInfo from "@/components/DocumentInfo";
import { DocDownloadButtons, DocDownloadIcons, downloadDoc, useDocStyles } from "@/components/DocDownloadActions";
import { RowActionsMenu } from "@/components/RowActionsMenu";
import { FulfilmentChoice, OrderRef, StockBadge, fulfilmentOf, isStockOrder } from "@/components/StockBadge";
import SearchablePicker from "@/components/SearchablePicker";
import { useSearchParams, Link } from "react-router-dom";
import PackingSlipsTab from "./PackingSlipsTab";

import FormattedNumberInput from "@/components/FormattedNumberInput";
import ModifyDocumentPopover from "@/components/ModifyDocumentPopover";
import LineHistoryDrawer from "@/components/LineHistoryDrawer";
import LineQtyActions from "@/components/LineQtyActions";
import { LineQtyBreakdown, ProtectionBadge } from "@/components/LineQtyBreakdown";
import { PROTECTION_TEXT, canViewQty, isActionablePending, isSubmittedPendingApproval, useQtyPositions } from "@/lib/qtyDisplay";
// Mar 1, 2026 · Reuse the domestic DeliveryChallansTab inside Exports so
// the Export DC experience mirrors SO 1:1 (LR No, Transporter, Driver,
// Vehicle, Pre-carriage / Transport details). Lightweight replica per
// user choice — same rules, same collection.
import { DeliveryChallansTab } from "@/pages/OrderExtras";
import { authStatusLabel } from "@/lib/authTerminology";

// Feb 2026 · Qty display helper — Order/Total/Dispatched/Pending Qty render as
// comma-grouped integers (1000 → "1,000") everywhere they show as read-only.
const fmtQtyInt = (v) => (Number(v) || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const inp = "w-full h-9 px-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-white text-sm focus:outline-none focus:border-[var(--accent)]";
const selCls = "bg-[var(--bg-0)] border-[var(--border)] text-white h-9";

const INCOTERMS = ["FOB", "CIF", "EXW", "DDP", "DAP", "CFR", "FCA", "CIP"];
const CURRENCIES = ["USD"];
const PAY_TERMS = ["LC", "TT", "DP", "DA"];
const GST_TYPES = [
  { v: "LUT", label: "LUT (0% IGST)" },
  { v: "IGST_5", label: "IGST 5%" },
  { v: "IGST_12", label: "IGST 12%" },
  { v: "IGST_18", label: "IGST 18%" },
];
const CONTAINER_TYPES = ["20FT", "40FT", "40HC", "LCL"];
const STATUSES = ["Draft", "Confirmed", "Shipped", "Closed"];

// Aug 31, 2026 · Zero-Trust — hardcoded role-set removed. Edit
// authority now flows through the DB via `hasPerm(...)`. See
// `canEdit` inside `Exports()` below.

export default function Exports() {
  const { user, hasPerm } = useAuth();
  const canEdit = user?.role === "Administrator"
    || hasPerm?.("exports:create") || hasPerm?.("exports:edit");
  // RBAC-EXP-DSP-01 · D4 — EPL / ECI actions follow their own modules (backend parity).
  const canP = (p) => user?.role === "Administrator" || !!hasPerm?.(p);
  const eplPerms = { create: canP("exports_epl:create"), edit: canP("exports_epl:edit"), del: canP("exports_epl:delete") };
  const eciPerms = { create: canP("exports_invoices:create"), edit: canP("exports_invoices:edit"), submit: canP("exports_invoices:submit") || canP("exports_invoices:create"), cancel: canP("exports_invoices:cancel") };
  // Feb 2026 · Deep-link sub-tab via ?tab= query param so the sidebar's
  // Packing Slips / Packing Lists / Invoices / Status Report menu items
  // land directly on the right tab. Two-way bound with the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (() => {
    const t = searchParams.get("tab");
    return ["orders", "slips", "epl", "dc", "invoices", "report"].includes(t) ? t : "orders";
  })();
  const [tab, setTabInternal] = useState(initialTab);
  const setTab = (v) => {
    setTabInternal(v);
    const next = new URLSearchParams(searchParams);
    if (v === "orders") next.delete("tab"); else next.set("tab", v);
    setSearchParams(next, { replace: true });
  };
  useEffect(() => {
    const t = searchParams.get("tab") || "orders";
    if (["orders", "slips", "epl", "dc", "invoices", "report"].includes(t) && t !== tab) {
      setTabInternal(t);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-1 flex items-center gap-2">
            <GlobeIcon size={12} /> International Trade
          </div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white">Export Management</h1>
          <div className="text-xs text-zinc-500 mt-1">
            ESO → EPL → ECI documentation workflow · linked to Production Plans + Packing Slips
            {!canEdit && <span className="ml-2 text-amber-400">· read-only</span>}
          </div>
        </div>
        {/* Mar 1, 2026 · Header action slot — mirrors the SO header. The
            actual "New Export SO" + "Export CSV" buttons live inside
            OrdersTab (and the Status Report tab hosts its own Print / Export
            controls) so they only show when the relevant tab is active,
            same UX pattern as Sales Orders. */}
        <div id="exp-header-actions" className="flex items-center gap-2"></div>
      </div>

      {/* Sub-tabs · under-the-title underline pattern, parity with SO */}
      <div className="flex gap-2 border-b border-[var(--border)]">
        {[
          { k: "orders",   label: "Export Sales Order"        },
          { k: "slips",    label: "Export Packing Slip"       },
          { k: "epl",      label: "Export Packing List"       },
          { k: "dc",       label: "Export DC"                 },
          { k: "invoices", label: "Export Commercial Invoice" },
          { k: "report",   label: "Export Order Status"       },
        ].map(t => (
          <button
            key={t.k}
            data-testid={`exp-tab-${t.k}`}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.k
                ? "border-[var(--accent)] text-white"
                : "border-transparent text-zinc-400 hover:text-white"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "orders"   && <OrdersTab   canEdit={canEdit} />}
      {tab === "slips"    && <ExportPackingSlipsTab />}
      {tab === "epl"      && <PackingTab  perms={eplPerms} />}
      {tab === "dc"       && <DeliveryChallansTab isExport />}
      {tab === "invoices" && <InvoicesTab perms={eciPerms} />}
      {tab === "report"   && <EsoStatusReport />}
    </div>
  );
}

// ============================================================================
//  Export Packing Slips (EPS) · Mar 2026 · Full replica of the domestic
//  Packing Slips module via `mode="export"` — Single Entry, Bulk Entry, and
//  Barcoding are literally the same component (`PackingSlipsTab`). Source
//  is locked to Export SO (ESO) / Excess-vs-ESO so `_next_eps_slip_no()`
//  on the backend mints EPS-YY-YY-NNNNN. UI, columns, action buttons all
//  mirror the domestic tab — muscle-memory preserved end-to-end.
// ============================================================================
function ExportPackingSlipsTab() {
  return (
    <div data-testid="export-slips-tab">
      <PackingSlipsTab mode="export" />
    </div>
  );
}

// ============================================================================
//  ESO Status Report (Feb 2026) — mirrors the SO Status Report
// ============================================================================
function EsoStatusReport() {
  const [data, setData] = useState({ rows: [], generated_at: "" });
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [preview, setPreview] = useState(null);
  // Feb 28, 2026 · Per-line audit drawer (edits · short-closes · excess dispatches).
  const [historyLine, setHistoryLine] = useState(null);
  const [woSummary, setWoSummary] = useState({});
  // Feb 2026 · Column-visibility picker (persisted per user in localStorage).
  // Mar 3, 2026 · Sequence: Buyer + Buyer PO adjacent; Item → Style →
  // Product Code kept as a triplet everywhere.
  // Phase 2C-5B4 · default column order mirrors SO Order Status — locked
  // operational sequence: Product → Order Qty → Buffer → Total → Dispatch
  // → Pending → Status → Hold/Short-Closed/Cancelled → Rate/Amount, then
  // secondary export-reference fields. All fields remain selectable.
  const ALL_COLS = [
    { k: "order_date", label: "Order Date" },
    { k: "buyer",      label: "Buyer" },
    { k: "buyer_po",   label: "Buyer PO" },
    { k: "item",       label: "Item No" },
    { k: "style",      label: "Style No" },
    { k: "product_code", label: "Product Code" },
    { k: "order_qty",  label: "Order Qty" },
    { k: "original_qty", label: "Original Qty" },
    { k: "add_qty",      label: "Add Qty" },
    { k: "buffer_qty", label: "Buffer Qty" },
    { k: "total_qty",  label: "Total Qty" },
    { k: "dispatched", label: "Dispatch" },
    { k: "pending",    label: "Pending" },
    { k: "status",     label: "Status" },
    { k: "fulfilment", label: "Fulfilment" },
    { k: "hold_qty",         label: "Hold Qty" },
    { k: "short_closed_qty", label: "Short Closed Qty" },
    { k: "cancelled_qty",    label: "Cancelled Qty" },
    { k: "rate",       label: "Rate USD" },
    { k: "amount",     label: "Amount USD" },
    // secondary / export-reference fields
    { k: "delivery",   label: "Delivery Date" },
    { k: "article",    label: "Article" },
    { k: "hs",         label: "HS Code" },
    { k: "amount_inr", label: "Amount INR" },
    { k: "wo_status",  label: "Linked WOs" },
    { k: "pct",        label: "%" },
    { k: "risk",       label: "Delivery Risk" },
  ];
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const raw = localStorage.getItem("mes.eso_report.cols.v2");
      if (raw) return new Set(JSON.parse(raw));
    } catch (e) { /* ignore */ }
    // R2-01L A4-02 · G7 · optional Original / Add columns are OFF by default.
    return new Set(ALL_COLS.map(c => c.k).filter(k => k !== "original_qty" && k !== "add_qty"));
  });
  const [showColsMenu, setShowColsMenu] = useState(false);
  // Export Order Status V1 parity · local density toggle (matches SO Order Status).
  const [esoRepDensity, setEsoRepDensity] = useState(() => {
    try { return localStorage.getItem("mes.eso_report.density") || "comfortable"; } catch { return "comfortable"; }
  });
  const setEsoRepDensityPersisted = (d) => {
    setEsoRepDensity(d);
    try { localStorage.setItem("mes.eso_report.density", d); } catch (e) { /* ignore */ }
  };
  const toggleCol = (k) => setVisibleCols(prev => {
    const next = new Set(prev);
    next.has(k) ? next.delete(k) : next.add(k);
    try { localStorage.setItem("mes.eso_report.cols.v2", JSON.stringify(Array.from(next))); } catch (e) { /* ignore */ }
    return next;
  });
  const showAllCols = () => {
    const next = new Set(ALL_COLS.map(c => c.k));
    setVisibleCols(next);
    try { localStorage.setItem("mes.eso_report.cols.v2", JSON.stringify(Array.from(next))); } catch (e) { /* ignore */ }
  };
  const col = (k) => visibleCols.has(k);
  const totalCols = 3 + Array.from(visibleCols).length;

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [rep, wos] = await Promise.all([
          api.get("/reports/eso-status"),
          api.get("/work-orders/summary/by-source").catch(() => ({ data: {} })),
        ]);
        if (mounted) { setData(rep.data); setWoSummary(wos.data || {}); }
      } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load report"); }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, []);

  const filtered = data.rows.filter(r => {
    const stq = (r.status || "").toLowerCase();
    // Phase 2C-5B · "Open" = active/actionable only; terminal states drop out.
    const TERMINAL = ["closed", "completed", "cancelled", "canceled", "short closed", "rejected"];
    if (statusFilter === "open" && TERMINAL.includes(stq)) return false;
    if (statusFilter === "closed" && stq !== "closed") return false;
    if (search) {
      const q = search.toLowerCase();
      const arts = (r.articles || []).join(" ").toLowerCase();
      if (!((r.doc_no || "").toLowerCase().includes(q) || (r.buyer_name || "").toLowerCase().includes(q) || (r.buyer_po_no || "").toLowerCase().includes(q) || arts.includes(q))) return false;
    }
    return true;
  });
  const totals = filtered.reduce((a, r) => ({ order: a.order + (r.order_qty || 0), total: a.total + (r.total_qty || 0), disp: a.disp + (r.disp_qty || 0), pending: a.pending + (r.pending_qty || 0), hold: a.hold + (r.hold_qty || 0), shortClosed: a.shortClosed + (r.short_closed_qty || 0), cancelled: a.cancelled + (r.cancelled_qty_net || 0) }), { order: 0, total: 0, disp: 0, pending: 0, hold: 0, shortClosed: 0, cancelled: 0 });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Filter</div>
        <div className="inline-flex rounded-sm border border-[var(--border)] overflow-hidden">
          {[
            { k: "all", label: "All", count: data.rows.length },
            { k: "open", label: "Open", count: data.rows.filter(r => !["closed","completed","cancelled","canceled","short closed","rejected"].includes((r.status || "").toLowerCase())).length },
            { k: "closed", label: "Closed", count: data.rows.filter(r => (r.status || "").toLowerCase() === "closed").length },
          ].map(t => (
            <button key={t.k} data-testid={`eso-report-filter-${t.k}`} onClick={() => setStatusFilter(t.k)} className={`px-3 h-8 text-xs font-medium uppercase tracking-widest border-r border-[var(--border)] last:border-r-0 ${statusFilter === t.k ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-2)]"}`}>
              {t.label}<span className={`ml-1.5 font-mono text-[10px] ${statusFilter === t.k ? "opacity-80" : "text-zinc-500"}`}>{t.count}</span>
            </button>
          ))}
        </div>
        <div className="relative w-64 ml-2">
          <MagnifyingGlassIcon size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input data-testid="eso-report-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="ESO / Buyer / PO / Article No…" className="h-8 w-full pl-7 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs focus:outline-none focus:border-[var(--accent)]" />
        </div>
        <div className="ml-auto text-[10px] font-mono text-zinc-500" data-testid="eso-status-summary">
                              {filtered.length} ESO{filtered.length !== 1 ? "s" : ""} · Order {totals.order.toLocaleString("en-IN", { maximumFractionDigits: 0 })}m · Disp {totals.disp.toLocaleString("en-IN", { maximumFractionDigits: 0 })}m · Pending <span className="text-amber-400">{totals.pending.toLocaleString("en-IN", { maximumFractionDigits: 0 })}m</span>
        </div>
        {/* Feb 2026 · Column-visibility picker */}
        <div className="relative">
          <button
            data-testid="eso-report-cols-btn"
            onClick={() => setShowColsMenu(v => !v)}
            className="h-9 px-3 border border-[var(--border)] text-xs text-zinc-300 hover:bg-[var(--bg-2)] rounded-sm flex items-center gap-2"
            title="Show/hide columns"
          >
            <span className="font-mono">{visibleCols.size}/{ALL_COLS.length}</span>Columns
          </button>
          {showColsMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowColsMenu(false)} />
              <div data-testid="eso-report-cols-menu" className="absolute right-0 top-full mt-1 z-50 bg-[var(--bg-1)] border border-[var(--border)] rounded-sm shadow-xl w-52 py-1">
                <div className="px-3 py-1.5 border-b border-[var(--border)] flex items-center justify-between">
                  <span className="overline">Toggle Columns</span>
                  <button onClick={showAllCols} data-testid="eso-report-cols-all" className="text-[10px] uppercase tracking-widest text-[var(--accent)] hover:underline">Show All</button>
                </div>
                <div className="max-h-72 overflow-y-auto">
                  {ALL_COLS.map(c => (
                    <label key={c.k} data-testid={`eso-report-col-toggle-${c.k}`} className="flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-[var(--bg-2)]">
                      <input type="checkbox" checked={visibleCols.has(c.k)} onChange={() => toggleCol(c.k)} className="w-3.5 h-3.5 accent-[var(--accent)]" />
                      <span className="text-zinc-300">{c.label}</span>
                    </label>
                  ))}
                </div>
                <div className="px-3 py-1.5 border-t border-[var(--border)] text-[10px] text-zinc-500">Preference saved per user (localStorage).</div>
              </div>
            </>
          )}
        </div>
        <div className="inline-flex rounded-sm border border-[var(--border)] overflow-hidden" role="group" aria-label="Row density">
          {[["comfortable", "Comfy"], ["compact", "Compact"]].map(([k, l]) => (
            <button key={k} data-testid={`eso-report-density-${k}`} onClick={() => setEsoRepDensityPersisted(k)} className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1.5 ${esoRepDensity === k ? "bg-[var(--accent)]/15 text-[var(--accent)]" : "text-zinc-400 hover:text-zinc-200"}`}>{l}</button>
          ))}
        </div>
        <button
          data-testid="eso-report-print"
          onClick={() => printDocument({
            kind: "Export Sales Order Status Report",
            no: fmtDateTimeIST(data.generated_at || new Date().toISOString()),
            meta: [
              { label: "Filter", value: statusFilter.toUpperCase() },
              { label: "Total ESOs", value: filtered.length },
              { label: "Order Qty (m)", value: totals.order.toLocaleString("en-IN", { maximumFractionDigits: 0 }) },
              { label: "Dispatched (m)", value: totals.disp.toLocaleString("en-IN", { maximumFractionDigits: 0 }) },
              { label: "Pending (m)", value: totals.pending.toLocaleString("en-IN", { maximumFractionDigits: 0 }) },
            ],
            sections: [{
              heading: `ESO Summary · ${filtered.length}`,
              columns: [
                { key: "doc_no", label: "ESO No", mono: true },
                { key: "order_date", label: "Order Date", mono: true },
                { key: "buyer_name", label: "Buyer" },
                { key: "buyer_po_no", label: "Buyer PO", mono: true },
                { key: "status", label: "Status" },
                { key: "total_qty", label: "Total (m)", align: "right", fmt: v => fmtQtyInt(v) },
                { key: "disp_qty", label: "Dispatched (m)", align: "right", fmt: v => fmtQtyInt(v) },
                { key: "pending_qty", label: "Pending (m)", align: "right", fmt: v => fmtQtyInt(v) },
                { key: "hold_qty", label: "Hold Qty", align: "right", fmt: v => fmtQtyInt(v) },
                { key: "short_closed_qty", label: "Short Closed Qty", align: "right", fmt: v => fmtQtyInt(v) },
                { key: "cancelled_qty_net", label: "Cancelled Qty", align: "right", fmt: v => fmtQtyInt(v) },
                { key: "progress_pct", label: "%", align: "right", fmt: v => `${Math.round(v || 0)}%` },
                { key: "risk_label", label: "Risk" },
              ],
              rows: filtered.map(r => ({ ...r, risk_label: r.delivery_risk?.label || "" })),
              footerRow: { doc_no: `Total ${filtered.length}`, total_qty: fmtQtyInt(totals.total), disp_qty: fmtQtyInt(totals.disp), pending_qty: fmtQtyInt(totals.pending), hold_qty: fmtQtyInt(totals.hold), short_closed_qty: fmtQtyInt(totals.shortClosed), cancelled_qty_net: fmtQtyInt(totals.cancelled) },
            }],
            signatures: ["Prepared By", "Reviewed By", "Approved"],
          })}
          className="h-9 px-4 border border-[var(--accent)] text-[var(--accent)] text-sm font-semibold rounded-sm flex items-center gap-2"
        ><PrinterIcon size={14} weight="bold" />Print / PDF</button>
        <button
          data-testid="eso-report-csv"
          onClick={() => {
            const flat = [];
            filtered.forEach(r => {
              const disps = r.dispatches || [];
              if (disps.length === 0) {
                flat.push({ ...r, risk_label: r.delivery_risk?.label || "", edc_no: "", edc_date: "", epl_no: "", slip_qty: 0 });
              } else {
                disps.forEach(d => flat.push({ ...r, risk_label: r.delivery_risk?.label || "", edc_no: d.edc_no || d.slip_no, edc_date: (d.date || "").slice(0, 10), epl_no: d.epl_no || "", slip_qty: d.quantity }));
              }
            });
            exportToCSV("eso-status-report.csv", flat, [
              { key: "doc_no", label: "ESO No" }, { key: "order_date", label: "Order Date" },
              { key: "buyer_name", label: "Buyer" }, { key: "buyer_po_no", label: "Buyer PO" },
              { key: "incoterms", label: "Incoterms" }, { key: "status", label: "Status" },
              { key: "total_qty", label: "Total Qty" }, { key: "disp_qty", label: "Dispatched" }, { key: "pending_qty", label: "Pending" },
              { key: "hold_qty", label: "Hold Qty" }, { key: "short_closed_qty", label: "Short Closed Qty" }, { key: "cancelled_qty_net", label: "Cancelled Qty" },
              { key: "progress_pct", label: "Progress %" }, { key: "risk_label", label: "Delivery Risk" },
              { key: "edc_no", label: "EDC No" }, { key: "edc_date", label: "EDC Date" }, { key: "epl_no", label: "EPL No" }, { key: "slip_qty", label: "Delivered Qty" },
            ]);
          }}
          className="h-9 px-4 border border-[var(--border)] text-sm font-semibold rounded-sm flex items-center gap-2"
        ><DownloadSimpleIcon size={14} weight="bold" />Export CSV</button>
      </div>

      <div className={`border border-[var(--border)] rounded-sm overflow-x-auto ${esoRepDensity === "compact" ? "[&_td]:!py-1.5 [&_td]:!px-2 [&_th]:!py-1.5 [&_th]:!px-2" : "[&_td]:px-3 [&_td]:py-2.5 [&_th]:px-3 [&_th]:py-2.5"}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="text-left !px-1 w-7"></th>
              <th className="text-left px-4 py-3 whitespace-nowrap">ESO No</th>
              {col("order_date") && <th className="text-left px-4 py-3">Order Date</th>}
              {col("buyer")      && <th className="text-left px-4 py-3">Buyer</th>}
              {col("buyer_po")   && <th className="text-left px-4 py-3">Buyer PO</th>}
              {col("item")       && <th className="text-right px-4 py-3">Item</th>}
              {col("style")      && <th className="text-left px-4 py-3">Style No</th>}
              {col("product_code") && <th className="text-left px-4 py-3 sticky left-0 z-30 bg-[var(--bg-1)] shadow-[1px_0_0_var(--border)]">Product Code</th>}
              {col("order_qty")  && <th className="text-right px-4 py-3" title="Customer ordered quantity">Order Qty</th>}
              {col("original_qty") && <th className="text-right px-4 py-3" title="Original ordered quantity · read-only">Original Qty</th>}
              {col("add_qty")      && <th className="text-right px-4 py-3">Add Qty</th>}
              {col("buffer_qty") && <th className="text-right px-4 py-3" title="Production allowance (extra_qty) — outside commercial Order Qty">Buffer Qty</th>}
              {col("total_qty")  && <th className="text-right px-4 py-3" title="Total Qty = Order + Buffer (technical cap)">Total Qty</th>}
              {col("dispatched") && <th className="text-right px-4 py-3">Dispatch</th>}
              {col("pending")    && <th className="text-right px-4 py-3">Pending</th>}
              {col("status")     && <th className="text-left px-4 py-3">Status</th>}
              {col("fulfilment") && <th className="text-left px-4 py-3" title="Derived from valid Delivered-EDC dispatch vs commercial Order Qty (buffer excluded). Independent of lifecycle status.">Fulfilment</th>}
              {col("hold_qty")         && <th className="text-right px-4 py-3" title="Outstanding balance while the ESO is on Hold">Hold Qty</th>}
              {col("short_closed_qty") && <th className="text-right px-4 py-3">Short Closed Qty</th>}
              {col("cancelled_qty")    && <th className="text-right px-4 py-3" title="Quantity extinguished by cancellation (net of short-close)">Cancelled Qty</th>}
              {col("rate")       && <th className="text-right px-4 py-3">Rate USD</th>}
              {col("amount")     && <th className="text-right px-4 py-3">Amount USD</th>}
              {col("delivery")   && <th className="text-left px-4 py-3">Delivery Date</th>}
              {col("article")    && <th className="text-left px-4 py-3">Article</th>}
              {col("hs")         && <th className="text-left px-4 py-3">HS</th>}
              {col("amount_inr") && <th className="text-right px-4 py-3">Amount INR</th>}
              {col("wo_status")  && <th className="text-left px-4 py-3">Linked WOs</th>}
              {col("pct")        && <th className="text-right px-4 py-3">%</th>}
              {col("risk")       && <th className="text-left px-4 py-3">Delivery Risk</th>}
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={totalCols} className="text-center py-14 text-zinc-500">Loading…</td></tr>}
            {!loading && filtered.length === 0 && <tr><td colSpan={totalCols} className="text-center py-14 text-zinc-500">No ESOs match this filter.</td></tr>}
            {!loading && filtered.flatMap(r => {
              const isOpen = expanded[r.order_id];
              const lines = (r.lines && r.lines.length) ? r.lines : [null];
              const dispatches = r.dispatches || [];
              const out = [];
              // Sep 03, 2026 · ESO Status Report now reads per-line
              // `total_qty` / `disp_qty` / `pending_qty` directly from
              // the backend (`_eso_line_rows`) — matches the canonical
              // per-line max(0, (qty+extra-cancel) - disp) formula.
              // Old code pro-rated the order-level `disp_qty` by
              // qty-share which ignored `extra_qty` + `cancelled_qty`
              // and drifted from SO Status Report on any partial-cancel
              // export. Pro-rating stays as a fallback for legacy rows
              // that predate the extended shape.
              const totalOrderQty = r.total_qty || 0;
              lines.forEach((line, lineIdx) => {
                const isFirst = lineIdx === 0;
                const isLast = lineIdx === lines.length - 1;
                const lineQty = line
                  ? Number(line.total_qty ?? ((Number(line.qty) || 0) + (Number(line.extra_qty) || 0) - (Number(line.cancelled_qty) || 0)))
                  : 0;
                // Prefer the per-line disp / pending shipped from
                // the backend; fall back to a share of the order
                // total only when the line pre-dates Sep 03, 2026.
                const hasLineDisp = line && line.disp_qty !== undefined;
                const share = totalOrderQty > 0 && line ? (Number(line.qty || 0) / totalOrderQty) : 0;
                const lineDisp = line
                  ? (hasLineDisp
                      ? Number(line.disp_qty || 0)
                      : Math.round((r.disp_qty || 0) * share))
                  : 0;
                const linePend = line
                  ? (line.pending_qty !== undefined
                      ? Number(line.pending_qty || 0)
                      : Math.max(0, lineQty - lineDisp))
                  : 0;
                const linePct = lineQty > 0 ? Math.min(100, (lineDisp / lineQty) * 100) : 0;
                out.push(
                  <tr
                    key={`${r.order_id}-${lineIdx}`}
                    data-testid={`eso-report-row-${r.doc_no}-${lineIdx}`}
                    className={`${!isLast ? "border-b border-[var(--border)]/40" : "border-b border-[var(--border)]"} align-top hover:bg-[var(--bg-2)]/40`}
                  >
                    <td className="!px-1 py-3 text-center w-7">
                      {isFirst && dispatches.length > 0 && (
                        <button onClick={() => setExpanded({ ...expanded, [r.order_id]: !isOpen })} data-testid={`eso-report-expand-${r.doc_no}`} className="text-[var(--accent)] font-mono text-xs">{isOpen ? "▼" : "▶"}</button>
                      )}
                    </td>
                    <td className="px-4 py-3 font-mono text-left">
                      <span data-testid={isFirst ? `eso-report-doc-${r.doc_no}` : `eso-report-doc-${r.doc_no}-${lineIdx}`} className="font-mono whitespace-nowrap text-white text-xs font-semibold">{r.doc_no}</span>
                    </td>
                    {col("order_date") && <td className="px-4 py-3 font-mono text-zinc-400 whitespace-nowrap">{r.order_date || ""}</td>}
                    {col("buyer")      && <td className="px-4 py-3 min-w-[150px] max-w-[190px]"><span className="text-zinc-200 font-medium block leading-tight line-clamp-2" title={r.buyer_name || "—"}>{r.buyer_name || "—"}</span></td>}
                    {col("buyer_po")   && <td className="px-4 py-3 font-mono text-zinc-400 whitespace-nowrap">{r.buyer_po_no || "—"}</td>}
                    {col("item")       && <td className="px-4 py-3 text-right font-mono text-zinc-300 text-xs">{line ? (line.item_no || String(lineIdx + 1).padStart(3, "0")) : "—"}</td>}
                    {col("style")      && <td className="px-4 py-3 font-mono text-zinc-300 text-xs">{line ? (line.style_no || "—") : "—"}</td>}
                    {col("product_code") && <td className="px-4 py-3 font-mono text-[var(--accent)] text-xs truncate max-w-[280px] sticky left-0 z-10 bg-[var(--bg-1)] shadow-[1px_0_0_var(--border)]" title={line?.article_no || ""}>{line ? (line.article_no || "—") : "—"}</td>}
                    {col("order_qty")  && <td className="px-4 py-3 text-right font-mono" data-testid={`eso-line-order-${r.doc_no}-${lineIdx}`}>{line ? Number(line.qty || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 }) : "—"}</td>}
                    {col("original_qty") && <td className="px-4 py-3 text-right font-mono text-zinc-300" data-testid={`eso-report-original-qty-${r.doc_no}-${lineIdx}`}>{!line ? "—" : line.original_qty === null || line.original_qty === undefined ? <span className="text-zinc-500 text-[10px]">Not established</span> : Number(line.original_qty).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>}
                    {col("add_qty")      && <td className="px-4 py-3 text-right font-mono text-zinc-300" data-testid={`eso-report-add-qty-${r.doc_no}-${lineIdx}`}>{line ? Number(line.add_qty || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 }) : "—"}</td>}
                    {col("buffer_qty") && <td className="px-4 py-3 text-right font-mono text-zinc-500" title="Production allowance — outside commercial Order Qty">{line ? Number(line.extra_qty || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 }) : "—"}</td>}
                    {col("total_qty")  && <td className="px-4 py-3 text-right font-mono text-zinc-400" title="Total Qty = Order + Buffer · technical cap, not the pending target.">{line ? lineQty.toLocaleString("en-IN", { maximumFractionDigits: 0 }) : "—"}</td>}
                    {col("dispatched") && <td className="px-4 py-3 text-right font-mono text-emerald-400">{line ? lineDisp.toLocaleString("en-IN", { maximumFractionDigits: 0 }) : "0"}</td>}
                    {col("pending")    && <td className={`px-4 py-3 text-right font-mono ${linePend > 0 ? "text-amber-400" : "text-zinc-500"}`}>{line ? linePend.toLocaleString("en-IN", { maximumFractionDigits: 0 }) : "0"}</td>}
                    {col("status")     && <td className="px-4 py-3">{isFirst ? <StatusBadge status={r.status || "Draft"} testId={`eso-line-status-${r.doc_no}`} /> : ""}</td>}
                    {col("fulfilment") && <td className="px-4 py-3">
                      {line && line.fulfilment_status ? (
                        <span
                          data-testid={`eso-report-fulfilment-${r.doc_no}-${lineIdx}`}
                          className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[11px] font-mono uppercase tracking-widest w-fit ${
                            line.fulfilment_status === "Fully Dispatched" ? "bg-emerald-500/10 text-emerald-400"
                              : line.fulfilment_status === "Partially Dispatched" ? "bg-amber-500/10 text-amber-400"
                              : "bg-zinc-500/10 text-zinc-400"}`}
                        >{line.fulfilment_status}</span>
                      ) : <span className="text-zinc-600">—</span>}
                    </td>}
                    {col("hold_qty")         && <td className={`px-4 py-3 text-right font-mono ${line && line.hold_qty > 0 ? "text-orange-300" : "text-zinc-600"}`} data-testid={`eso-report-hold-qty-${r.doc_no}-${lineIdx}`}>{line ? fmtQtyInt(line.hold_qty || 0) : "0"}</td>}
                    {col("short_closed_qty") && <td className={`px-4 py-3 text-right font-mono ${line && line.short_closed_qty > 0 ? "text-purple-300" : "text-zinc-600"}`} data-testid={`eso-report-short-closed-qty-${r.doc_no}-${lineIdx}`}>{line ? fmtQtyInt(line.short_closed_qty || 0) : "0"}</td>}
                    {col("cancelled_qty")    && <td className={`px-4 py-3 text-right font-mono ${line && line.cancelled_qty_net > 0 ? "text-rose-300" : "text-zinc-600"}`} data-testid={`eso-report-cancelled-qty-${r.doc_no}-${lineIdx}`}>{line ? fmtQtyInt(line.cancelled_qty_net || 0) : "0"}</td>}
                    {col("rate")       && <td className="px-4 py-3 text-right font-mono text-zinc-300">{line ? `$${Number(line.unit_price_usd || 0).toFixed(4)}` : "—"}</td>}
                    {col("amount")     && <td className="px-4 py-3 text-right font-mono text-emerald-300">{line ? `$${Number(line.amount_usd || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}</td>}
                    {col("delivery")   && <td className="px-4 py-3 font-mono">{isFirst ? (r.delivery_date ? (() => {
                      // Mirrors the SEALED SO Register: inline-flex, no nowrap — date
                      // wraps to a 2nd line with the Overdue pill when the column is tight.
                      const risk = r.delivery_risk || {};
                      const overdue = (risk.days_left != null && risk.days_left < 0) || /overdue/i.test(risk.label || "");
                      return (
                        <span className="inline-flex items-center gap-1.5">
                          <span className={`font-mono text-xs ${overdue ? "text-amber-400 font-semibold" : "text-zinc-400"}`}>{r.delivery_date}</span>
                          {overdue && <span data-testid={`eso-report-overdue-${r.doc_no}`} title={risk.label || "Delivery date has passed"} className="px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-400 text-[9px] font-mono uppercase tracking-widest">Overdue</span>}
                        </span>
                      );
                    })() : <span className="text-zinc-600">—</span>) : ""}</td>}
                    {/* Mar 3, 2026 · Article column shows BASE article no only (e.g. "10712");
                        Product Code column above already carries the full technical string. */}
                    {col("article")    && <td className="px-4 py-3 font-mono text-[var(--accent)] text-xs tabular-nums" title={line?.article_no || ""}>{line ? (baseArticle(line.article_no) || "—") : "—"}</td>}
                    {col("hs")         && <td className="px-4 py-3 font-mono text-zinc-400 text-xs">{line ? (line.hs_code || "—") : "—"}</td>}
                    {col("amount_inr") && <td className="px-4 py-3 text-right font-mono text-zinc-400 text-xs" title={`FX @ ₹${Number(r.fx_rate_to_inr || 0).toFixed(2)}`}>{line ? `₹${(Number(line.amount_usd || 0) * Number(r.fx_rate_to_inr || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}</td>}
                    {col("wo_status")  && <td className="px-4 py-3">
                      {isFirst && (() => {
                        const b = woSummary[`ESO|${r.order_id}`] || woSummary[`SO|${r.order_id}`];
                        if (!b || !b.count) return <span className="text-[11px] text-zinc-600 font-mono">No WO</span>;
                        const st = b.statuses || {};
                        const parts = Object.entries(st).map(([k, v]) => `${v} ${k}`).join(" · ");
                        return (
                          <div className="leading-tight" data-testid={`eso-wo-chip-${r.doc_no}`} title={b.wos.map(w => `${w.wo_no} · ${w.item_code} · ${w.status}`).join("\n")}>
                            <span className="font-mono text-xs font-semibold text-sky-300">{b.count} {b.count === 1 ? "WO" : "WOs"}</span>
                            <span className="block text-[10px] font-mono text-zinc-500 whitespace-nowrap overflow-hidden text-ellipsis max-w-[160px]">{parts}</span>
                          </div>
                        );
                      })()}
                    </td>}
                    {col("pct")        && <td className="px-4 py-3 text-right font-mono text-zinc-300">{line ? `${Math.round(linePct)}%` : "—"}</td>}
                    {col("risk")       && <td className="px-4 py-3">{isFirst && r.delivery_risk ? <EsoRiskBadge risk={r.delivery_risk} docNo={r.doc_no} /> : ""}</td>}
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {isFirst && (
                        <div className="flex justify-end">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button data-testid={`eso-report-actions-${r.doc_no}`} className="h-7 px-2.5 rounded-sm border border-[var(--border)] text-zinc-300 text-[11px] hover:bg-white/10 whitespace-nowrap">Actions ▾</button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="bg-[var(--bg-1)] border-[var(--border)] text-sm min-w-[140px]">
                              <DropdownMenuItem data-testid={`eso-report-view-${r.doc_no}`} onClick={() => setPreview(r)}>View</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              });
              if (isOpen && r.dispatches.length > 0) {
                out.push(
                  <tr key={`${r.order_id}-disp`} className="border-b border-[var(--border)] bg-[var(--bg-2)]/30">
                    <td></td>
                    <td colSpan={totalCols - 1} className="px-4 py-3">
                      <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Delivered Dispatches · EDC → EPL · {r.dispatches.length}</div>
                      <table className="w-full text-xs">
                        <thead><tr className="text-[10px] uppercase tracking-widest text-zinc-500"><th className="text-left py-1">EDC No</th><th className="text-left py-1">EDC Date</th><th className="text-left py-1">EPL No</th><th className="text-right py-1">Qty (m)</th></tr></thead>
                        <tbody>{r.dispatches.map(d => (
                          <tr key={d.edc_no || d.slip_no} data-testid={`eso-report-disp-${d.edc_no || d.slip_no}`}>
                            <td className="py-1 font-mono text-[var(--accent)]">{d.edc_no || d.slip_no}</td>
                            <td className="py-1 font-mono text-zinc-400">{(d.date || "").slice(0, 10)}</td>
                            <td className="py-1 font-mono text-zinc-300">{d.epl_no || "—"}</td>
                            <td className="py-1 text-right font-mono text-emerald-400">{Number(d.quantity).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                          </tr>
                        ))}</tbody>
                      </table>
                    </td>
                  </tr>
                );
              }
              return out;
            })}
          </tbody>
        </table>
      </div>

      {/* View / Preview Dialog — shows full ESO breakdown incl. per-line rates,
          progress bar, and every linked packing slip dispatch */}
      <Dialog open={!!preview} onOpenChange={(v) => !v && setPreview(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-[900px] max-h-[92vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()} description="Export document action — Proforma / Packing List / Sales Order / Delivery Challan." onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              {preview?.doc_no}
              {preview?.delivery_risk && <EsoRiskBadge risk={preview.delivery_risk} docNo={preview.doc_no} />}
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-4 gap-3 text-xs">
                <div><div className="overline mb-0.5">Order Date</div><div className="font-mono">{preview.order_date}</div></div>
                <div><div className="overline mb-0.5">Buyer</div><div>{preview.buyer_name || "—"}</div></div>
                <div><div className="overline mb-0.5">Buyer PO</div><div className="font-mono">{preview.buyer_po_no || "—"}</div></div>
                <div><div className="overline mb-0.5">Incoterms</div><div className="font-mono">{preview.incoterms || "—"}</div></div>
              </div>
              <div className="grid grid-cols-4 gap-3 text-xs">
                <div><div className="overline mb-0.5">Total Qty</div><div className="font-mono">{fmtQtyInt(preview.total_qty)} m</div></div>
                <div><div className="overline mb-0.5">Dispatched</div><div className="font-mono text-emerald-400">{fmtQtyInt(preview.disp_qty)} m</div></div>
                <div><div className="overline mb-0.5">Pending</div><div className={`font-mono ${(preview.pending_qty || 0) > 0 ? "text-amber-400" : "text-zinc-500"}`}>{fmtQtyInt(preview.pending_qty)} m</div></div>
                <div><div className="overline mb-0.5">Progress</div><div className="font-mono">{Math.round(preview.progress_pct || 0)}%</div></div>
              </div>
              <div className="h-2 bg-[var(--bg-2)] rounded-sm overflow-hidden"><div className="h-full bg-[var(--accent)]" style={{ width: `${Math.min(100, Math.round(preview.progress_pct || 0))}%` }} /></div>

              <div className="border-t border-[var(--border)] pt-3">
                <div className="overline mb-2">Line Items · {(preview.lines || []).length}</div>
                <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead><tr className="text-zinc-500 border-b border-[var(--border)]"><th className="text-left px-2 py-1.5">#</th><th className="text-left px-2 py-1.5">Product Code</th><th className="text-left px-2 py-1.5">Style No</th><th className="text-left px-2 py-1.5">HS</th><th className="text-right px-2 py-1.5">Qty</th><th className="text-right px-2 py-1.5">Rate</th><th className="text-right px-2 py-1.5">Amount</th><th className="text-left px-2 py-1.5" title="Item-wise Required Delivery Date">Delivery</th><th className="text-center px-2 py-1.5">History</th></tr></thead>
                    <tbody>{(preview.lines || []).map((l, i) => (
                      <tr key={i} className="border-b border-[var(--border)]/50">
                        <td className="px-2 py-1.5 font-mono text-zinc-500">{l.item_no}</td>
                        <td className="px-2 py-1.5 font-mono text-[var(--accent)] text-[11px] truncate max-w-[240px]" title={l.article_no} data-testid={`eso-preview-line-product-code-${l.line_id || i}`}>{l.article_no || "—"}</td>
                        <td className="px-2 py-1.5 font-mono text-zinc-300">{l.style_no || "—"}</td>
                        <td className="px-2 py-1.5 font-mono text-zinc-400">{l.hs_code || "—"}</td>
                        <td className="px-2 py-1.5 text-right font-mono">{Number(l.qty || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                        <td className="px-2 py-1.5 text-right font-mono">${Number(l.unit_price_usd || 0).toFixed(4)}</td>
                        <td className="px-2 py-1.5 text-right font-mono text-emerald-400">${Number(l.amount_usd || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td className="px-2 py-1.5 font-mono text-zinc-300 text-[11px]" data-testid={`eso-preview-line-delivery-${l.line_id || i}`}>{l.delivery_date || preview.delivery_date || "—"}</td>
                        <td className="px-2 py-1.5 text-center">
                          <button data-testid={`eso-line-history-${l.line_id || i}`}
                            onClick={() => setHistoryLine({ source: "ESO", orderId: preview.order_id, lineId: l.line_id, lineRef: l.line_id || `idx-${i}`, orderNo: preview.doc_no, productCode: l.article_no })}
                            className="h-5 px-1 border border-[var(--border)] hover:border-[var(--accent)] text-[var(--accent)] rounded-sm text-[9px] font-mono uppercase tracking-widest">
                            History
                          </button>
                        </td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>

              <div>
                <div className="overline mb-2">Dispatches · {(preview.dispatches || []).length}</div>
                {(preview.dispatches || []).length === 0 ? (
                  <div className="text-xs text-zinc-500 italic border border-[var(--border)] rounded-sm p-4 text-center">No packing slip dispatches yet.</div>
                ) : (
                  <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead><tr className="text-zinc-500 border-b border-[var(--border)]"><th className="text-left px-2 py-1.5">Date</th><th className="text-left px-2 py-1.5">Slip No</th><th className="text-right px-2 py-1.5">Boxes</th><th className="text-right px-2 py-1.5">Qty (m)</th></tr></thead>
                      <tbody>{preview.dispatches.map(d => (
                        <tr key={d.slip_no} className="border-b border-[var(--border)]/50">
                          <td className="px-2 py-1.5 font-mono text-zinc-400">{d.date}</td>
                          <td className="px-2 py-1.5 font-mono text-[var(--accent)]">{d.slip_no}</td>
                          <td className="px-2 py-1.5 text-right font-mono text-zinc-300">{d.boxes}</td>
                          <td className="px-2 py-1.5 text-right font-mono text-emerald-400">{Number(d.quantity).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setPreview(null)} className="h-9 px-4 border border-[var(--border)] text-sm rounded-sm hover:bg-[var(--bg-2)]">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <LineHistoryDrawer open={!!historyLine} onClose={() => setHistoryLine(null)}
        source={historyLine?.source} orderId={historyLine?.orderId} lineId={historyLine?.lineId} lineRef={historyLine?.lineRef}
        orderNo={historyLine?.orderNo} productCode={historyLine?.productCode} />
    </div>
  );
}
function AuthStatusBadge({ r }) {
  // Mar 2026 · Delegates to the shared StatusBadge palette so every
  // dashboard renders the same Grey/Orange/Green/Red/Dark-Grey pill.
  // Amendment suffix (R1, R2…) kept as a title tooltip.
  const s = r?.auth_status || "Draft";
  const amendTag = (r?.amendment_no || 0) > 0 ? ` · R${r.amendment_no}` : "";
  return (
    <span
      data-testid={`eso-auth-${r?.doc_no}-${s}`}
      title={
        s === "Authorised" && r?.authorised_by
          ? `Authorised by ${r.authorised_by}${amendTag}`
          : (amendTag ? `Revision${amendTag}` : "")
      }
    >
      <StatusBadge status={s} />
    </span>
  );
}

function EsoViewDialog({ eso, open, onClose, canAuth, canEdit, onChanged, onPrint, initialAction, onHold, onAdminAmend }) {
  const { user: lqaUser } = useAuth();  // SOESO-LA01 · P2-E · line qty actions are Administrator-only (backend-enforced)
  // IMP-1C · P-2 — action-specific Modify Order permissions (backend: exports:cancel / :unapprove).
  const { user: _viewUser, hasPerm: _viewHasPerm } = useAuth();
  const _viewAdmin = _viewUser?.role === "Administrator";
  const [lcDetail, setLcDetail] = useState({ deferred: false, packed: [] }); // ESO-LC01 · S-5 / A-1
  const [historyLine, setHistoryLine] = useState(null); // P2-E-UAT-02 · line-wise History drawer
  // R2-01L A4-02 · D2/O1 · read-only breakdown for ESO viewers (exports:view · RBAC-P1-D).
  const canQtyView = canViewQty(_viewUser, _viewHasPerm, "eso");
  const [qpos] = useQtyPositions("eso", eso?.id, open && eso?.auth_status === "Authorised" && canQtyView);
  const canEsoClose = _viewAdmin || !!_viewHasPerm?.("exports:cancel");
  // RBAC-B2-S3 · ESO action controls use the authoritative exports:* codes only (backend parity).
  const canEsoUnapprove = _viewAdmin || !!_viewHasPerm?.("exports:unapprove");
  const canEsoSubmit = _viewAdmin || !!_viewHasPerm?.("exports:submit") || !!_viewHasPerm?.("exports:create");
  const canEsoReject = _viewAdmin || !!_viewHasPerm?.("exports:reject");
  const canEsoCancel = _viewAdmin || !!_viewHasPerm?.("exports:cancel");
  const [amendReason, setAmendReason] = useState("");
  const [showAmendPrompt, setShowAmendPrompt] = useState(false);
  const [showCancelPrompt, setShowCancelPrompt] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [showRejectPrompt, setShowRejectPrompt] = useState(false);
  const [busy, setBusy] = useState(false);
  // Feb 2026 · Dynamic Order Modification — shared popover for
  // Unapprove / Short Close / Cancel Balance on Authorised ESOs.
  const [modifyOpen, setModifyOpen] = useState(false);
  // PHASE-1 · Server-derived commercial-freeze flag. Fetched from the ESO
  // detail endpoint on open; drives Raise Amendment / Modify Order
  // visibility. Backend (auth_workflow / _eso_update_body) stays
  // authoritative — this is UI convenience only.
  const [frozen, setFrozen] = useState(false);
  const [frozenReason, setFrozenReason] = useState("");
  useEffect(() => {
    if (open) {
      setAmendReason("");
      setShowRejectPrompt(false);
      setShowAmendPrompt(initialAction === "amend" && (eso?.auth_status || "Draft") === "Authorised" && eso?.status !== "Short Closed");
      if (initialAction === "modify" && ((eso?.auth_status || "Draft") === "Authorised" || eso?.auth_status === "Short Closed")) {
        setModifyOpen(true);
      }
      if (eso?.id) {
        api.get(`/exports/orders/${eso.id}`).then((res) => {
          setFrozen(Boolean(res?.data?.commercially_frozen));
          setFrozenReason(res?.data?.commercially_frozen_reason || "");
          setLcDetail({ deferred: Boolean(res?.data?.dispatch_sync_deferred),
                        packed: (res?.data?.items || []).map((x) => Number(x.packed_awaiting_dispatch_qty || 0)),
                        // ESO-LC01 · D-3 · authoritative server Close preview (null = not verified)
                        close: (res?.data?.items || []).map((x) => (typeof x.close_eligible_qty === "number"
                          ? { pending: Number(x.close_pending_qty || 0), packed: Number(x.packed_undelivered_qty || 0), eligible: x.close_eligible_qty }
                          : null)) });
        }).catch(() => { setFrozen(false); setFrozenReason(""); });
      }
    }
  }, [open, eso?.id, eso?.auth_status, initialAction]);
  if (!eso) return null;
  // IMP-0B · decision B — Short Closed is on lifecycle `status`; auth_status keeps the
  // authorization value. Display-state folds both so every `st` gate behaves as pre-B.
  const st = (eso.status || "") === "Short Closed" ? "Short Closed" : (eso.auth_status || "Draft");
  // Feb 2026 · Cancelled ESOs must not surface any lifecycle action —
  // `auth_status` stays on the pre-cancel value (usually "Draft"), so
  // gating on `auth_status` alone would leak "Submit for Approval" +
  // "Authorise" buttons onto a dead order. This flag folds the doc-level
  // status into the gate so the footer only shows Close + Browser Print.
  // DP-ESO-AUTH-REJECT · Feb 2026 — `status="Rejected"` is now a
  // Management-Rejected/editable-correction state (NOT a terminal cancel),
  // so it must remain interactive. Only true `status="cancelled"` is
  // treated as read-only here.
  const isCancelled = (eso.status || "").toLowerCase() === "cancelled";
  const call = async (path, body) => {
    setBusy(true);
    try {
      await api.post(`/exports/orders/${eso.id}/${path}`, body || {});
      toast.success(`ESO ${path} complete`);
      onChanged && await onChanged();
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || `Failed to ${path}`); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-[1000px] max-h-[92vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()} description="Export document action — Proforma / Packing List / Sales Order / Delivery Challan." onEscapeKeyDown={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-3">
            {eso.doc_no}
            <AuthStatusBadge r={eso} />
            {eso.delivery_risk && <EsoRiskBadge risk={eso.delivery_risk} docNo={eso.doc_no} />}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-4 gap-3 text-xs">
            <div><div className="overline mb-0.5">Order Date</div><div className="font-mono">{eso.order_date}</div></div>
            <div><div className="overline mb-0.5">Delivery</div><div className="font-mono text-amber-300">{eso.delivery_date || "—"}</div></div>
            <div><div className="overline mb-0.5">Incoterms</div><div className="font-mono">{eso.incoterms || "—"}</div></div>
            <div><div className="overline mb-0.5">Status</div><div>{eso.status}</div></div>
            <div className="col-span-2"><div className="overline mb-0.5">Buyer</div><div>{eso.buyer_name}<div className="text-zinc-500 text-[11px]">{eso.buyer_address}</div></div></div>
            <div><div className="overline mb-0.5">Buyer PO</div><div className="font-mono">{eso.buyer_po_no || "—"}{eso.buyer_po_date ? ` · ${eso.buyer_po_date}` : ""}</div></div>
            <div><div className="overline mb-0.5">Proforma Invoice</div><div className="font-mono">{eso.pi_no || "—"}{eso.pi_date ? ` · ${eso.pi_date}` : ""}</div></div>
            <div className="col-span-2"><div className="overline mb-0.5">Consignee</div><div>{eso.consignee_name || "—"}<div className="text-zinc-500 text-[11px]">{eso.consignee_address}</div></div></div>
            <div><div className="overline mb-0.5">Payment</div><div className="font-mono">{eso.payment_terms}</div></div>
            <div><div className="overline mb-0.5">Total (USD)</div><div className="font-mono text-emerald-400">${Number(eso.total_usd || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
          </div>

          <DocumentInfo docNo={eso.doc_no} createdAt={eso.created_at} updatedAt={eso.updated_at} createdBy={eso.created_by} docLabel="Export SO No." testidPrefix="eso" className="" />

          <div className="border-t border-[var(--border)] pt-3">
            {lcDetail.deferred && (
              <div data-testid="eso-dispatch-sync-deferred-banner" className="mb-2 px-3 py-2 border border-amber-500/50 bg-amber-500/10 text-amber-300 rounded-sm text-xs flex items-center justify-between gap-2">
                <span>Dispatch figures on this ESO are waiting to resync after a delivery. Quantity checks still use delivery records.</span>
                {_viewAdmin && (
                  <button data-testid="eso-resync-dispatch-btn"
                    onClick={async () => {
                      try {
                        const { data } = await api.post(`/exports/orders/${eso.id}/resync-dispatch`);
                        if (data.ok) { toast.success("Dispatch resynced"); onChanged && onChanged(); }
                        else toast.error("Resync still deferred — try again shortly");
                      } catch (e) { const d = e?.response?.data?.detail; toast.error((d && d.message) || d || "Resync failed"); }
                    }}
                    className="h-6 px-2 border border-amber-500/60 hover:bg-amber-500/20 rounded-sm text-[10px] font-mono uppercase tracking-widest">Resync</button>
                )}
              </div>
            )}
            <div className="overline mb-2">Line Items · {(eso.items || []).length}</div>
            <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="text-zinc-500 border-b border-[var(--border)]"><th className="text-left px-2 py-1.5">#</th><th className="text-left px-2 py-1.5">Product Code</th><th className="text-left px-2 py-1.5">Style No</th><th className="text-left px-2 py-1.5">HS</th><th className="text-right px-2 py-1.5">Qty</th><th className="text-right px-2 py-1.5">Rate</th><th className="text-right px-2 py-1.5">Amount</th><th className="text-left px-2 py-1.5" title="Item-wise Required Delivery Date">Delivery</th>{canQtyView && st === "Authorised" && <th className="text-left px-2 py-1.5" title="Read-only quantity breakdown">Qty Breakdown</th>}<th className="text-center px-2 py-1.5">Status</th>{canAuth && st === "Authorised" && <th className="text-center px-2 py-1.5 w-28">Actions</th>}</tr></thead>
                <tbody>{(eso.items || []).map((l, i) => {
                  // R2-01L A4-02 · G6 · completion uses customer Qty (buffer excluded) · G1 cancelled order shows no pending.
                  const ordered = Number(l.qty || 0);
                  const disp = Number(l.disp_qty || 0);
                  const cancelled = Number(l.cancelled_qty || 0);
                  const pending = isCancelled ? 0 : Math.max(0, ordered - disp - cancelled);
                  // ESO-LC01 · closable pending excludes buffer; server deducts packed-undelivered.
                  const openPending = Math.max(0, Number(l.qty || 0) - disp - cancelled);
                  const lineRef = l.line_id || `idx-${i}`;
                  const qp = qpos[lineRef];
                  const prot = qp?.readiness_block || null;
                  const packedAwaiting = Number(lcDetail.packed[i] || 0);
                  const closeQ = (lcDetail.close || [])[i] || null;
                  const closeOk = Boolean(closeQ) && closeQ.eligible > 0 && !prot;
                  let lst = "In-Progress";
                  let cls = "border-sky-500/50 text-sky-300 bg-sky-500/10";
                  if (isCancelled) { lst = "Cancelled"; cls = "border-red-500/50 text-red-300 bg-red-500/10"; }
                  else if (l.short_closed) { lst = "Closed";   cls = "border-zinc-500/50 text-zinc-300 bg-zinc-500/10 [border-style:dashed]"; }
                  else if (pending <= 0 && disp > 0) { lst = "Completed"; cls = "border-emerald-500/50 text-emerald-300 bg-emerald-500/10"; }
                  else if (l.amended) { lst = "Amended"; cls = "border-amber-500/50 text-amber-300 bg-amber-500/10"; }
                  // Feb 07, 2026 · `canLineAct` gates line-level Modify /
                  // Short-Close inside the ESO view dialog. Split off
                  // `canAuth` — modifying qty is an "edit" per the
                  // PermissionReferenceGuide contract, not an approval.
                  const canLineAct = canEdit && st === "Authorised" && !isCancelled && !l.short_closed && openPending > 0;
                  return (
                    <tr key={i} className="border-b border-[var(--border)]/50" data-testid={`eso-line-${l.line_id || i}`}>
                      <td className="px-2 py-1.5 font-mono text-zinc-500">{String(i+1).padStart(3, "0")}</td>
                      <td className="px-2 py-1.5 font-mono text-[var(--accent)] text-[11px] truncate max-w-[240px]" title={l.article_no} data-testid={`eso-line-product-code-${l.line_id || i}`}>{l.article_no || "—"}</td>
                      <td className="px-2 py-1.5 font-mono text-zinc-300">{l.style_no || "—"}</td>
                      <td className="px-2 py-1.5 font-mono text-zinc-400">{l.hs_code || "—"}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{Number(l.qty || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                      <td className="px-2 py-1.5 text-right font-mono">${Number(l.unit_price_usd || 0).toFixed(4)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-emerald-400">${Number(l.amount_usd || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="px-2 py-1.5 font-mono text-zinc-300 text-[11px]" data-testid={`eso-line-delivery-${l.line_id || i}`}>{l.delivery_date || eso.delivery_date || "—"}</td>
                      {canQtyView && st === "Authorised" && (
                        <td className="px-2 py-1.5"><LineQtyBreakdown line={qp} cancelled={isCancelled} testid={`eso-line-breakdown-${lineRef}`} /></td>
                      )}
                      <td className="px-2 py-1.5 text-center">
                        <span data-testid={`eso-line-status-${l.line_id || i}`}
                              className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[9px] font-mono uppercase tracking-widest ${cls}`}
                              title={l.short_close_reason || (l.line_history || []).slice(-1)[0]?.reason || ""}>{lst}</span>
                        <ProtectionBadge cls={prot} testid={`eso-line-protection-${lineRef}`} />
                        <button data-testid={`eso-view-line-history-${lineRef}`}
                          onClick={(e) => { e.stopPropagation(); setHistoryLine({ source: "ESO", orderId: eso.id, lineId: l.line_id, lineRef, orderNo: eso.doc_no, productCode: l.article_no }); }}
                          title="View line audit timeline"
                          className="ml-1 h-5 px-1 border border-[var(--border)] hover:border-[var(--accent)] text-[var(--accent)] rounded-sm text-[9px] font-mono uppercase tracking-widest">
                          History
                        </button>
                        {st === "Authorised" && canQtyView && (
                          <span className="ml-1"><LineQtyActions otype="eso" orderId={eso.id} lineRef={lineRef} articleNo={l.article_no}
                            readOnly={lqaUser?.role !== "Administrator"}
                            testid={`eso-line-qty-${lineRef}`} onChanged={async () => { onChanged && (await onChanged()); }} /></span>
                        )}
                        {packedAwaiting > 0 && (
                          <div data-testid={`eso-line-packed-awaiting-${lineRef}`} className="mt-0.5 text-[9px] font-mono text-sky-300">Packed – awaiting dispatch: {packedAwaiting} m</div>
                        )}
                      </td>
                      {canAuth && st === "Authorised" && (
                        <td className="px-2 py-1.5 text-center">
                          {canLineAct ? (
                            <div className="flex items-center justify-center gap-1">
                              <button data-testid={`eso-line-close-${lineRef}`}
                                disabled={!closeOk}
                                onClick={async () => {
                                  if (!closeOk) return;
                                  const fmt = (n) => Number(n).toLocaleString("en-IN", { maximumFractionDigits: 3 });
                                  const reason = window.prompt(`Short-Close Line ${String(i+1).padStart(3, "0")}?\nPending: ${fmt(closeQ.pending)} m\nPacked awaiting dispatch: ${fmt(closeQ.packed)} m (kept for delivery)\nEligible Short Close: ${fmt(closeQ.eligible)} m\nReason (min 4 chars):`, "");
                                  if (reason === null) return;
                                  if ((reason || "").trim().length < 4) { toast.error("Reason must be at least 4 characters"); return; }
                                  try {
                                    const { data } = await api.post(`/exports/orders/${eso.id}/lines/${lineRef}/short-close`, { reason: reason.trim(), expected_article_no: l.article_no || "", expected_qty: Number(l.qty || 0) });
                                    toast.success(`Line ${i+1} short-closed · ${data.closed_qty} cancelled${data.reserved_packed_qty ? ` · ${data.reserved_packed_qty} packed kept for delivery` : ""}`);
                                    onChanged && onChanged();
                                  } catch (e) {
                                    const d = e?.response?.data?.detail;
                                    const conflict = e?.response?.status === 409;
                                    toast.error(`${(d && d.message) || d || "Short-close failed"}${conflict ? " · quantities changed — reopen the order to refresh" : ""}`);
                                    if (conflict) { onChanged && onChanged(); onClose && onClose(); }
                                  }
                                }}
                                title={prot ? `Close disabled · ${PROTECTION_TEXT[prot] || prot}`
                                  : !closeQ ? "Close quantity could not be verified — reopen the order to refresh"
                                  : closeQ.eligible > 0 ? `Short-close ${closeQ.eligible} m unpacked pending (with reason)`
                                  : "Nothing unpacked to close — all outstanding quantity is packed"}
                                className="h-6 px-1.5 border border-red-500/50 text-red-300 hover:bg-red-500/10 rounded-sm text-[9px] font-mono uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent">{closeQ && closeQ.eligible <= 0 ? "Packed" : "Close"}</button>
                            </div>
                          ) : (<span className="text-[9px] text-zinc-600">—</span>)}
                        </td>
                      )}
                    </tr>
                  );
                })}</tbody>
                {(eso.items || []).length > 0 && (
                  <tfoot className="bg-[var(--bg-2)]/40 border-t-2 border-[var(--border)]">
                    <tr className="font-semibold">
                      <td colSpan={4} className="px-2 py-1.5 text-right text-[9px] uppercase tracking-widest text-zinc-500">Totals</td>
                      <td className="px-2 py-1.5 text-right font-mono text-white" data-testid="eso-view-totals-qty">{Number((eso.items || []).reduce((a, l) => a + Number(l.qty || 0), 0)).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                      <td></td>
                      <td className="px-2 py-1.5 text-right font-mono text-emerald-400" data-testid="eso-view-totals-amount">${Number((eso.items || []).reduce((a, l) => a + Number(l.amount_usd || 0), 0)).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td></td>
                      <td></td>
                      {canQtyView && st === "Authorised" && <td></td>}
                      {canAuth && st === "Authorised" && <td></td>}
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          {(eso.amendment_history || []).length > 0 && (
            <div>
              <div className="overline mb-2">Amendment History · {(eso.amendment_history || []).length}</div>
              <div className="border border-[var(--border)] rounded-sm">
                {(eso.amendment_history || []).map((h, i) => (
                  <div key={i} data-testid={`eso-hist-${h.n}-${h.kind}`} className="flex items-center gap-3 px-3 py-2 border-b border-[var(--border)]/50 last:border-b-0 text-xs">
                    <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-mono uppercase ${h.kind === "reject" ? "bg-red-500/10 text-red-400" : "bg-blue-500/10 text-blue-400"}`}>{h.kind}</span>
                    <span className="font-mono text-zinc-500">{fmtDateTimeIST(h.date)}</span>
                    <span className="text-zinc-300 flex-1">{h.reason}</span>
                    <span className="font-mono text-zinc-500">{h.by}</span>
                    <span className="font-mono text-[var(--accent)]">R{h.n}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(showAmendPrompt || showRejectPrompt) && (
            <div className="border border-amber-500/40 rounded-sm p-3 bg-amber-500/5">
              <div className="overline mb-1.5">{showAmendPrompt ? "Amendment Reason (required)" : "Rejection Reason"}</div>
              <textarea
                data-testid={showAmendPrompt ? "eso-amend-reason" : "eso-reject-reason"}
                value={amendReason}
                onChange={e => setAmendReason(e.target.value)}
                placeholder="e.g. Buyer revised quantity from 5000m → 6000m"
                rows={3}
                className="w-full px-3 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-amber-500"
              />
            </div>
          )}
          {showCancelPrompt && (
            <div data-testid="eso-cancel-prompt" className="border border-red-500/50 rounded-sm p-3 bg-red-500/5">
              <div className="text-sm text-red-300 font-semibold mb-1">Cancel this order permanently?</div>
              <div className="text-xs text-zinc-400 mb-2">The original order and existing downstream history will be retained, but no further processing will be allowed. This action cannot be reversed.</div>
              <div className="overline mb-1.5">Cancellation Reason (required)</div>
              <textarea
                data-testid="eso-cancel-reason"
                value={cancelReason}
                onChange={e => setCancelReason(e.target.value)}
                placeholder="e.g. Buyer withdrew the export order"
                rows={3}
                className="w-full px-3 py-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm focus:outline-none focus:border-red-500"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <div className="flex-1 text-xs text-zinc-500 self-center">
            {isCancelled && eso.cancelled_by && <span data-testid="eso-cancelled-info">Cancelled by <span className="font-mono text-red-400">{eso.cancelled_by}</span> · {fmtDateIST(eso.cancelled_at)}{eso.cancellation_reason ? ` · ${eso.cancellation_reason}` : ""}</span>}
            {!isCancelled && st === "Authorised" && eso.authorised_by && <span data-testid="eso-authorised-by">Authorised by <span className="font-mono text-emerald-400">{eso.authorised_by}</span> · {fmtDateIST(eso.authorised_at)}</span>}
          </div>
          {showCancelPrompt && <>
            <button onClick={() => { setShowCancelPrompt(false); setCancelReason(""); }} className="h-9 px-4 border border-[var(--border)] text-sm rounded-sm">Back</button>
            <button data-testid="eso-cancel-confirm" disabled={busy || cancelReason.trim().length < 4} onClick={() => call("cancel", { reason: cancelReason.trim() })} className="h-9 px-4 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-sm disabled:opacity-40">Confirm Cancel Order</button>
          </>}
          {showAmendPrompt && <>
            <button onClick={() => { setShowAmendPrompt(false); setAmendReason(""); }} className="h-9 px-4 border border-[var(--border)] text-sm rounded-sm">Cancel</button>
            <button data-testid="eso-amend-confirm" disabled={busy || !amendReason.trim()} onClick={() => call("amend", { reason: amendReason.trim() })} className="h-9 px-4 bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold rounded-sm disabled:opacity-40">Confirm Amendment</button>
          </>}
          {showRejectPrompt && <>
            <button onClick={() => { setShowRejectPrompt(false); setAmendReason(""); }} className="h-9 px-4 border border-[var(--border)] text-sm rounded-sm">Cancel</button>
            <button data-testid="eso-reject-confirm" disabled={busy} onClick={() => call("reject", { reason: amendReason.trim() })} className="h-9 px-4 bg-red-500 hover:bg-red-600 text-white text-sm font-semibold rounded-sm">Confirm Reject</button>
          </>}
          {!showAmendPrompt && !showRejectPrompt && !showCancelPrompt && (<>
            <button onClick={onClose} className="h-9 px-4 border border-[var(--border)] text-sm rounded-sm hover:bg-[var(--bg-2)]">Close</button>
            {onPrint && (
              <button data-testid="eso-view-print" onClick={() => onPrint(eso.id)} className="h-9 px-4 border border-sky-500/40 text-sky-300 text-sm font-semibold rounded-sm hover:bg-sky-500/10 flex items-center gap-2"><PrinterIcon size={14} weight="bold" />Browser Print</button>
            )}
            <DocDownloadButtons type="ESO" id={eso.id} testPrefix="eso-view" />
            {canEsoSubmit && !isCancelled && (st === "Draft" || st === "Rejected") && (
              <button data-testid="eso-submit-btn" disabled={busy} onClick={() => call("submit")} className="h-9 px-4 bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold rounded-sm">Submit for Authorisation</button>
            )}
            {canAuth && !isCancelled && st === "PendingApproval" && (
              <button data-testid="eso-authorise-btn" disabled={busy} onClick={() => call("authorise")} className="h-9 px-4 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold rounded-sm">Authorise</button>
            )}
            {canEsoReject && !isCancelled && st === "PendingApproval" && (
              <button data-testid="eso-reject-btn" disabled={busy} onClick={() => setShowRejectPrompt(true)} className="h-9 px-4 border border-red-500/40 text-red-400 text-sm rounded-sm">Reject</button>
            )}
            {canEdit && !isCancelled && st === "Authorised" && !frozen && (
              <button data-testid="eso-amend-btn" onClick={() => setShowAmendPrompt(true)} className="h-9 px-4 border border-amber-500/40 text-amber-400 text-sm rounded-sm">Raise Amendment</button>
            )}
            {/* IMP-1C · P-1 — reference-only Administrative Amendment for a verified legacy Pending ESO. */}
            {canEdit && !isCancelled && st === "PendingApproval" && eso.controls?.legacy_actions && eso.controls?.can_admin_amend && onAdminAmend && (
              <button data-testid="eso-admin-amend-view-btn" onClick={() => onAdminAmend(eso.id)} className="h-9 px-4 border border-blue-500/40 text-blue-300 text-sm rounded-sm hover:bg-blue-500/10">Administrative Amendment…</button>
            )}
            {!isCancelled && ((st === "Short Closed" && canEsoClose) || (st === "Authorised" && !frozen && (canEsoClose || canEsoUnapprove)) || (st === "PendingApproval" && eso.controls?.legacy_actions && canEsoClose)) && (
              <button
                data-testid="eso-modify-btn"
                disabled={busy}
                onClick={() => setModifyOpen(true)}
                className={`h-9 px-4 border text-sm rounded-sm ${
                  st === "Short Closed"
                    ? "border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10"
                    : "border-amber-500/50 text-amber-300 hover:bg-amber-500/10"
                }`}
              >
                {st === "Short Closed" ? "Reverse Short Close" : "Modify Order"}
              </button>
            )}
            {/* Phase 2C-6B1A · Hold / Release consolidated into View (removed
                from the ESO main row). Gated on lifecycle status; backend
                capability stays authoritative. */}
            {onHold && canEdit && !isCancelled && st === "Authorised" && ["confirmed", "open"].includes((eso.status || "").toLowerCase()) && (
              <button data-testid="eso-hold-btn" onClick={() => onHold("hold")} className="h-9 px-4 border border-amber-500/40 text-amber-400 text-sm rounded-sm hover:bg-amber-500/10">Hold Order</button>
            )}
            {onHold && canEdit && (eso.status || "").toLowerCase() === "hold" && (
              <button data-testid="eso-release-hold-btn" onClick={() => onHold("release")} className="h-9 px-4 border border-green-500/40 text-green-400 text-sm rounded-sm hover:bg-green-500/10">Release Hold</button>
            )}
            {frozen && st === "Authorised" && !isCancelled && (
              <span data-testid="eso-frozen-note" className="h-9 px-3 inline-flex items-center text-xs text-zinc-400 border border-zinc-700 rounded-sm">
                {frozenReason || "Commercially Frozen — downstream transaction exists"}
              </span>
            )}
            {canEsoCancel && !isCancelled && (st === "Authorised" || st === "Short Closed") && ["Authorised", "Short Closed"].includes(eso.auth_status) && (
              <button data-testid="eso-cancel-btn" onClick={() => setShowCancelPrompt(true)} className="h-9 px-4 border border-red-500/50 text-red-400 text-sm rounded-sm hover:bg-red-500/10">Cancel Order</button>
            )}
          </>)}
        </DialogFooter>
      </DialogContent>
      <ModifyDocumentPopover
        docType="ESO"
        docId={eso.id}
        docNo={eso.doc_no}
        open={modifyOpen}
        onClose={() => setModifyOpen(false)}
        onDone={async () => { onChanged && await onChanged(); onClose(); }}
        onLineCancel={async () => { onChanged && await onChanged(); }}
        testidPrefix="eso-modify"
        allow={{ unapprove: canEsoUnapprove, close: canEsoClose }}
      />
      <LineHistoryDrawer open={!!historyLine} onClose={() => setHistoryLine(null)}
        source={historyLine?.source} orderId={historyLine?.orderId} lineId={historyLine?.lineId} lineRef={historyLine?.lineRef}
        orderNo={historyLine?.orderNo} productCode={historyLine?.productCode} />
    </Dialog>
  );
}
// Traffic-light risk chip (mirror of RiskBadge from SalesOrders.jsx)
function EsoRiskBadge({ risk, docNo }) {
  const map = {
    critical:  { cls: "bg-red-500/15 text-red-400 border-red-500/40",       dot: "bg-red-400",     pulse: true,  prefix: "AT RISK" },
    at_risk:   { cls: "bg-amber-500/15 text-amber-400 border-amber-500/40", dot: "bg-amber-400",   pulse: false, prefix: "WATCH" },
    on_track:  { cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/30", dot: "bg-emerald-400", pulse: false, prefix: "ON TRACK" },
    delivered: { cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/30",    dot: "bg-zinc-400",    pulse: false, prefix: "SHIPPED" },
  };
  const cfg = map[risk?.state] || map.on_track;
  const label = risk?.label || cfg.prefix;
  return (
    <span
      data-testid={`eso-risk-${docNo}-${risk?.state || "unknown"}`}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest w-fit ${cfg.cls}`}
      title={`${cfg.prefix} · ${Math.round(risk?.progress_pct || 0)}% complete`}
    >
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot} ${cfg.pulse ? "animate-pulse" : ""}`} />
      {label}
    </span>
  );
}

function ClipboardIcon(props) { return <GlobeIcon {...props} />; }

// ============================================================================
//  Export Sales Orders
// ============================================================================
// Mar 1, 2026 · ESO Orders grid column chooser — module-scope constant
// so both the state initializer inside OrdersTab and the render loop use
// the same source of truth without React-scope leakage.
// Feb 2026 · ESO Grid Parity Sweep — replicated the domestic SO column
// order so operators switching between the two modules keep the same
// mental model. `buyer` + `buyer_po` are internally kept as keys (to
// avoid churning the persisted localStorage cache) but their labels now
// read "Customer" / "Customer PO No" — the ESO IS a Sales Order for an
// export customer. `description` + `colour` join the roster to close
// the last two gaps vs the domestic grid.
const ESO_ORDERS_ALL_COLS = [
  { k: "order_date",  label: "Date" },
  { k: "buyer",       label: "Customer" },
  { k: "buyer_po",    label: "Customer PO No" },
  { k: "item",        label: "Item No" },
  { k: "style",       label: "Style No" },
  { k: "article",     label: "Product Code" },
  { k: "hs",          label: "HS Code" },
  { k: "description", label: "Description" },
  { k: "colour",      label: "Colour" },
  { k: "size",        label: "Size" },
  { k: "qty",         label: "Order Qty" },
  { k: "extra_qty",   label: "Buffer Qty" },
  { k: "total_qty",   label: "Total Qty" },
  { k: "disp_qty",    label: "Dispatch Qty" },
  { k: "pending_qty", label: "Pending Qty" },
  { k: "rate",        label: "Rate USD" },
  { k: "amount",      label: "Amount USD" },
  { k: "amount_inr",  label: "Amount INR" },
  { k: "delivery",    label: "Delivery" },
  { k: "incoterms",   label: "Incoterms" },
  { k: "status",      label: "Status" },
  { k: "wo_status",   label: "WO Status" },
];

function OrdersTab({ canEdit }) {
  // Mar 1, 2026 · Column-visibility picker for the ESO Orders grid.
  // Amount USD + Amount INR are new columns; the chooser hides/shows any
  // of the 14 toggleable columns (ESO No + Actions are pinned). Persists
  // per user in localStorage["mes.eso_orders.cols"].
  const [esoOrdersCols, setEsoOrdersCols] = useState(() => {
    try {
      const raw = localStorage.getItem("mes.eso_orders.cols");
      if (raw) {
        const persisted = new Set(JSON.parse(raw));
        // Feb 2026 · Auto-enable newly-added columns for existing users
        // (e.g. `wo_status`, `description`, `colour`, `size`, and the
        // qty-tracking triplet added in the SO Parity Sweep) — otherwise
        // their cached preference silently hides features shipped after
        // they first opened the page.
        for (const c of ESO_ORDERS_ALL_COLS) if (!persisted.has(c.k) && (c.k === "wo_status" || c.k === "description" || c.k === "colour" || c.k === "size" || c.k === "extra_qty" || c.k === "total_qty" || c.k === "disp_qty" || c.k === "pending_qty")) persisted.add(c.k);
        return persisted;
      }
    } catch (e) { /* ignore */ }
    return new Set(ESO_ORDERS_ALL_COLS.map(c => c.k));
  });
  const [esoOrdersMenu, setEsoOrdersMenu] = useState(false);
  const toggleEsoOrdersCol = (k) => setEsoOrdersCols(prev => {
    const next = new Set(prev);
    next.has(k) ? next.delete(k) : next.add(k);
    try { localStorage.setItem("mes.eso_orders.cols", JSON.stringify(Array.from(next))); } catch (e) { /* ignore */ }
    return next;
  });
  const resetEsoOrdersCols = () => {
    const next = new Set(ESO_ORDERS_ALL_COLS.map(c => c.k));
    setEsoOrdersCols(next);
    try { localStorage.setItem("mes.eso_orders.cols", JSON.stringify(Array.from(next))); } catch (e) { /* ignore */ }
  };
  const esoOrdersCol = (k) => esoOrdersCols.has(k);

  // Feb 2026 · Row-density toggle — mirrors the domestic SO grid so
  // buyers scanning long export backlogs can flip to Compact for
  // above-the-fold density parity. Persisted under
  // `mes.eso_orders.density` so the choice sticks across sessions.
  const [esoDensity, setEsoDensity] = useState(() => {
    try { return localStorage.getItem("mes.eso_orders.density") || "comfortable"; }
    catch { return "comfortable"; }
  });
  const setEsoDensityPersisted = (d) => {
    setEsoDensity(d);
    try { localStorage.setItem("mes.eso_orders.density", d); } catch (e) { /* ignore */ }
  };
  const esoCellPad = esoDensity === "compact" ? "px-2 py-1.5" : "px-3 py-2.5";

  const { user } = useAuth();
  // Aug 31, 2026 · Zero-Trust — DB-backed approval gate.
  const { hasPerm } = useAuth();
  const canAuth = user?.role === "Administrator" || !!hasPerm?.("exports:approve");
  const canSubmitEso = user?.role === "Administrator" || !!hasPerm?.("exports:submit") || !!hasPerm?.("exports:create");  // RBAC-B2-S3
  // Feb 2026 · ESO Print/PDF — opens the backend-generated PDF in a new tab
  // via a blob URL (avoids the "Missing token" pitfall by sending the JWT
  // through axios/fetch instead of a plain anchor).
  const esoPrintPdf = async (id) => {
    try {
      const res = await api.get(`/exports/orders/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      // Revoke shortly after so the browser has time to render the tab.
      setTimeout(() => window.URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to open ESO PDF");
    }
  };
  // Feb 27, 2026 · Browser-printable ESO that mirrors the EPI layout —
  // Blob-URL for Safari compatibility, opens in a new tab, cleans up shortly.
  const esoPrintHtml = async (id) => {
    try {
      const res = await api.get(`/exports/orders/${id}/print.html`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 8000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to open ESO print view");
    }
  };
  const [rows, setRows] = useState([]);
  const [plans, setPlans] = useState([]);
  const [slips, setSlips] = useState([]);
  const [articles, setArticles] = useState([]);
  const [itemsMaster, setItemsMaster] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // SOESO-DN01 · C-1/C-2 — one in-flight save at a time; one stable Idempotency-Key per genuine Create.
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const createKeyRef = useRef(null);
  const [linking, setLinking] = useState(null); // { type: 'plans'|'slips', row }
  const [viewing, setViewing] = useState(null); // ESO shown in the read-only View dialog
  const [adminAmendId, setAdminAmendId] = useState(null); // Phase 2C-4 · admin amend
  const [holdAction, setHoldAction] = useState(null); // Phase 2C-5 · {id, mode}
  // Feb 2026 · Optional intent carried into the ViewDialog. Set to "amend"
  // when the user clicks the amber Amend pencil on the grid — the dialog
  // auto-opens the amendment-reason prompt so it feels like a direct action
  // rather than a two-step View → Raise Amendment hop.
  const [viewIntent, setViewIntent] = useState(null);
  const empty = {
    order_date: new Date().toISOString().slice(0, 10),
    delivery_date: "",
    // Feb 2026 · Order-against tab (Customer PO vs Stock). Stock hides Buyer /
    // Consignee / PO fields; only line items are required.
    order_against: "customer_po",
    fulfilment_mode: "production_sales",
    buyer_customer_id: "",
    buyer_name: "", buyer_address: "", buyer_country: "",
    buyer_po_no: "", buyer_po_date: "",
    pi_no: "", pi_date: "",
    consignee_customer_id: "",
    consignee_name: "", consignee_address: "",
    incoterms: "FOB", port_of_loading: "", port_of_discharge: "",
    country_of_origin: "INDIA", country_of_final_destination: "",
    currency: "USD", fx_rate_to_inr: 83.0,
    payment_terms: "TT", lc_no: "", lc_date: "",
    items: [], freight_usd: 0, insurance_usd: 0, other_charges_usd: 0,
    remarks: "", status: "Draft",
  };
  const [form, setForm] = useState(empty);
  // Feb 2026 · Commercial Integrity — field-level red highlights on save.
  const [fieldErrors, setFieldErrors] = useState({});
  // Feb 2026 · WO Traceability — mirror SO dashboard's `woSummary` shape:
  // `{ "ESO|<id>": { count, statuses, wos:[{wo_no,status,item_code}] } }`.
  const [woSummary, setWoSummary] = useState({});
  // Feb 2026 · Pending Planning filter chip — surfaces ESOs with zero
  // linked WOs so the planner can zero in on the backlog.
  const [planFilter, setPlanFilter] = useState("all");   // "all" | "pending_planning" | "wo_linked"

  const load = async () => {
    const [o, p, s, a, im, cu, wos] = await Promise.all([
      api.get("/exports/orders/"),
      api.get("/production-plans/"),
      api.get("/packing-slips/"),
      api.get("/articles/"),
      api.get("/items/"),
      api.get("/customers/"),
      api.get("/work-orders/summary/by-source").catch(() => ({ data: {} })),
    ]);
    setRows(o.data); setPlans(p.data); setSlips(s.data);
    // Feb 2026 · Smart Lock filter — same rationale as SalesOrders.
    setArticles((a.data || []).filter(x => x.is_active !== false));
    setItemsMaster(im.data); setCustomers(cu.data || []);
    setWoSummary(wos.data || {});
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    // Feb 2026 · ESO No is auto-assigned on save — mirrors SO's proven
    // pattern. Backend `_eso_create_body` fills in `doc_no` when the
    // POST body arrives with it blank, so cancelled drafts never burn
    // a sequence number.
    // Feb 2026 · Auto-fill FX from FX Rate Master. Ask the backend for
    // the current USD → INR rate (server falls back to 83.0 when no
    // rate is stamped) before opening the dialog so the FX field
    // reflects today's master value from the get-go.
    (async () => {
      let fx = 83.0;
      try {
        const { data } = await api.get("/fx-rates/current", { params: { currency: "USD" } });
        if (data?.rate_to_inr) fx = Number(data.rate_to_inr);
      } catch (_e) { /* silent — keep the baked-in default */ }
      setForm({ ...empty, doc_no: "", fx_rate_to_inr: fx });
      setEditing(null);
      createKeyRef.current = uuidv4();
      setOpen(true);
    })();
  };
  const openEdit = (r) => {
    // Back-fill Customer Master linkage for legacy ESOs that were created
    // before `buyer_customer_id` / `consignee_customer_id` existed — match by
    // name so the picker shows the right entry instead of a blank placeholder.
    const byName = (nm) => nm ? (customers.find(c => (c.name || "").trim().toLowerCase() === (nm || "").trim().toLowerCase())?.id || "") : "";
    setForm({
      ...r,
      buyer_customer_id: r.buyer_customer_id || byName(r.buyer_name),
      consignee_customer_id: r.consignee_customer_id || byName(r.consignee_name),
    });
    setEditing(r.id);
    setOpen(true);
  };
  const addItem = () => setForm(f => ({ ...f, items: [...(f.items || []), { article_no: "", style_no: "", hs_code: "", qty: 0, extra_allowance_pct: 0, extra_qty: 0, total_qty: 0, uom: "MTR", unit_price_usd: 0, amount_usd: 0, delivery_date: f.delivery_date || "" }] }));
  const rmItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, x) => x !== i) }));
  const setItem = (i, k, v) => setForm(f => {
    const items = f.items.map((it, x) => x === i ? { ...it, [k]: v } : it);
    const li = items[i];
    const qty = Number(li.qty || 0);
    // Feb 2026 · line-wise Extra Allowance (SO parity). Editing % recomputes
    // Extra Qty; editing Extra Qty directly sticks (manual override wins).
    // Editing Order Qty recomputes Extra Qty from the current %. Total Qty
    // is always (qty + extra_qty), and drives the Production Qty aggregate
    // shown in the totals row.
    if (k === "qty") {
      li.extra_qty = +(qty * Number(li.extra_allowance_pct || 0) / 100).toFixed(2);
    } else if (k === "extra_allowance_pct") {
      li.extra_qty = +(qty * Number(v || 0) / 100).toFixed(2);
    }
    li.total_qty = +(qty + Number(li.extra_qty || 0)).toFixed(2);
    if (k === "qty" || k === "unit_price_usd") {
      li.amount_usd = +(qty * Number(li.unit_price_usd || 0)).toFixed(2);
    }
    return { ...f, items };
  });
  const sub = useMemo(() => (form.items || []).reduce((a, it) => a + Number(it.amount_usd || 0), 0), [form.items]);
  // Feb 2026 · Total Qty aggregate (SO parity) — Σ(Order) + Σ(Extra) = Total Qty.
  const qtyTotals = useMemo(() => {
    const its = form.items || [];
    const ord = its.reduce((a, it) => a + Number(it.qty || 0), 0);
    const ext = its.reduce((a, it) => a + Number(it.extra_qty || 0), 0);
    return { ordered: ord, extra: ext, total: ord + ext };
  }, [form.items]);
  const total = sub + Number(form.freight_usd || 0) + Number(form.insurance_usd || 0) + Number(form.other_charges_usd || 0);

  const save = async (submitAfter = false) => {
    if (savingRef.current) return;
    // Feb 2026 · Commercial Integrity Rules — hard-block save on empty
    // Buyer / lines / line-delivery-dates. LC + missing-PO/PI fire as
    // non-blocking warnings the user can save through.
    const errs = {};
    if (form.order_against !== "stock" && !form.buyer_name.trim()) errs.buyer_name = true;
    const items = form.items || [];
    if (!items.length) errs.items_empty = true;
    const hasValid = items.some(l => (Number(l.qty) || Number(l.order_qty) || 0) > 0 && (Number(l.unit_price_usd) || Number(l.rate) || 0) > 0);
    if (items.length && !hasValid) errs.items_qty_rate = true;
    const missingDelivery = items.findIndex(li => !((li.delivery_date || "").trim() || (form.delivery_date || "").trim()));
    if (items.length && missingDelivery >= 0) errs[`line_delivery_${missingDelivery}`] = true;
    setFieldErrors(errs);
    if (errs.buyer_name) { toast.error("Buyer name required (mandatory)"); return; }
    if (errs.items_empty) { toast.error("Add at least one line item"); return; }
    if (errs.items_qty_rate) { toast.error("At least one line needs Qty > 0 AND Rate > 0"); return; }
    if (missingDelivery >= 0) { toast.error(`Line ${missingDelivery + 1} · Required Delivery Date is missing`); return; }

    // Non-blocking warnings — Letter-of-Credit + missing PO/PI reference.
    if ((form.payment_terms || "").toUpperCase() === "LC") {
      toast.warning("Payment via LC selected. Please ensure documents are in order.");
    }
    if (!(form.buyer_po_no || "").trim() || !(form.pi_no || "").trim()) {
      toast.warning("PO/PI reference missing — you can still save and add it later.");
    }

    // Auto-header delivery = MAX of all line delivery dates. Ensures the
    // ESO header always reflects the latest committed shipment.
    const lineDates = items.map(l => (l.delivery_date || "").trim()).filter(Boolean);
    let effectiveHeader = form.delivery_date;
    if (lineDates.length) {
      const latest = lineDates.sort().slice(-1)[0];
      if (latest !== form.delivery_date) effectiveHeader = latest;
    }

    // Feb 2026 · Save-time HS Code back-fill — sweep every line item that
    // still has a blank HS Code and pull it from the linked Article record
    // (or the Product Master fallback). Guarantees the invoice / commercial
    // -invoice footer summary is accurate on the very first save even if the
    // operator skipped past the HS column after picking an article.
    const patched = items.map(row => {
      if ((row.hs_code || "").trim()) return row;
      const art = articles.find(a => a.product_code === row.article_no);
      const hs = (art?.hs_code || "").trim()
        || ((Array.isArray(itemsMaster) ? itemsMaster : []).find(m => (m.description || "") === (row.article_no || ""))?.hs_code || "");
      return hs ? { ...row, hs_code: hs } : row;
    });
    const payload = { ...form, delivery_date: effectiveHeader, items: patched };
    savingRef.current = true;
    setSaving(true);
    try {
      let id = editing;
      if (editing) await api.put(`/exports/orders/${editing}`, payload);
      else {
        if (!createKeyRef.current) createKeyRef.current = uuidv4();
        const { data } = await api.post("/exports/orders/", payload, { headers: { "Idempotency-Key": createKeyRef.current } });
        id = data.id;
        createKeyRef.current = null;
        setEditing(data.id);
      }
      if (submitAfter) {
        try {
          await api.post(`/exports/orders/${id}/submit`, {});
          toast.success("ESO · Submitted for Authorisation");
        } catch (se) {
          toast.error("ESO was saved as Draft, but Submit for Authorisation failed. You can submit it from the list.");
        }
      } else {
        toast.success(editing ? "ESO updated" : "ESO created");
      }
      setFieldErrors({});
      setOpen(false); load();
    } catch (e) {
      const st = e?.response?.status;
      const detail = e?.response?.data?.detail || "";
      if (!editing && createKeyRef.current && (!e?.response || st >= 500 || (st === 409 && /Still Processing/i.test(String(detail))))) {
        toast.error("Save not confirmed — your entries are kept. Retry safely: the same request is reused, so no duplicate ESO is created.", {
          action: { label: "Retry safely", onClick: () => save(submitAfter) },
          duration: 12000,
        });
        load();
      } else {
        toast.error(detail || "Save failed");
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  const remove = async (id) => {
    if (!confirm("Delete this ESO?")) return;
    try { await api.delete(`/exports/orders/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  // P1-ESO-MANAGEMENT-AUTH-CONTROL · Phase 2A · Feb 2026 — Owner
  // Decision 4 (Retire Authorize-from-Draft). Grid one-click Authorise
  // is now gated to PendingApproval only. Draft / Rejected ESOs must
  // route through Submit → Pending before Management may Authorise.
  const quickAuthorize = async (r) => {
    if (!confirm(`Authorise ${r.doc_no}? Once authorised, edits are only allowed via Raise Amendment.`)) return;
    try {
      await api.post(`/exports/orders/${r.id}/authorise`, {});
      toast.success(`${r.doc_no} authorised`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Authorise failed"); }
  };
  const submitLink = async (ids) => {
    if (!ids.length) { toast.error("Select at least one"); return; }
    try {
      const key = linking.type === "plans" ? "plan_ids" : "slip_ids";
      const url = linking.type === "plans" ? "link-plans" : "link-slips";
      await api.post(`/exports/orders/${linking.row.id}/${url}`, { [key]: ids });
      toast.success(`Linked ${ids.length} ${linking.type}`);
      setLinking(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Link failed"); }
  };

  const statusColor = (s) => ({
    Draft: "bg-zinc-500/10 text-zinc-400",
    Open: "bg-blue-500/10 text-blue-400",
    Confirmed: "bg-blue-500/10 text-blue-400",
    Planned: "bg-blue-500/10 text-blue-400",
    Running: "bg-emerald-500/10 text-emerald-400",
    Completed: "bg-emerald-500/10 text-emerald-400",
    Shipped: "bg-amber-500/10 text-amber-400",
    Closed: "bg-emerald-500/10 text-emerald-400",
  }[s] || "bg-zinc-500/10 text-zinc-400");

  // Search + filter state (Feb 2026) — mirrors the SO Orders tab. Free-text search
  // spans ESO No / Buyer / Buyer PO / Article. Empty search shows everything.
  const [esoSearch, setEsoSearch] = useState("");
  const buyerName = (bcid, fallback = "") => {
    if (bcid) {
      const c = customers.find(x => x.id === bcid);
      if (c) return c.name;
    }
    return fallback;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-zinc-500">{rows.length} export order{rows.length !== 1 ? "s" : ""}</div>
        {canEdit && (
          <button data-testid="new-eso-btn" onClick={openCreate} className="h-9 px-4 bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold rounded-sm flex items-center gap-2">
            <PlusIcon size={14} weight="bold" /> New Export SO
          </button>
        )}
      </div>

      {/* Mar 3, 2026 · Slim pinned KPI strip — matches the SO Orders tab
          pattern (SalesOrders.jsx `so-summary-bar`). Rolls up qty + ₹
          totals across every ESO row: Total Value / Dispatched Value /
          Pending Value use the row's own `fx_rate_to_inr` so INR
          conversion matches the Amount INR column. Partial dispatches
          are exact because disp_qty is per-line. */}
      {(() => {
        const t = rows.reduce((a, r) => {
          const fx = Number(r.fx_rate_to_inr || 0);
          // R2-01L A4-02 · G5/D4 · actionable pending follows PSOR-01 eligibility; submitted approvals shown apart.
          const actionable = isActionablePending("eso", r);
          const awaitingApproval = isSubmittedPendingApproval("eso", r);
          for (const li of (r.items || [])) {
            const q = Number(li.qty) || 0;
            const ex = Number(li.extra_qty) || 0;
            const cx = Number(li.cancelled_qty) || 0;
            const dq = Number(li.disp_qty) || 0;
            const price = Number(li.unit_price_usd) || 0;
            // Phase 2C-5B2 · HEADER commercial KPI = customer Order Qty
            // ONLY. Buffer / extra_qty is a production allowance and must
            // never inflate commercial Order / Dispatch / Pending / Value.
            // Line-level Total Qty (order + buffer) stays in the table.
            const orderable = Math.max(0, q - cx);
            const open = li.short_closed ? 0 : Math.max(0, q - cx - dq);
            const pend = actionable ? open : 0;
            if (awaitingApproval) a.approvalQ += open;
            a.qty     += q;
            a.dispQ   += dq;
            a.pendQ   += pend;
            a.valUsd  += orderable * price;
            a.dispUsd += dq * price;
            a.pendUsd += pend * price;
            a.valInr  += orderable * price * fx;
            a.dispInr += dq * price * fx;
            a.pendInr += pend * price * fx;
          }
          return a;
        }, { qty: 0, dispQ: 0, pendQ: 0, valUsd: 0, dispUsd: 0, pendUsd: 0, valInr: 0, dispInr: 0, pendInr: 0, approvalQ: 0 });
        const cell = "font-mono tabular-nums";
        const fmtI = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
        return (
          <div
            data-testid="eso-summary-bar"
            className="flex flex-wrap items-center gap-x-5 gap-y-1 px-3 py-1.5 border border-[var(--border)] bg-[var(--bg-1)] rounded-sm text-xs"
          >
            <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Orders</span>
            <span className={cell + " text-white font-semibold"} data-testid="eso-summary-orders">{rows.length}</span>

            <span className="h-3 w-px bg-[var(--border)]" />
            <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Order Qty (m)</span>
            <span className={cell + " text-[var(--accent)] font-semibold"} data-testid="eso-summary-total-qty">{Number(t.qty).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span>

            <span className="h-3 w-px bg-[var(--border)]" />
            <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Dispatched</span>
            <span className={cell + " text-emerald-300 font-semibold"} data-testid="eso-summary-disp-qty">{Number(t.dispQ).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span>

            <span className="h-3 w-px bg-[var(--border)]" />
            <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Pending</span>
            <span className={cell + ` font-semibold ${t.pendQ > 0 ? "text-amber-300" : "text-emerald-300"}`} data-testid="eso-summary-pend-qty" title="Actionable pending · authorised orders only (same rule as the Pending Order Statement)">{Number(t.pendQ).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span>

            <span className="h-3 w-px bg-[var(--border)]" />
            <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Pending Approval</span>
            <span className={cell + " text-zinc-300 font-semibold"} data-testid="eso-summary-pending-approval" title="Submitted orders awaiting authorisation · not included in actionable Pending">{Number(t.approvalQ).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span>

            <span className="h-3 w-px bg-[var(--border)]" />
            <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Total Value</span>
            <span className={cell + " text-white font-semibold"} data-testid="eso-summary-total-value" title={`$${Number(t.valUsd).toLocaleString("en-US", { maximumFractionDigits: 0 })}`}>{fmtI(t.valInr)}</span>

            <span className="h-3 w-px bg-[var(--border)]" />
            <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Dispatched Value</span>
            <span className={cell + " text-emerald-300 font-semibold"} data-testid="eso-summary-disp-value" title={`$${Number(t.dispUsd).toLocaleString("en-US", { maximumFractionDigits: 0 })}`}>{fmtI(t.dispInr)}</span>

            <span className="h-3 w-px bg-[var(--border)]" />
            <span className="text-zinc-500 uppercase tracking-widest text-[9px]">Pending Value</span>
            <span className={cell + ` font-semibold ${t.pendInr > 0 ? "text-amber-300" : "text-emerald-300"}`} data-testid="eso-summary-pend-value" title={`$${Number(t.pendUsd).toLocaleString("en-US", { maximumFractionDigits: 0 })}`}>{fmtI(t.pendInr)}</span>
          </div>
        );
      })()}

      {/* Global search — matches SO Orders tab UX */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative w-full max-w-md">
          <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            data-testid="eso-search"
            value={esoSearch}
            onChange={e => setEsoSearch(e.target.value)}
            placeholder="Search ESO no, buyer, PO, article no…"
            className="w-full h-9 pl-9 pr-3 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm placeholder:text-zinc-500 focus:outline-none focus:border-[var(--accent)]"
          />
        </div>
        {esoSearch && (
          <button data-testid="eso-search-clear" onClick={() => setEsoSearch("")} className="h-9 px-3 text-xs text-zinc-400 hover:text-white border border-[var(--border)] rounded-sm">Clear</button>
        )}
        {/* Feb 2026 · WO Traceability filter chips */}
        {(() => {
          const pendingPlanningCount = rows.filter(r => {
            const b = woSummary[`ESO|${r.id}`];
            return !b || !b.count;
          }).length;
          const linkedCount = rows.length - pendingPlanningCount;
          const tabs = [
            { k: "all",              label: "All",              count: rows.length },
            { k: "pending_planning", label: "Pending Planning", count: pendingPlanningCount },
            { k: "wo_linked",        label: "WO Linked",        count: linkedCount },
          ];
          return (
            <div className="inline-flex rounded-sm border border-[var(--border)] overflow-hidden ml-auto" data-testid="eso-plan-filter-strip">
              {tabs.map(t => (
                <button
                  key={t.k}
                  data-testid={`eso-plan-filter-${t.k}`}
                  onClick={() => setPlanFilter(t.k)}
                  className={`px-3 h-9 text-[11px] font-medium uppercase tracking-widest border-r border-[var(--border)] last:border-r-0 ${planFilter === t.k ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-2)]"}`}
                >
                  {t.label}
                  <span className={`ml-1.5 font-mono text-[10px] ${planFilter === t.k ? "opacity-80" : "text-zinc-500"}`}>{t.count}</span>
                </button>
              ))}
            </div>
          );
        })()}
      </div>

      <div className="tactical-card overflow-x-auto">
        {/* Mar 1, 2026 · Column chooser for the ESO Orders grid — mirrors the
            Status Report chooser. Pinned columns: ESO No + Actions.
            Feb 2026 · Also hosts the Compact/Comfy density toggle so ESO
            visually matches the domestic SO grid. */}
        <div className="flex items-center justify-end px-4 py-2 border-b border-[var(--border)] relative gap-2">
          <div className="inline-flex rounded-sm border border-[var(--border)] overflow-hidden" role="group" aria-label="Row density">
            {[["comfortable", "Comfy"], ["compact", "Compact"]].map(([k, l]) => (
              <button
                key={k}
                data-testid={`eso-density-${k}`}
                onClick={() => setEsoDensityPersisted(k)}
                className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1.5 ${esoDensity === k ? "bg-[var(--accent)]/15 text-[var(--accent)]" : "text-zinc-400 hover:text-zinc-200"}`}
              >{l}</button>
            ))}
          </div>
          <button data-testid="eso-orders-cols-btn"
            onClick={() => setEsoOrdersMenu(v => !v)}
            className="text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 border border-[var(--border)] hover:border-[var(--accent)] rounded-sm text-zinc-300 flex items-center gap-2"
            title="Show / hide columns">
            <span className="font-mono">{esoOrdersCols.size}/{ESO_ORDERS_ALL_COLS.length}</span> Columns
          </button>
          {esoOrdersMenu && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setEsoOrdersMenu(false)} />
              <div className="absolute right-4 top-11 z-40 w-64 bg-[var(--bg-1)] border border-[var(--border)] rounded-sm shadow-xl p-2 max-h-96 overflow-y-auto" data-testid="eso-orders-cols-menu">
                <div className="flex items-center justify-between px-1 pb-2 border-b border-[var(--border)] mb-2">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Show columns</div>
                  <button onClick={resetEsoOrdersCols} data-testid="eso-orders-cols-reset" className="text-[10px] font-mono uppercase tracking-widest text-[var(--accent)] hover:underline">Reset</button>
                </div>
                {ESO_ORDERS_ALL_COLS.map(c => (
                  <label key={c.k} className="flex items-center gap-2 px-2 py-1 rounded-sm text-xs cursor-pointer hover:bg-[var(--bg-2)]">
                    <input type="checkbox" data-testid={`eso-orders-col-${c.k}`}
                      checked={esoOrdersCols.has(c.k)}
                      onChange={() => toggleEsoOrdersCol(c.k)}
                      className="w-3.5 h-3.5 accent-[var(--accent)]" />
                    <span className="text-zinc-200">{c.label}</span>
                  </label>
                ))}
              </div>
            </>
          )}
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className={`text-right ${esoCellPad}`}>Sales Order No</th>
              {esoOrdersCol("order_date") && <th className={`text-left ${esoCellPad}`}>Date</th>}
              {esoOrdersCol("buyer")      && <th className={`text-left ${esoCellPad}`}>Customer</th>}
              {esoOrdersCol("buyer_po")   && <th className={`text-left ${esoCellPad}`}>Customer PO No</th>}
              {esoOrdersCol("item")       && <th className={`text-right ${esoCellPad}`}>Item No</th>}
              {esoOrdersCol("style")      && <th className={`text-left ${esoCellPad}`}>Style No</th>}
              {esoOrdersCol("article")    && <th className={`text-left ${esoCellPad} sticky left-0 z-30 bg-[var(--bg-1)] shadow-[1px_0_0_var(--border)]`}>Product Code</th>}
              {esoOrdersCol("hs")         && <th className={`text-left ${esoCellPad}`}>HS Code</th>}
              {esoOrdersCol("description") && <th className={`text-left ${esoCellPad}`}>Description</th>}
              {esoOrdersCol("colour")     && <th className={`text-left ${esoCellPad}`}>Colour</th>}
              {esoOrdersCol("size")       && <th className={`text-left ${esoCellPad}`}>Size</th>}
              {esoOrdersCol("qty")        && <th className={`text-right ${esoCellPad}`}>Order Qty</th>}
              {esoOrdersCol("extra_qty")  && <th className={`text-right ${esoCellPad}`} title="Buffer Qty · technical over-dispatch cap (never counted in Pending math)">Buffer Qty</th>}
              {esoOrdersCol("total_qty")  && <th className={`text-right ${esoCellPad}`} title="Total Qty = Order + Buffer">Total Qty</th>}
              {esoOrdersCol("disp_qty")   && <th className={`text-right ${esoCellPad}`} title="Dispatched via Export Packing Slips shipped">Dispatch Qty</th>}
              {esoOrdersCol("pending_qty") && <th className={`text-right ${esoCellPad}`} title="Pending = Order − Cancelled − Dispatch">Pending Qty</th>}
              {esoOrdersCol("rate")       && <th className={`text-right ${esoCellPad}`}>Rate USD</th>}
              {esoOrdersCol("amount")     && <th className={`text-right ${esoCellPad}`}>Amount USD</th>}
              {esoOrdersCol("amount_inr") && <th className={`text-right ${esoCellPad}`}>Amount INR</th>}
              {esoOrdersCol("delivery")   && <th className={`text-left ${esoCellPad}`}>Delivery</th>}
              {esoOrdersCol("incoterms")  && <th className={`text-left ${esoCellPad}`}>Incoterms</th>}
              {esoOrdersCol("status")     && <th className={`text-left ${esoCellPad}`}>Status</th>}
              {esoOrdersCol("wo_status")  && <th className={`text-left ${esoCellPad}`} title="Linked Work Orders — mirrors the domestic SO chip; No WO / Draft WO / WO Active / Completed">WO Status</th>}
              <th className={`text-right ${esoCellPad}`}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {(() => {
              const q = esoSearch.trim().toLowerCase();
              const searched = !q ? rows : rows.filter(r =>
                (r.doc_no || "").toLowerCase().includes(q) ||
                (r.buyer_name || "").toLowerCase().includes(q) ||
                (r.buyer_po_no || "").toLowerCase().includes(q) ||
                (r.items || []).some(li => (li.article_no || "").toLowerCase().includes(q))
              );
              // Feb 2026 · WO Traceability filter — Pending Planning keeps
              // only ESOs with zero linked WOs; WO Linked keeps only ESOs
              // with at least one WO in any state.
              const list = searched.filter(r => {
                if (planFilter === "all") return true;
                const b = woSummary[`ESO|${r.id}`];
                const hasWo = !!(b && b.count);
                return planFilter === "wo_linked" ? hasWo : !hasWo;
              });
              return (<>
                {list.flatMap(r => {
                  const lines = (r.items || []);
                  const linesToRender = lines.length ? lines : [null];
                  return linesToRender.map((line, lineIdx) => {
                    const isFirst = lineIdx === 0;
                    const isLast = lineIdx === linesToRender.length - 1;
                    const bName = buyerName(r.buyer_customer_id, r.buyer_name || "—");
                    // Feb 2026 · Cancelled / Rejected ESOs get a red
                    // strike-through row visual signal — the row remains
                    // in the grid but is muted so operators know it's
                    // out-of-scope for downstream planning.
                    // DP-ESO-AUTH-REJECT · Feb 2026 — Rejected ESOs
                    // remain editable (management asked for correction);
                    // we keep the visual signal but action gating no
                    // longer conflates the two statuses (see
                    // `isCancelled` narrowing above).
                    const isEsoCancelled = (r.status || "").toLowerCase() === "cancelled" || (r.status || "").toLowerCase() === "rejected";
                    return (
                      <tr
                        key={`${r.id}-${lineIdx}`}
                        data-testid={`eso-row-${r.doc_no}-${lineIdx}`}
                        className={`${!isLast ? "border-b border-[var(--border)]/40" : "border-b border-[var(--border)]"} align-top hover:bg-[var(--bg-2)]/40 ${isEsoCancelled ? "[&>td]:text-zinc-500 opacity-75" : ""}`}
                      >
                        <td className={`${esoCellPad} font-mono text-right`}>
                          <div className="flex flex-col items-end gap-1">
                            {/* ESO Register · complete ESO number on ONE line (no wrap /
                                no ellipsis), repeated identically on every line-item row.
                                UI-only — document number value is unchanged. */}
                            <span
                              data-testid={isFirst ? `eso-row-doc-${r.doc_no}` : `eso-row-doc-${r.doc_no}-${lineIdx}`}
                              className="font-mono whitespace-nowrap text-white text-xs font-semibold"
                            >{r.doc_no}{r.order_against === "stock" && (<><span className="text-zinc-500 mx-1">·</span><StockBadge testId={isFirst ? `eso-stock-chip-${r.doc_no}` : undefined} /></>)}</span>
                            {isFirst && fulfilmentOf(r) === "sales_only" && (
                              <span data-testid={`eso-sales-only-chip-${r.doc_no}`} className="px-1.5 py-0.5 rounded-sm text-[9px] font-mono uppercase tracking-widest bg-amber-500/15 text-amber-300 w-fit">SALES ONLY</span>
                            )}
                          </div>
                        </td>
                        {esoOrdersCol("order_date") && <td className={`${esoCellPad} font-mono text-zinc-400 whitespace-nowrap`}>{r.order_date || "—"}</td>}
                        {esoOrdersCol("buyer")      && <td className={`${esoCellPad}`}><span className="text-zinc-200 font-medium leading-tight" style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", maxWidth: "170px" }} title={bName}>{bName}</span></td>}
                        {esoOrdersCol("buyer_po")   && <td className={`${esoCellPad} font-mono text-zinc-400 whitespace-nowrap`}>{r.buyer_po_no || "—"}</td>}
                        {esoOrdersCol("item")       && <td className={`${esoCellPad} text-right font-mono text-zinc-300 text-xs`}>{line ? String(lineIdx + 1).padStart(3, "0") : "—"}</td>}
                        {esoOrdersCol("style")      && <td className={`${esoCellPad} font-mono text-zinc-300 text-xs`}>{line ? (line.style_no || "—") : "—"}</td>}
                        {esoOrdersCol("article")    && <td className={`${esoCellPad} sticky left-0 z-10 bg-[var(--bg-1)] shadow-[1px_0_0_var(--border)]`}>
                          {line ? <span className="font-mono text-[var(--accent)] text-xs whitespace-nowrap" title={line.article_no || ""}>{line.article_no || "—"}</span> : <span className="text-zinc-500">—</span>}
                        </td>}
                        {esoOrdersCol("hs")         && <td className={`${esoCellPad} font-mono text-zinc-400 text-xs`}>{line ? (line.hs_code || "—") : "—"}</td>}
                        {esoOrdersCol("description") && <td className={`${esoCellPad} text-zinc-300 text-xs`} data-testid={`eso-line-description-${r.doc_no}-${lineIdx}`}>{(() => {
                          if (!line) return <span className="text-zinc-500">—</span>;
                          // Feb 2026 · Description parity with domestic SO —
                          // prefer article-master `description`, fallback to
                          // whatever's snapshotted on the ESO line.
                          const art = (articles || []).find(a => a.id === line.article_id || a.product_code === line.article_no);
                          const desc = art?.description || line.description || "—";
                          return <span title={desc}>{desc}</span>;
                        })()}</td>}
                        {esoOrdersCol("colour")     && <td className={`${esoCellPad} text-zinc-300 text-xs`} data-testid={`eso-line-colour-${r.doc_no}-${lineIdx}`}>{(() => {
                          if (!line) return <span className="text-zinc-500">—</span>;
                          const art = (articles || []).find(a => a.id === line.article_id || a.product_code === line.article_no);
                          const c = art?.colour || line.colour || "—";
                          return <span>{c}</span>;
                        })()}</td>}
                        {esoOrdersCol("size")       && <td className={`${esoCellPad} font-mono text-zinc-300 text-xs`} data-testid={`eso-line-size-${r.doc_no}-${lineIdx}`}>{(() => {
                          if (!line) return <span className="text-zinc-500">—</span>;
                          const art = (articles || []).find(a => a.id === line.article_id || a.product_code === line.article_no);
                          const s = art?.size || line.size || "—";
                          return <span>{s}</span>;
                        })()}</td>}
                        {esoOrdersCol("qty")        && <td className={`${esoCellPad} text-right`}>
                          {line ? (
                            <span className="font-mono font-semibold text-white tabular-nums" data-testid={`eso-line-qty-${r.doc_no}-${lineIdx}`}>
                              {Number(line.qty || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                            </span>
                          ) : <span className="text-zinc-500">—</span>}
                        </td>}
                        {esoOrdersCol("extra_qty")  && <td className={`${esoCellPad} text-right`}>{(() => {
                          if (!line) return <span className="text-zinc-500">—</span>;
                          const buf = Number(line.extra_qty) || 0;
                          if (!buf) return <span className="font-mono text-zinc-500 tabular-nums" data-testid={`eso-line-buffer-${r.doc_no}-${lineIdx}`}>0</span>;
                          return (
                            <span className="font-mono text-zinc-300 tabular-nums text-xs" data-testid={`eso-line-buffer-${r.doc_no}-${lineIdx}`} title="Buffer Qty · technical over-dispatch cap. NEVER counted in Pending math.">
                              {Number(buf).toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                            </span>
                          );
                        })()}</td>}
                        {esoOrdersCol("total_qty")  && <td className={`${esoCellPad} text-right`}>{(() => {
                          if (!line) return <span className="text-zinc-500">—</span>;
                          const q = Number(line.qty) || 0;
                          const b = Number(line.extra_qty) || 0;
                          const t = Number(line.total_qty ?? (q + b));
                          return <span className="font-mono text-zinc-400 tabular-nums text-xs" data-testid={`eso-line-total-${r.doc_no}-${lineIdx}`} title="Total Qty = Order + Buffer · technical cap only, not the pending target.">{t.toLocaleString("en-IN", { maximumFractionDigits: 0 })}</span>;
                        })()}</td>}
                        {esoOrdersCol("disp_qty")   && <td className={`${esoCellPad} text-right`}>{(() => {
                          if (!line) return <span className="text-zinc-500">—</span>;
                          const d = Number(line.disp_qty) || 0;
                          return (
                            <span className="font-mono font-semibold text-emerald-300 tabular-nums" data-testid={`eso-line-disp-${r.doc_no}-${lineIdx}`} title="Dispatched via Export Packing Slips shipped">
                              {d.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                            </span>
                          );
                        })()}</td>}
                        {esoOrdersCol("pending_qty") && <td className={`${esoCellPad} text-right`}>{(() => {
                          if (!line) return <span className="text-zinc-500">—</span>;
                          // Mirrors backend _eso_progress_and_risk pending math:
                          // pending = max(0, order − cancelled − disp). Short-closed lines force 0.
                          const q = Number(line.qty) || 0;
                          const c = Number(line.cancelled_qty) || 0;
                          const d = Number(line.disp_qty) || 0;
                          const p = line.short_closed ? 0 : Math.max(0, q - c - d);
                          const cls = p > 0 ? "text-amber-300" : "text-emerald-400";
                          return (
                            <span className={`font-mono font-semibold tabular-nums ${cls}`} data-testid={`eso-line-pending-${r.doc_no}-${lineIdx}`} title="Pending = Order − Cancelled − Dispatch · Buffer never enters this math.">
                              {p.toLocaleString("en-IN", { maximumFractionDigits: 0 })}
                            </span>
                          );
                        })()}</td>}
                        {esoOrdersCol("rate")       && <td className={`${esoCellPad} text-right font-mono text-zinc-300 text-xs`}>{line ? `$${Number(line.unit_price_usd || 0).toFixed(4)}` : "—"}</td>}
                        {esoOrdersCol("amount")     && <td className={`${esoCellPad} text-right font-mono text-emerald-300 text-xs font-semibold`} data-testid={`eso-amount-usd-${r.doc_no}-${lineIdx}`}>{line ? `$${Number(line.amount_usd || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}</td>}
                        {esoOrdersCol("amount_inr") && <td className={`${esoCellPad} text-right font-mono text-amber-300 text-xs`} data-testid={`eso-amount-inr-${r.doc_no}-${lineIdx}`} title={`FX @ ₹${Number(r.fx_rate_to_inr || 0).toFixed(2)}`}>{line ? `₹${(Number(line.amount_usd || 0) * Number(r.fx_rate_to_inr || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}</td>}
                        {esoOrdersCol("delivery")   && <td className={`${esoCellPad} font-mono`}>{isFirst ? (r.delivery_date ? (() => {
                          // Delivery mirrors the SEALED Domestic SO Register exactly:
                          // inline-flex, no nowrap — the compact column lets the date
                          // wrap to a second line (with the Overdue pill) when tight.
                          // Risk calculation is unchanged (uses delivery_risk).
                          const risk = r.delivery_risk || {};
                          const overdue = (risk.days_left != null && risk.days_left < 0) || /overdue/i.test(risk.label || "");
                          return (
                            <span className="inline-flex items-center gap-1.5" data-testid={`eso-risk-${r.doc_no}-${risk.state || "unknown"}`} title={risk.label || ""}>
                              <span className={`font-mono text-xs ${overdue ? "text-amber-400 font-semibold" : "text-zinc-400"}`}>{r.delivery_date}</span>
                              {overdue && (
                                <span data-testid={`eso-overdue-${r.doc_no}`} title={risk.label || "Delivery date has passed"} className="px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-400 text-[9px] font-mono uppercase tracking-widest">Overdue</span>
                              )}
                            </span>
                          );
                        })() : <span className="text-zinc-600">—</span>) : null}</td>}
                        {esoOrdersCol("incoterms")  && <td className={`${esoCellPad} font-mono text-zinc-400`}>{isFirst ? r.incoterms : null}</td>}
                        {esoOrdersCol("status")     && <td className={`${esoCellPad}`}>{isFirst ? (() => {
                          // Visual hierarchy: lifecycle PRIMARY · authorisation SECONDARY.
                          // Delivery risk/overdue now lives ONLY in the Delivery column
                          // (single source of visual responsibility). No logic changed.
                          const authS = r.auth_status || "Draft";
                          const amendTag = (r.amendment_no || 0) > 0 ? ` · R${r.amendment_no}` : "";
                          return (
                            <div className="flex flex-col gap-1">
                              <span className={`px-2 py-0.5 rounded-sm text-[10px] font-mono uppercase tracking-widest w-fit ${statusColor(r.status)}`}>{r.status}</span>
                              <span data-testid={`eso-auth-${r.doc_no}-${authS}`} className="text-[10px] font-mono text-zinc-500 w-fit" title={authS === "Authorised" && r.authorised_by ? `Authorised by ${r.authorised_by}${amendTag}` : (amendTag ? `Revision${amendTag}` : "")}>{authStatusLabel(authS)}</span>
                            </div>
                          );
                        })() : null}</td>}
                        {esoOrdersCol("wo_status") && <td className={`${esoCellPad}`}>{isFirst ? (() => {
                          // Feb 2026 · WO Traceability chip — mirrors the
                          // SO OrdersTab renderer. `woSummary[ESO|<id>]`
                          // returns `{ count, statuses, wos }` so we can
                          // color-code No WO / Draft WO / WO Active /
                          // Completed without an extra API call.
                          const b = woSummary[`ESO|${r.id}`] || woSummary[`SO|${r.id}`];
                          if (!b || !b.count) return <span data-testid={`eso-wo-none-${r.doc_no}`} className="text-[11px] text-zinc-600 font-mono">No WO</span>;
                          const st = b.statuses || {};
                          const parts = Object.entries(st).map(([k, v]) => `${v} ${k}`).join(" · ");
                          // Line 1: WO count — subtle information accent. Line 2: status
                          // composition — smaller muted text, truncated to one line.
                          // Full WO linkage stays in the tooltip.
                          return (
                            <div className="leading-tight" data-testid={`eso-wo-chip-orders-${r.doc_no}`} title={b.wos.map(w => `${w.wo_no} · ${w.item_code} · ${w.status}`).join("\n")}>
                              <span className="font-mono text-xs font-semibold text-sky-300">{b.count} {b.count === 1 ? "WO" : "WOs"}</span>
                              <span className="block text-[10px] font-mono text-zinc-500 whitespace-nowrap overflow-hidden text-ellipsis max-w-[160px]">{parts}</span>
                            </div>
                          );
                        })() : null}</td>}
                        <td className={`${esoCellPad}`}>
                          {isFirst ? (() => {
                            // ESO Register V1 · consolidated Actions dropdown (SO V1
                            // parity). Only existing permitted actions; every handler,
                            // testid, RBAC gate, auth gate and lifecycle gate reused
                            // unchanged. Link-Plans / Link-Slips keep their disabled
                            // (Pending-Authorisation) behavior.
                            const isCanc = (r.status || "").toLowerCase() === "cancelled";
                            const isShortClosed = (r.status || "").toLowerCase() === "short closed";
                            const isPendingAuth = r.auth_status === "PendingApproval" || r.approval_status === "Pending Approval";
                            const items = [];
                            items.push(<DropdownMenuItem key="view" data-testid={`eso-view-${r.doc_no}`} onClick={() => setViewing(r)}>View</DropdownMenuItem>);
                            items.push(<DropdownMenuItem key="print" data-testid={`eso-print-html-${r.doc_no}`} className="text-sky-300" onClick={() => esoPrintHtml(r.id)}>Browser Print (A4)</DropdownMenuItem>);
                            items.push(<DropdownMenuItem key="dl-pdf" data-testid={`eso-download-pdf-${r.doc_no}`} className="text-rose-300" onClick={() => downloadDoc("ESO", r.id, "pdf")}>Download PDF</DropdownMenuItem>);
                            items.push(<DropdownMenuItem key="dl-xlsx" data-testid={`eso-export-excel-${r.doc_no}`} className="text-emerald-300" onClick={() => downloadDoc("ESO", r.id, "xlsx")}>Export to Excel</DropdownMenuItem>);
                            if (canAuth && r.auth_status === "PendingApproval" && !isCanc)
                              items.push(<DropdownMenuItem key="auth" data-testid={`eso-authorize-${r.doc_no}`} className="text-emerald-300" onClick={() => quickAuthorize(r)}>Authorise</DropdownMenuItem>);
                            if (canEdit && (r.auth_status === "Authorised" || isShortClosed) && !isCanc)
                              items.push(<DropdownMenuItem key="admin" data-testid={`eso-admin-amend-${r.doc_no}`} className="text-blue-400" onClick={() => setAdminAmendId(r.id)}>Administrative Amendment…</DropdownMenuItem>);
                            if (canEdit && r.auth_status !== "Authorised" && !isPendingAuth && !isCanc)
                              items.push(<DropdownMenuItem key="edit" data-testid={`eso-edit-${r.doc_no}`} onClick={() => openEdit(r)}>Edit</DropdownMenuItem>);
                            if (canEdit)
                              items.push(<DropdownMenuItem key="link-plans" data-testid={`eso-link-plans-${r.doc_no}`} disabled={isPendingAuth} onClick={() => !isPendingAuth && setLinking({ type: "plans", row: r })}>Link Production Plans…</DropdownMenuItem>);
                            if (canEdit)
                              items.push(<DropdownMenuItem key="link-slips" data-testid={`eso-link-slips-${r.doc_no}`} disabled={isPendingAuth} onClick={() => !isPendingAuth && setLinking({ type: "slips", row: r })}>Link Packing Slips…</DropdownMenuItem>);
                            return (
                              <div className="flex justify-end">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button data-testid={`eso-actions-${r.doc_no}`} className="h-7 px-2.5 rounded-sm border border-[var(--border)] text-zinc-300 text-[11px] hover:bg-white/10 whitespace-nowrap">Actions ▾</button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="bg-[var(--bg-1)] border-[var(--border)] text-sm min-w-[200px]">
                                    {items}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            );
                          })() : null}
                        </td>
                      </tr>
                    );
                  });
                })}
                {rows.length === 0 && <tr><td colSpan={14} className="py-16 text-center text-zinc-500">No export orders yet</td></tr>}
                {rows.length > 0 && list.length === 0 && <tr><td colSpan={14} className="py-10 text-center text-zinc-500">No orders match "{esoSearch}"</td></tr>}
              </>);
            })()}
          </tbody>
        </table>
      </div>

      {/* ESO create/edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="eso-editor-dialog" className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-[1200px] max-h-[92vh] overflow-y-auto space-y-3" description="Export document action — Proforma / Packing List / Sales Order / Delivery Challan.">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-3">
            <FileTextIcon size={18} weight="bold" className="text-[var(--accent)]" />
            <span>{editing ? "Edit Export SO" : "New Export SO"} · {form.doc_no || <span className="text-zinc-500 text-sm">Auto · assigned on save</span>}</span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-amber-300 border border-amber-500/40 bg-amber-500/5 px-2 py-0.5 rounded-sm">Export</span>
            <span className={`ml-2 inline-flex items-center px-2 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-widest ${(form.status === "Draft" ? "border-zinc-500/40 bg-zinc-500/5 text-zinc-300" : form.status === "Confirmed" ? "border-blue-500/40 bg-blue-500/5 text-blue-300" : form.status === "Shipped" ? "border-emerald-500/40 bg-emerald-500/5 text-emerald-300" : "border-zinc-500/40 bg-zinc-500/5 text-zinc-300")}`}>{form.status || "Draft"}</span>
            {form.order_against === "stock" && (
              <span data-testid="eso-dialog-stock-chip" className="inline-flex items-center gap-1"><span className="text-zinc-500">·</span><StockBadge /></span>
            )}
          </DialogTitle></DialogHeader>

          {/* Feb 2026 · Order-Against segmented tab. Switches between the
              traditional Buyer-PO flow and Against-Stock (build for stock,
              no buyer required). Stock hides Buyer / Consignee / PO fields. */}
          <div className="flex items-center gap-3" data-testid="eso-order-against-tabs">
            <div className="overline">Order Type</div>
            <div className="inline-flex rounded-sm border border-[var(--border)] overflow-hidden">
              {[
                { k: "customer_po", label: "Against Customer PO" },
                { k: "stock",       label: "Against Stock" },
              ].map(t => (
                <button
                  key={t.k}
                  type="button"
                  data-testid={`eso-order-against-${t.k}`}
                  onClick={() => setForm(f => ({
                    ...f,
                    order_against: t.k,
                    // Clear buyer + consignee + PO fields on Stock switch so
                    // the ESO payload stays clean.
                    ...(t.k === "stock" ? {
                      buyer_customer_id: "", buyer_name: "", buyer_address: "", buyer_country: "",
                      buyer_po_no: "", buyer_po_date: "",
                      consignee_customer_id: "", consignee_name: "", consignee_address: "",
                      fulfilment_mode: "production_sales",
                    } : {}),
                  }))}
                  className={`px-3 h-8 text-[11px] font-medium uppercase tracking-widest border-r border-[var(--border)] last:border-r-0 ${(form.order_against || "customer_po") === t.k ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-2)]"}`}
                >{t.label}</button>
              ))}
            </div>
            {form.order_against === "stock" && (
              <span className="text-[10px] text-emerald-400 font-mono uppercase tracking-widest" title="Buyer & PO fields are hidden — this ESO builds for stock.">Stock Only — No Sales · no buyer required</span>
            )}
            {form.order_against !== "stock" && (
              <FulfilmentChoice prefix="eso" value={form.fulfilment_mode}
                onChange={(k) => setForm(f => ({ ...f, fulfilment_mode: k }))} />
            )}
          </div>

          <div className="grid grid-cols-3 gap-3">
            <F label="ESO Date"><input type="date" data-testid="eso-order-date" value={form.order_date} onChange={e => setForm({ ...form, order_date: e.target.value })} className={inp} /></F>
            <F label="Delivery Date" style={{ maxWidth: "220px" }}>
              <div className="flex items-center gap-1">
                <input type="date" data-testid="eso-delivery-date" value={form.delivery_date} onChange={e => setForm({ ...form, delivery_date: e.target.value })} className={inp + " flex-1"} title="Optional · when set, the traffic-light risk uses this instead of order_date + 60d" />
                <button
                  type="button"
                  data-testid="eso-bulk-line-date-btn"
                  disabled={!form.delivery_date || !(form.items || []).length}
                  onClick={() => {
                    // Feb 2026 · Bulk Line Date · force every ESO line's
                    // Required Delivery to the header value. Handy when
                    // the buyer converges on one date late in the deal.
                    const target = form.delivery_date;
                    const items = form.items || [];
                    if (!target) { toast.error("Set an ESO Delivery Date first"); return; }
                    if (!items.length) { toast.error("Add at least one line item"); return; }
                    setForm({ ...form, items: items.map(li => ({ ...li, delivery_date: target })) });
                    toast.success(`Applied ${target} to ${items.length} line${items.length === 1 ? "" : "s"}`);
                  }}
                  title="Force every line's Required Delivery Date to the header value"
                  className="h-9 px-2 text-[10px] font-semibold uppercase tracking-widest border border-[var(--border)] bg-[var(--bg-2)]/60 text-zinc-300 hover:text-white hover:border-[var(--accent)]/60 hover:bg-[var(--accent)]/10 rounded-sm transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Apply&nbsp;All
                </button>
              </div>
            </F>
            <F label="Status">
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger className={selCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  {STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="FX Rate → INR">
              <div className="flex items-center gap-1">
                <FormattedNumberInput step="0.01" value={form.fx_rate_to_inr} onChange={e => setForm({ ...form, fx_rate_to_inr: parseFloat(e.target.value) || 0 })} className={inp} data-nouppercase />
                <button
                  type="button"
                  data-testid="eso-fx-refresh"
                  onClick={async () => {
                    try {
                      const { data } = await api.get("/fx-rates/current", { params: { currency: form.currency || "USD" } });
                      if (data?.rate_to_inr) {
                        setForm(f => ({ ...f, fx_rate_to_inr: Number(data.rate_to_inr) }));
                        toast.success(`Fetched ${data.currency} @ ₹${data.rate_to_inr}`);
                      } else {
                        toast.info("No FX rate stamped for this currency yet");
                      }
                    } catch (_e) { toast.error("FX Master lookup failed"); }
                  }}
                  title="Refresh from FX Rate Master"
                  className="h-9 px-2 border border-[var(--border)] hover:border-[var(--accent)] text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white rounded-sm whitespace-nowrap"
                >
                  Sync
                </button>
              </div>
            </F>

            {form.order_against !== "stock" && (<>
            <F label="Buyer (Customer Master) *" className="col-span-2">
              <SearchablePicker
                testid="eso-buyer"
                value={form.buyer_customer_id || ""}
                onChange={(v) => {
                  const c = customers.find(x => x.id === v);
                  // Feb 2026 · Prefer the customer's primary Bill To address for
                  // the printed Buyer Address. Falls back to primary Ship, then
                  // first address, so legacy customers without a Bill flag
                  // still yield sensible auto-fill.
                  const addrs = Array.isArray(c?.addresses) ? c.addresses : [];
                  const primary = addrs.find(a => a.is_primary_bill) || addrs.find(a => a.is_primary_ship) || addrs[0];
                  const lines = primary ? [primary.address, [primary.city, primary.state, primary.pincode].filter(Boolean).join(", "), primary.country].filter(Boolean).join(", ") : "";
                  setForm(f => ({
                    ...f,
                    buyer_customer_id: v,
                    buyer_name: c?.name || f.buyer_name,
                    buyer_country: (primary?.country) || c?.country || f.buyer_country,
                    buyer_address: lines || f.buyer_address,
                  }));
                }}
                placeholder="Pick a Buyer from Customer Master…"
                headerHint="Foreign buyers only · search by name or country"
                options={customers.filter(c => (c.country || "").trim().toUpperCase() !== "INDIA").map(c => ({
                  value: c.id,
                  code: c.code || c.name.slice(0, 6).toUpperCase(),
                  name: c.name,
                  meta: c.country || "",
                }))}
              />
            </F>
            <F label="Buyer Country"><input value={form.buyer_country} onChange={e => setForm({ ...form, buyer_country: e.target.value })} className={inp} /></F>
            <F label="Buyer Name (as printed)" className="col-span-2"><input data-testid="eso-buyer-name" data-nouppercase value={form.buyer_name} onChange={e => setForm({ ...form, buyer_name: e.target.value })} className={`${inp} ${fieldErrors.buyer_name ? "border-red-500 ring-1 ring-red-500/50" : ""}`} /></F>
            <F label="Buyer Address" className="col-span-3"><input data-nouppercase value={form.buyer_address} onChange={e => setForm({ ...form, buyer_address: e.target.value })} className={inp} /></F>

            <div className="col-span-3 flex items-end gap-3 flex-wrap">
              <F label="Buyer PO No" className="shrink-0" style={{ width: "220px" }}><input data-testid="eso-buyer-po-no" value={form.buyer_po_no} onChange={e => setForm({ ...form, buyer_po_no: e.target.value })} className={`${inp} ${!(form.buyer_po_no || "").trim() ? "border-amber-500/60 ring-1 ring-amber-500/30" : ""}`} title={!(form.buyer_po_no || "").trim() ? "PO reference missing — you can still save and add it later" : ""} /></F>
              <F label="Buyer PO Date" className="shrink-0" style={{ width: "220px" }}><input type="date" data-testid="eso-buyer-po-date" value={form.buyer_po_date} onChange={e => setForm({ ...form, buyer_po_date: e.target.value })} className={inp} /></F>
              <F label="Proforma Invoice No" className="shrink-0" style={{ width: "220px" }}><input data-testid="eso-pi-no" value={form.pi_no} onChange={e => setForm({ ...form, pi_no: e.target.value })} className={`${inp} ${!(form.pi_no || "").trim() ? "border-amber-500/60 ring-1 ring-amber-500/30" : ""}`} title={!(form.pi_no || "").trim() ? "PI reference missing — you can still save and add it later" : ""} /></F>
              <F label="Proforma Invoice Date" className="shrink-0" style={{ width: "220px" }}><input type="date" data-testid="eso-pi-date" value={form.pi_date} onChange={e => setForm({ ...form, pi_date: e.target.value })} className={inp} /></F>
            </div>

            <F label="Consignee (Customer Master)" className="col-span-2">
              <SearchablePicker
                testid="eso-consignee"
                value={form.consignee_customer_id || ""}
                onChange={(v) => {
                  const c = customers.find(x => x.id === v);
                  // Feb 2026 · Prefer primary Ship To for the consignee auto-
                  // fill (falls back to primary Bill / first address).
                  const addrs = Array.isArray(c?.addresses) ? c.addresses : [];
                  const primary = addrs.find(a => a.is_primary_ship) || addrs.find(a => a.is_primary_bill) || addrs[0];
                  const lines = primary ? [primary.address, [primary.city, primary.state, primary.pincode].filter(Boolean).join(", "), primary.country].filter(Boolean).join(", ") : "";
                  setForm(f => ({
                    ...f,
                    consignee_customer_id: v,
                    consignee_name: c?.name || f.consignee_name,
                    consignee_address: lines || f.consignee_address,
                  }));
                }}
                placeholder="Pick Consignee (often same as Buyer)…"
                headerHint="Foreign customers only · search by name or country"
                options={customers.filter(c => (c.country || "").trim().toUpperCase() !== "INDIA").map(c => ({
                  value: c.id,
                  code: c.code || c.name.slice(0, 6).toUpperCase(),
                  name: c.name,
                  meta: c.country || "",
                }))}
              />
            </F>
            <F label="Final Destination"><input value={form.country_of_final_destination} onChange={e => setForm({ ...form, country_of_final_destination: e.target.value })} className={inp} /></F>
            <F label="Consignee Name (override)"><input data-nouppercase value={form.consignee_name} onChange={e => setForm({ ...form, consignee_name: e.target.value })} className={inp} /></F>
            <F label="Consignee Address" className="col-span-2"><input data-nouppercase value={form.consignee_address} onChange={e => setForm({ ...form, consignee_address: e.target.value })} className={inp} /></F>
            </>)}

            <F label="Incoterms">
              <Select value={form.incoterms} onValueChange={v => setForm({ ...form, incoterms: v })}>
                <SelectTrigger className={selCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  {INCOTERMS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Port of Loading"><input value={form.port_of_loading} onChange={e => setForm({ ...form, port_of_loading: e.target.value })} className={inp} /></F>
            <F label="Port of Discharge"><input value={form.port_of_discharge} onChange={e => setForm({ ...form, port_of_discharge: e.target.value })} className={inp} /></F>

            <F label="Payment Terms">
              <Select value={form.payment_terms} onValueChange={v => setForm({ ...form, payment_terms: v })}>
                <SelectTrigger className={selCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  {PAY_TERMS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="LC No"><input value={form.lc_no} onChange={e => setForm({ ...form, lc_no: e.target.value })} className={inp} /></F>
            <F label="LC Date"><input type="date" value={form.lc_date} onChange={e => setForm({ ...form, lc_date: e.target.value })} className={inp} /></F>
          </div>

          <div className="mt-3">
            <div className="flex justify-between items-center mb-2">
              <div className="overline">Line Items · USD</div>
              <button onClick={addItem} className="text-xs h-7 px-2 rounded-sm border border-[var(--border)] text-zinc-400 hover:text-white flex items-center gap-1"><PlusIcon size={11} /> Add Line</button>
            </div>
            <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[var(--border)] text-zinc-500">
                    <th className="text-left px-2 py-2 min-w-[220px]">Article</th>
                    <th className="text-left px-2 py-2 min-w-[140px]">Style No</th>
                    <th className="text-left px-2 py-2 min-w-[100px]">HS Code</th>
                    <th className="text-right px-2 py-2 min-w-[110px] bg-pink-500/10">Order Qty</th>
                    <th className="text-right px-2 py-2 min-w-[100px]">Extra Allow %</th>
                    <th className="text-right px-2 py-2 min-w-[100px]" title="Over-Dispatch Buffer · technical over-production tolerance">Buffer Qty</th>
                    <th className="text-right px-2 py-2 min-w-[110px] bg-emerald-500/10">Total Qty</th>
                    <th className="text-left px-2 py-2 min-w-[60px]">UOM</th>
                    <th className="text-left px-2 py-2 min-w-[140px] bg-amber-500/10">Req Delivery Date</th>
                    <th className="text-right px-2 py-2 min-w-[100px]">Rate USD</th>
                    <th className="text-right px-2 py-2 min-w-[110px]">Amount USD</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {(form.items || []).map((it, i) => {
                    const artId = articles.find(a => a.product_code === it.article_no)?.id || "";
                    return (
                    <tr key={i} className="border-b border-[var(--border)]">
                      <td className="p-1" style={{ minWidth: "240px" }}>
                        <ArticleCombobox
                          testid={`eso-line-article-${i}`}
                          articles={articles}
                          value={artId}
                          onChange={(id) => {
                            const a = articles.find(x => x.id === id);
                            if (!a) return;
                            // Feb 2026 · Priority is now Article-Master `hs_code` first
                            // (auto-inherited from Article Type via the composer), then
                            // Product Master's `hs_code`, then whatever was already on
                            // the row. Picking a new article overrides stale values so
                            // the invoice footer is accurate on the first save.
                            const masterHs = (Array.isArray(itemsMaster) ? itemsMaster : []).find(m => (m.description || "") === (a.product_code || ""))?.hs_code || "";
                            const nextHs = (a.hs_code || "").trim() || masterHs || "";
                            setForm(f => ({
                              ...f,
                              items: f.items.map((row, x) => x === i ? {
                                ...row,
                                article_no: a.product_code || "",
                                style_no: row.style_no || a.style_no || "",
                                hs_code: nextHs || row.hs_code || "",
                              } : row),
                            }));
                          }}
                          onCreated={(a) => setArticles(prev => [...prev, a])}
                        />
                      </td>
                      <td className="p-1"><input data-testid={`eso-line-style-${i}`} value={it.style_no || ""} onChange={e => setItem(i, "style_no", e.target.value)} maxLength={20} className={inp + " h-8 font-mono"} /></td>
                      <td className="p-1"><input value={it.hs_code} onChange={e => setItem(i, "hs_code", e.target.value)} className={inp + " h-8 font-mono"} /></td>
                      <td className="p-1"><FormattedNumberInput data-testid={`eso-line-qty-${i}`} data-nouppercase max="9999999" step="1" value={it.qty} onChange={e => setItem(i, "qty", Math.min(9999999, parseFloat(e.target.value) || 0))} className={inp + " h-8 text-right font-mono tabular-nums"} title="Max 7 digits · 0 – 9,999,999" /></td>
                      <td className="p-1"><FormattedNumberInput data-testid={`eso-line-extra-pct-${i}`} data-nouppercase step="0.01" value={it.extra_allowance_pct || 0} onChange={e => setItem(i, "extra_allowance_pct", parseFloat(e.target.value) || 0)} className={inp + " h-8 text-right font-mono tabular-nums"} title="Per-line Extra Allowance % — bump for high-risk products" /></td>
                      <td className="p-1"><FormattedNumberInput data-testid={`eso-line-extra-qty-${i}`} data-nouppercase step="0.01" value={it.extra_qty || 0} onChange={e => setItem(i, "extra_qty", parseFloat(e.target.value) || 0)} className={inp + " h-8 text-right font-mono tabular-nums"} title="Direct edit overrides the % — sticks even when % changes" /></td>
                      <td className="p-1 text-right font-mono text-emerald-300 bg-emerald-500/5" data-testid={`eso-line-total-qty-${i}`}>{fmtQtyInt(it.total_qty)}</td>
                      <td className="p-1"><input value={it.uom} onChange={e => setItem(i, "uom", e.target.value)} className={inp + " h-8"} /></td>
                      <td className="p-1 bg-amber-500/5"><input type="date" data-testid={`eso-line-delivery-${i}`} data-nouppercase value={it.delivery_date || ""} onChange={e => setItem(i, "delivery_date", e.target.value)} title="Item-wise required delivery date · benchmark for at-risk chip + WO deadline. Falls back to order-level Delivery Date when blank." className={inp + " h-8 font-mono " + (!it.delivery_date && !form.delivery_date ? "border-amber-500/60" : "")} /></td>
                      <td className="p-1"><FormattedNumberInput data-nouppercase step="0.0001" value={it.unit_price_usd} onChange={e => setItem(i, "unit_price_usd", parseFloat(e.target.value) || 0)} className={inp + " h-8 text-right font-mono"} /></td>
                      <td className="p-1 text-right font-mono text-white">{Number(it.amount_usd || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="p-1"><button onClick={() => rmItem(i)} className="text-zinc-500 hover:text-red-400"><TrashIcon size={12} /></button></td>
                    </tr>
                    );
                  })}
                  {(form.items || []).length === 0 && <tr><td colSpan={12} className="py-6 text-center text-zinc-500">No line items — click "+ Add Line"</td></tr>}
                </tbody>
                {(form.items || []).length > 0 && (
                  <tfoot>
                    <tr className="bg-[var(--bg-2)] font-medium border-t border-[var(--border)]">
                      <td colSpan={3} className="px-2 py-2 text-right text-[10px] uppercase tracking-widest text-zinc-500">Totals · Total Qty = Σ(Order + Extra)</td>
                      <td className="px-2 py-2 text-right font-mono text-white bg-pink-500/10" data-testid="eso-total-ordered">{qtyTotals.ordered.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td></td>
                      <td className="px-2 py-2 text-right font-mono text-white" data-testid="eso-total-extra">{qtyTotals.extra.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="px-2 py-2 text-right font-mono text-emerald-300 bg-emerald-500/10" data-testid="eso-total-production">{qtyTotals.total.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td colSpan={3} className="px-2 py-2 text-right font-mono text-zinc-400">Amount USD</td>
                      <td className="px-2 py-2 text-right font-mono text-white">{sub.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-3">
            <F label="Insurance USD"><FormattedNumberInput data-nouppercase step="0.01" value={form.insurance_usd} onChange={e => setForm({ ...form, insurance_usd: parseFloat(e.target.value) || 0 })} className={inp} /></F>
            <F label="Other Charges USD"><FormattedNumberInput data-nouppercase step="0.01" value={form.other_charges_usd} onChange={e => setForm({ ...form, other_charges_usd: parseFloat(e.target.value) || 0 })} className={inp} /></F>
            <F label="Grand Total">
              <div className="h-9 px-3 flex items-center bg-[var(--bg-2)] rounded-sm font-mono font-semibold text-emerald-400" data-testid="eso-total">${total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
            </F>
          </div>

          <F label="Remarks" className="mt-3"><input data-nouppercase value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} className={inp} /></F>

          <DialogFooter className="gap-2">
            <button data-testid="eso-cancel" onClick={() => setOpen(false)} className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs font-mono uppercase tracking-widest rounded-sm">Cancel</button>
            <button data-testid="save-eso" disabled={saving} onClick={() => save(false)} className="disabled:opacity-50 disabled:cursor-not-allowed h-9 px-4 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)]/10 text-xs font-mono uppercase tracking-widest rounded-sm">{editing ? "Update ESO" : "Save as Draft"}</button>
            {!editing && canSubmitEso && (
              <button data-testid="eso-submit-authorisation" disabled={saving} onClick={() => save(true)} className="disabled:opacity-50 disabled:cursor-not-allowed h-9 px-4 bg-[var(--accent)] hover:bg-blue-600 text-white text-xs font-mono uppercase tracking-widest rounded-sm">Submit for Authorisation</button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link picker */}
      <LinkPicker
        linking={linking}
        onClose={() => setLinking(null)}
        onSubmit={submitLink}
        plans={plans}
        slips={slips}
      />

      {/* Read-only View + Authorisation & Amendment workflow (Feb 2026) */}
      <EsoViewDialog
        eso={viewing}
        open={!!viewing}
        onClose={() => { setViewing(null); setViewIntent(null); }}
        canAuth={canAuth}
        canEdit={canEdit}
        onPrint={esoPrintHtml}
        onChanged={load}
        initialAction={viewIntent}
        onHold={(mode) => viewing && setHoldAction({ id: viewing.id, mode })}
        onAdminAmend={(id) => setAdminAmendId(id)}
      />
      <AdminAmendDialog open={!!adminAmendId} orderType="ESO" orderId={adminAmendId} onClose={() => setAdminAmendId(null)} onSaved={() => load()} />
      <HoldReasonDialog open={!!holdAction} onClose={() => setHoldAction(null)}
        title={holdAction?.mode === "release" ? "Release Hold" : "Put Export Order on Hold"}
        actionLabel={holdAction?.mode === "release" ? "Release Hold" : "Put on Hold"}
        endpoint={holdAction ? `/exports/orders/${holdAction.id}/${holdAction.mode === "release" ? "release-hold" : "hold"}` : ""}
        onDone={() => load()} />
    </div>
  );
}

function LinkPicker({ linking, onClose, onSubmit, plans, slips }) {
  const [selected, setSelected] = useState({});
  useEffect(() => { setSelected({}); }, [linking?.row?.id, linking?.type]);
  if (!linking) return null;
  const items = linking.type === "plans" ? plans : slips;
  const toggle = (id) => setSelected(s => ({ ...s, [id]: !s[id] }));
  const submit = () => onSubmit(Object.keys(selected).filter(k => selected[k]));
  return (
    <Dialog open={!!linking} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-2xl max-h-[80vh] overflow-y-auto" description="Export document action — Proforma / Packing List / Sales Order / Delivery Challan.">
        <DialogHeader><DialogTitle className="font-display">Link {linking.type === "plans" ? "Production Plans" : "Packing Slips"} · {linking.row.doc_no}</DialogTitle></DialogHeader>
        <div className="space-y-1 max-h-96 overflow-y-auto">
          {items.map(it => (
            <label key={it.id} className="flex items-center gap-3 p-2 rounded-sm hover:bg-[var(--bg-2)] cursor-pointer text-sm">
              <input type="checkbox" checked={!!selected[it.id]} onChange={() => toggle(it.id)} className="accent-[var(--accent)]" />
              <div className="flex-1 font-mono text-xs">
                {linking.type === "plans"
                  ? `${it.id.slice(0, 8)}… · order ${(it.order_id || "").slice(0, 8)}… · ${it.planned_qty || 0}m · ${it.status || ""}`
                  : `${it.slip_no || it.id.slice(0, 8)} · ${(it.pack_date || "")} · ${(it.boxes || []).length} box(es)`}
              </div>
            </label>
          ))}
          {items.length === 0 && <div className="py-10 text-center text-zinc-500 text-sm">No records available</div>}
        </div>
        <DialogFooter>
          <button onClick={onClose} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
          <button data-testid="link-submit" onClick={submit} className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-sm font-semibold text-white">Link Selected</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
//  Export Packing Lists
// ============================================================================
function PackingTab({ perms }) {
  const [rows, setRows] = useState([]);
  const [orders, setOrders] = useState([]);
  const [slips, setSlips] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // Mar 2026 · Finance-to-Logistics · click-through from the EPL grid's
  // Commercial Invoice cell into the Export Commercial Invoice tab.
  const [, setEplSearchParams] = useSearchParams();
  const openInvoiceTab = (invNo) => {
    const next = new URLSearchParams();
    next.set("tab", "invoices");
    if (invNo) next.set("q", invNo);
    setEplSearchParams(next, { replace: false });
  };
  // Source mode (Feb 2026) — mirror SO Packing List: either type boxes manually
  // or cherry-pick existing Export-linked Packing Slips whose boxes auto-flow
  // into the EPL. Both modes write to the same `form.boxes` array.
  const [sourceMode, setSourceMode] = useState("manual"); // "manual" | "slips"
  // Mar 2, 2026 · Dispatch Source toggle — Path A (Packing Floor) vs
  // Path B (FG-STORE). Backend stamps `source_godown_code` on save.
  const [dispatchSource, setDispatchSource] = useState("PACKING-FLOOR");
  const [selectedSlipIds, setSelectedSlipIds] = useState(new Set());
  const [productFilter, setProductFilter] = useState(new Set()); // subset of product codes to narrow the slip list
  // Feb 2026 · read-only View dialog for the row-level Actions column
  const [viewing, setViewing] = useState(null);
  const eplStyles = useDocStyles(viewing?.id ? "EPL" : null, viewing?.id);
  // Feb 2026 · Product Master exposes `standard_picks_per_cm`; join with slips
  // to expose the product-code chip UX identical to SO Packing List.
  const empty = {
    epl_date: new Date().toISOString().slice(0, 10),
    export_order_id: "", export_order_no: "",
    container_no: "", seal_no: "", container_type: "20FT",
    shipping_marks: "", package_type: "CARTON",
    boxes: [], remarks: "",
    linked_internal_ps_ids: [],
  };
  const [form, setForm] = useState(empty);

  // Feb 2026 · pull Product Master so HS codes can be auto-filled per box
  // when the source packing slip doesn't carry one.
  const [itemsMaster, setItemsMaster] = useState([]);
  const load = async () => {
    const [e, o, ps, im] = await Promise.all([
      api.get("/exports/packing-lists/"),
      api.get("/exports/orders/"),
      api.get("/packing-slips/"),
      api.get("/items/"),
    ]);
    setRows(e.data); setOrders(o.data); setSlips(ps.data || []); setItemsMaster(im.data || []);
  };
  const hsCodeFor = (articleNo) => {
    if (!articleNo) return "";
    const m = (itemsMaster || []).find(x => (x.description || "") === articleNo || (x.code || "") === articleNo);
    return (m && m.hs_code) || "";
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => {
    // Feb 2026 · EPL doc_no auto-assigned on save (SO-style).
    setForm({ ...empty, doc_no: "" });
    setEditing(null); setSourceMode("manual"); setDispatchSource("PACKING-FLOOR"); setSelectedSlipIds(new Set()); setOpen(true);
  };
  const openEdit = (r) => {
    setForm({ ...r, linked_internal_ps_ids: r.linked_internal_ps_ids || [] });
    setEditing(r.id);
    setSourceMode((r.linked_internal_ps_ids || []).length > 0 ? "slips" : "manual");
    setDispatchSource((r.dispatch_source || "PACKING-FLOOR").toUpperCase());
    setSelectedSlipIds(new Set(r.linked_internal_ps_ids || []));
    setOpen(true);
  };
  const addBox = () => setForm(f => ({ ...f, boxes: [...(f.boxes || []), { box_no: "", article_no: "", style_no: "", hs_code: "", qty: 0, uom: "MTR", joints: 0, net_weight_kg: 0, tare_weight_kg: 0, gross_weight_kg: 0, dimensions: "" }] }));
  const rmBox = (i) => setForm(f => ({ ...f, boxes: f.boxes.filter((_, x) => x !== i) }));
  // Feb 2026 · when the article changes on a manually-entered box, auto-fill
  // HS Code from Product Master (planner can still override in the input).
  const setBox = (i, k, v) => setForm(f => ({
    ...f,
    boxes: f.boxes.map((b, x) => {
      if (x !== i) return b;
      const next = { ...b, [k]: v };
      if (k === "article_no" && !b.hs_code) {
        const hs = hsCodeFor(v);
        if (hs) next.hs_code = hs;
      }
      return next;
    }),
  }));

  const totals = useMemo(() => {
    const b = form.boxes || [];
    return {
      count: b.length,
      qty: b.reduce((a, x) => a + Number(x.qty || 0), 0),
      net: b.reduce((a, x) => a + Number(x.net_weight_kg || 0), 0),
      gross: b.reduce((a, x) => a + Number(x.gross_weight_kg || 0), 0),
    };
  }, [form.boxes]);

  const onEsoPick = (eid) => {
    const o = orders.find(x => x.id === eid);
    setForm(f => ({ ...f, export_order_id: eid, export_order_no: o?.doc_no || "", boxes: [], linked_internal_ps_ids: [] }));
    setSelectedSlipIds(new Set());
  };

  // Packing Slips linked to the picked ESO (source_type == "export_order").
  // Filtered live so the checklist only surfaces slips relevant to this EPL.
  // Feb 2026 · duplicate-lock — a slip that has already been consumed by
  // ANOTHER EPL is filtered out so the same PS number can't be pulled into
  // two Export Packing Lists (mirrors the SO Packing List `slipUsedInPl`
  // guard). The currently-edited EPL's own links are exempt so an admin can
  // still open, re-arrange, and save.
  const slipUsedInEpl = useMemo(() => {
    const m = {};
    rows.forEach(r => {
      if (editing && r.id === editing) return;
      (r.linked_internal_ps_ids || []).forEach(sid => { if (sid) m[sid] = r.doc_no || r.id; });
    });
    return m;
  }, [rows, editing]);

  // Mar 2026 · Helper — resolves an article description from the Product
  // Master so the EPS picker rows carry a human-readable label alongside
  // the raw product code.
  const descFor = (productCode) => {
    if (!productCode) return "";
    const m = (itemsMaster || []).find(x =>
      (x.code || "") === productCode
      || (x.article_no || "") === productCode
      || (x.description || "") === productCode
    );
    return (m && (m.description || m.article_description)) || "";
  };

  const availableSlips = useMemo(() => {
    if (!form.export_order_id) return [];
    // V28-007B · Export eligibility is decided by authoritative structured
    // lineage, NOT the slip-number prefix. A slip qualifies for this ESO's
    // EPL when it is genuinely an Export slip (source_type "export_order" OR
    // it carries an export_order_id) AND its export_order_id matches the
    // picked ESO id. This admits BOTH current EPS-* slips and legacy Export
    // PS-* slips (e.g. PS-26-27-3617..3851) while still EXCLUDING Domestic PS
    // (which have no export_order_id and source_type "sales_order"). Boxes
    // already consumed by another EPL stay hidden via slipUsedInEpl. The old
    // `slip_no.startsWith("EPS-")` gate was removed per Owner clarification
    // because PS-/EPS- prefix is NOT the authority for Export-vs-Domestic.
    return slips.filter(s =>
      (s.source_type === "export_order" || s.export_order_id) &&
      s.export_order_id === form.export_order_id &&
      !slipUsedInEpl[s.id]
    );
  }, [slips, form.export_order_id, slipUsedInEpl]);

  // Count of slips that WOULD have shown for this ESO but are already
  // consumed by another EPL — surfaced as a hint so planners don't wonder
  // why a slip they know exists isn't in the list.
  const usedElsewhereCount = useMemo(() => {
    if (!form.export_order_id) return 0;
    return slips.filter(s =>
      (s.source_type === "export_order" || s.export_order_id) &&
      s.export_order_id === form.export_order_id &&
      slipUsedInEpl[s.id]
    ).length;
  }, [slips, form.export_order_id, slipUsedInEpl]);

  // Product codes available on this ESO's slips (drives the chip filter row).
  const esoProductCodes = useMemo(() => {
    const set = new Set();
    availableSlips.forEach(s => (s.boxes || []).forEach(b => { if (b.product_code) set.add(b.product_code); }));
    return Array.from(set).sort();
  }, [availableSlips]);

  // Feb 28, 2026 · Pack-First lock — an excess slip is blocked ONLY when
  // its linked excess_production entry isn't yet 'Approved for Billing'.
  const [excessEntries, setExcessEntries] = useState([]);
  useEffect(() => {
    api.get("/excess-production/", { params: {} })
      .then(({ data }) => setExcessEntries(data || []))
      .catch(() => setExcessEntries([]));
  }, [open]);
  const excessStatusByPsId = useMemo(() => {
    const m = {};
    (excessEntries || []).forEach(e => {
      const pid = e.packing_slip_id;
      if (!pid) return;
      if (!m[pid]) m[pid] = { all_approved: true };
      if (e.status !== "Approved for Billing") m[pid].all_approved = false;
    });
    return m;
  }, [excessEntries]);
  const excessSelectedSlips = useMemo(() => {
    if (selectedSlipIds.size === 0) return [];
    return availableSlips.filter(s => selectedSlipIds.has(s.id) && (
      (s.source_type || "") === "excess_so" ||
      (s.source_type || "") === "excess_eso" ||
      s.is_excess_dispatch === true
    ));
  }, [availableSlips, selectedSlipIds]);
  const unauthorizedExcessSlips = useMemo(() =>
    excessSelectedSlips.filter(s => !(excessStatusByPsId[s.id] && excessStatusByPsId[s.id].all_approved)),
    [excessSelectedSlips, excessStatusByPsId]);
  const hasExcessSelected = unauthorizedExcessSlips.length > 0;
  const EXCESS_BLOCK_MSG = "This Packing List contains excess production. Excess quantities must be moved to General Stock or authorized via an Order Amendment before they can be included in a formal Packing List.";

  const toggleProductCode = (pc) => {
    setProductFilter(prev => {
      const next = new Set(prev);
      next.has(pc) ? next.delete(pc) : next.add(pc);
      return next;
    });
  };
  const clearProductCodes = () => setProductFilter(new Set());

  // Box-number continuation: sum boxes already recorded on OTHER EPLs for
  // this ESO (excludes the EPL being edited) so BOX-numbering stays
  // globally sequential per Export Order — mirrors the SO PL boxStartIdx.
  const eplBoxStartIdx = useMemo(() => {
    if (!form.export_order_id) return 1;
    let used = 0;
    rows.forEach(r => {
      if (editing && r.id === editing) return;
      if (r.export_order_id !== form.export_order_id) return;
      used += (r.boxes || []).length;
    });
    return used + 1;
  }, [rows, editing, form.export_order_id]);

  // Slips further narrowed by the Product Code chips. When no filter is
  // active (empty Set) we return everything.
  const filteredSlips = useMemo(() => {
    if (productFilter.size === 0) return availableSlips;
    return availableSlips.filter(s => (s.boxes || []).some(b => productFilter.has(b.product_code)));
  }, [availableSlips, productFilter]);

  // Summary of the currently ticked slips (drives the "Selected Totals" row).
  const selectedTotals = useMemo(() => {
    const picked = availableSlips.filter(s => selectedSlipIds.has(s.id));
    const acc = { boxes: 0, meters: 0, net: 0, gross: 0 };
    picked.forEach(ps => (ps.boxes || []).forEach(b => {
      acc.boxes += 1;
      acc.meters += Number(b.meters || 0);
      acc.net += Number(b.net_weight || 0);
      acc.gross += Number(b.gross_weight || 0);
    }));
    return acc;
  }, [availableSlips, selectedSlipIds]);

  // Re-derive form.boxes from the ticked Packing Slips whenever the selection
  // changes. Each PS box becomes one EPL box; qty comes from `meters`, weights
  // from net/gross. Article/style/hs propagate straight through. Feb 2026 ·
  // box_no is now sequentially numbered starting at eplBoxStartIdx so the
  // preview mirrors the SO PL "BOX-0002 → BOX-0005" chip.
  const rebuildBoxesFromSlips = (ids) => {
    const picked = availableSlips.filter(s => ids.has(s.id));
    const boxes = [];
    let boxIdx = eplBoxStartIdx;
    picked.forEach(ps => {
      (ps.boxes || []).forEach(b => {
        boxes.push({
          box_no: `BOX-${String(boxIdx).padStart(4, "0")}`,
          article_no: b.product_code || "",
          style_no: b.style_no || "",
          // Feb 2026 · fall back to Product Master when the slip box's
          // hs_code is blank — ensures EPL prints/exports always carry it.
          hs_code: b.hs_code || hsCodeFor(b.product_code) || "",
          qty: Number(b.meters || 0),
          uom: "MTR",
          joints: Number(b.joints || 0),
          net_weight_kg: Number(b.net_weight || 0),
          tare_weight_kg: Number(b.tare_weight || 0),
          gross_weight_kg: Number(b.gross_weight || 0),
          dimensions: b.dimensions || "",
          source_slip_no: ps.slip_no || "",
          source_slip_box_no: b.box_no || "",
        });
        boxIdx++;
      });
    });
    setForm(f => ({ ...f, boxes, linked_internal_ps_ids: Array.from(ids) }));
  };
  const toggleSlipSel = (id) => {
    setSelectedSlipIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      rebuildBoxesFromSlips(next);
      return next;
    });
  };
  const selectAllSlips = () => {
    const next = new Set(availableSlips.map(s => s.id));
    setSelectedSlipIds(next);
    rebuildBoxesFromSlips(next);
  };
  const clearAllSlips = () => {
    const next = new Set();
    setSelectedSlipIds(next);
    rebuildBoxesFromSlips(next);
  };
  const save = async () => {
    if (!form.export_order_id) { toast.error("Pick an Export SO"); return; }
    if (!(form.boxes || []).length) { toast.error("Add at least one box"); return; }
    const payload = { ...form, dispatch_source: dispatchSource };
    try {
      if (editing) await api.put(`/exports/packing-lists/${editing}`, payload);
      else await api.post("/exports/packing-lists/", payload);
      toast.success(editing ? "EPL updated" : "EPL created");
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => {
    if (!confirm("Delete this EPL?")) return;
    try { await api.delete(`/exports/packing-lists/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  // Mar 2026 · EPL Print/PDF now uses the SAME client-side `printDocument`
  // renderer as the Sales-Module Packing List (OrderExtras.jsx `printPl`).
  // Column order, meta strip, totals row and column widths mirror the sales
  // PL so the shop floor sees one consistent print format for both flows.
  const printPdf = async (id) => {
    try {
      const { data: r } = await api.get(`/exports/packing-lists/${id}`);
      const eso = orders.find(o => o.id === r.export_order_id);
      const enrichedBoxes = (r.boxes || []).map(b => {
        const net = Number(b.net_weight_kg || 0);
        const gross = Number(b.gross_weight_kg || 0);
        return {
          ...b,
          // Iter39 fix · align field names with the EPL box schema so the
          // Sales-PL-style print no longer prints "—" / 0.000 in Slip No
          // and Tare Wt columns.
          slip_no: b.slip_no || b.source_slip_no || "",
          slip_box_no: b.slip_box_no || b.source_box_no || "",
          tare_weight_kg: (b.tare_weight_kg != null && b.tare_weight_kg !== "") ? Number(b.tare_weight_kg) : Math.max(0, gross - net),
          style_no: b.style_no || "",
          hs_code: b.hs_code || hsCodeFor(b.product_code || b.article_no) || "",
        };
      });
      const totalJoints = enrichedBoxes.reduce((a, b) => a + (Number(b.joints) || 0), 0);
      // Totals are derived from the actual box rows so the header + footer
      // always reconcile with the printed detail. EPL record aggregates
      // (r.total_*) can be 0/empty on some records, which previously made
      // Total Boxes / Net Wt / Gross Wt print as "—" / 0.000.
      const sumBoxes = (k) => enrichedBoxes.reduce((a, b) => a + Number(b[k] || 0), 0);
      const hasBoxes = enrichedBoxes.length > 0;
      const totalQty     = sumBoxes("qty");
      const totalNetWt   = hasBoxes ? sumBoxes("net_weight_kg")   : Number(r.total_net_weight_kg || 0);
      const totalGrossWt = hasBoxes ? sumBoxes("gross_weight_kg") : Number(r.total_gross_weight_kg || 0);
      const totalTareWt  = hasBoxes ? sumBoxes("tare_weight_kg")  : Math.max(0, totalGrossWt - totalNetWt);
      const totalBoxesCt = (r.total_boxes != null && r.total_boxes !== "") ? r.total_boxes : enrichedBoxes.length;
      const fmtNum = (v, dp = 2) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: dp, maximumFractionDigits: dp });
      printSignedDocument("EPL", r.id, {
        kind: "Export Packing List",
        no: r.doc_no,
        date: r.epl_date,
        meta: [
          { label: "Buyer Name",       value: eso?.buyer_name || "—" },
          { label: "Buyer PO",         value: eso?.buyer_po_no || "—" },
          { label: "Export SO",        value: r.export_order_no || eso?.doc_no || "—" },
          // Mar 2026 · Finance-to-Logistics Sync — surface the linked
          // Export Commercial Invoice on the printed EPL so buyers &
          // freight-forwarders can cross-reference immediately.
          { label: "Commercial Invoice",
            value: (r.export_commercial_invoice_no
                    || (r.linked_invoice_kind === "commercial" ? r.linked_invoice_no : ""))
                   || "INV PENDING" },
          { label: "Total Boxes",      value: totalBoxesCt },
          { label: "Quantity",         value: `${fmtNum(totalQty, 2)} ${enrichedBoxes[0]?.uom || ""}` },
          { label: "Net Weight (kg)",  value: fmtNum(totalNetWt, 2) },
          { label: "Gross Weight (kg)", value: fmtNum(totalGrossWt, 2) },
        ],
        sections: [{
          heading: `Box-wise Packing Detail · ${enrichedBoxes.length} Boxes`,
          columns: [
            { key: "box_no",       label: "Box No",       mono: true },
            { key: "slip_no",      label: "Slip No",      mono: true, fmt: (v, row) => v ? (row.slip_box_no ? `${v} · ${row.slip_box_no}` : v) : "—" },
            { key: "product_code", label: "Product Code", mono: true, fmt: (v, row) => v || row.article_no || "—" },
            { key: "style_no",     label: "Style No",     mono: true, fmt: v => v || "—" },
            { key: "hs_code",      label: "HS Code",      mono: true, fmt: v => v || "—" },
            { key: "qty",          label: "Meter/Box",    align: "right", fmt: v => fmtNum(v, 2) },
            { key: "joints",       label: "Joints",       align: "right", fmt: v => Number(v || 0) },
            { key: "net_weight_kg",   label: "Net Wt",   align: "right", fmt: v => fmtNum(v, 3) },
            { key: "tare_weight_kg",  label: "Tare Wt",  align: "right", fmt: v => fmtNum(v, 3) },
            { key: "gross_weight_kg", label: "Gross Wt", align: "right", fmt: v => fmtNum(v, 3) },
          ],
          rows: enrichedBoxes,
          footerRow: {
            box_no: `Total ${enrichedBoxes.length} boxes`,
            qty: fmtNum(totalQty, 2),
            joints: totalJoints,
            net_weight_kg: fmtNum(totalNetWt, 3),
            tare_weight_kg: fmtNum(totalTareWt, 3),
            gross_weight_kg: fmtNum(totalGrossWt, 3),
          },
        }],
        footer: r.remarks ? `Remarks: ${r.remarks}` : "",
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to open print view"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-zinc-500">{rows.length} export packing list{rows.length !== 1 ? "s" : ""}</div>
        {perms.create && (
          <button data-testid="new-epl-btn" onClick={openCreate} className="h-9 px-4 bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold rounded-sm flex items-center gap-2">
            <PlusIcon size={14} weight="bold" /> New Packing List
          </button>
        )}
      </div>

      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="text-left px-4 py-3">EPL No</th>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Export SO</th>
              <th className="text-left px-4 py-3">Container</th>
              <th className="text-left px-4 py-3">Source Godown</th>
              <th className="text-right px-4 py-3">Boxes</th>
              <th className="text-right px-4 py-3">Total Meters</th>
              <th className="text-right px-4 py-3">Net kg</th>
              <th className="text-right px-4 py-3">Gross kg</th>
              <th className="text-left px-4 py-3">Commercial Invoice</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40" data-testid={`epl-row-${r.doc_no}`}>
                <td className="px-4 py-3 font-mono text-white">
                  <div className="flex items-center gap-1.5">
                    <span>{r.doc_no}</span>
                    {/* Mar 2026 · BILLED badge — surfaces the moment an
                        ECI stamps this EPL so operators immediately see
                        why the row can't be picked for a new invoice. */}
                    {(r.export_commercial_invoice_no
                      || (r.linked_invoice_kind === "commercial" && r.linked_invoice_no)
                      || r.linked_invoice_id) && (
                      <span
                        data-testid={`epl-billed-${r.doc_no}`}
                        title={`Billed via ${r.export_commercial_invoice_no || r.linked_invoice_no || "invoice"}`}
                        className="inline-flex items-center gap-1 px-1.5 py-[1px] rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 text-[9px] font-mono uppercase tracking-widest"
                      >
                        <span className="w-1 h-1 rounded-full bg-emerald-400" />BILLED
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 font-mono text-zinc-400">{r.epl_date}</td>
                <td className="px-4 py-3 text-right"><TwoLineDocNo value={r.export_order_no || "—"} className="text-zinc-300" /></td>
                <td className="px-4 py-3 font-mono text-zinc-400">{r.container_no || "—"} · {r.container_type}</td>
                <td className="px-4 py-3" data-testid={`epl-source-${r.doc_no}`}>
                  {r.source_godown_code === "FG-STORE" ? (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-emerald-500/10 text-emerald-400 text-[11px] font-mono uppercase tracking-widest">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />FG-STORE
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-amber-500/10 text-amber-400 text-[11px] font-mono uppercase tracking-widest">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />{r.source_godown_code || "PACK-1FLR"}
                    </span>
                  )}
                </td>
                {/* A2 · Phase 2 (Oct 2026) — EPL register Box Count.
                    Backend `_epl_totals` emits `box_count` on every row
                    (len of persisted `boxes[]`). Historical defect:
                    this cell was reading `r.total_boxes`, which the
                    backend never emitted, so the column rendered blank
                    even though the authoritative count was 1 or more.
                    Precedence: authoritative backend `box_count` first
                    (including zero), then the raw `boxes[]` fallback
                    for legacy payloads that pre-date the stamp. */}
                <td className="px-4 py-3 text-right font-mono text-white" data-testid={`epl-boxes-${r.doc_no}`}>{r.box_count != null ? r.box_count : ((r.boxes || []).length || "—")}</td>
                {/* Feb 2026 · Total Meters — sum of boxes[].qty (meters).
                    Backend `total_qty` field is authoritative; falls back
                    to a live sum for legacy EPLs that predate the field. */}
                <td className="px-4 py-3 text-right font-mono text-[var(--accent)] tabular-nums" data-testid={`epl-meters-${r.doc_no}`}>{Number(r.total_qty || (r.boxes || []).reduce((s, b) => s + Number(b.qty || 0), 0)).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td className="px-4 py-3 text-right font-mono text-white">{Number(r.total_net_weight_kg || 0).toFixed(2)}</td>
                <td className="px-4 py-3 text-right font-mono text-white">{Number(r.total_gross_weight_kg || 0).toFixed(2)}</td>
                {/* Mar 2026 · Finance-to-Logistics Sync — clickable ECI
                    number (jumps to the Export Commercial Invoice tab
                    filtered to the number) or an amber "INV PENDING"
                    pill when the EPL isn't billed yet. */}
                <td className="px-4 py-3" data-testid={`epl-ci-${r.doc_no}`}>
                  {(() => {
                    const invNo = r.export_commercial_invoice_no
                      || (r.linked_invoice_kind === "commercial" ? r.linked_invoice_no : "");
                    if (invNo) {
                      return (
                        <button
                          type="button"
                          data-testid={`epl-ci-link-${r.doc_no}`}
                          onClick={() => openInvoiceTab(invNo)}
                          title={`Open Export Commercial Invoice ${invNo}`}
                          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-sky-500/10 text-sky-300 hover:bg-sky-500/20 hover:text-sky-200 border border-sky-500/30 text-[11px] font-mono uppercase tracking-widest"
                        >
                          <LinkIcon size={11} />{invNo}
                        </button>
                      );
                    }
                    return (
                      <span
                        data-testid={`epl-ci-pending-${r.doc_no}`}
                        title="No Export Commercial Invoice raised against this EPL yet."
                        className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-amber-500/10 text-amber-400 border border-amber-500/30 text-[11px] font-mono uppercase tracking-widest"
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />INV PENDING
                      </span>
                    );
                  })()}
                </td>
                <td className="px-4 py-3">
                  <RowActionsMenu testId={`epl-actions-${r.doc_no}`}>
                    <button data-testid={`epl-view-${r.doc_no}`} onClick={() => setViewing(r)} className="p-1.5 rounded-sm border border-[var(--border)] text-[var(--accent)] hover:bg-[var(--bg-2)]" title="View"><EyeIcon size={14} /></button>
                    <button data-testid={`epl-pdf-${r.doc_no}`} onClick={() => printPdf(r.id)} className="p-1.5 rounded-sm text-sky-300 hover:bg-sky-500/10 border border-sky-500/30" title="Print"><PrinterIcon size={14} weight="bold" /></button>
                    <DocDownloadIcons type="EPL" id={r.id} testPrefix={`epl-row-${r.doc_no}`} />
                    {perms.edit && <button data-testid={`epl-edit-${r.doc_no}`} onClick={() => openEdit(r)} className="p-1.5 rounded-sm border border-[var(--border)] text-zinc-400 hover:text-white" title="Edit"><PencilSimpleIcon size={14} /></button>}
                    {perms.del && <button data-testid={`epl-del-${r.doc_no}`} onClick={() => remove(r.id)} className="p-1.5 rounded-sm border border-[var(--border)] text-zinc-400 hover:text-red-400 hover:border-red-500/30" title="Delete"><TrashIcon size={14} /></button>}
                  </RowActionsMenu>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={11} className="py-16 text-center text-zinc-500">No export packing lists yet</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-[95vw] w-[95vw] max-h-[95vh] overflow-y-auto" onInteractOutside={(e) => e.preventDefault()} description="Export document action — Proforma / Packing List / Sales Order / Delivery Challan." onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle className="font-display">{editing ? "Edit EPL" : "New EPL"} · {form.doc_no || <span className="text-zinc-500 text-sm">Auto · assigned on save</span>}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-3 gap-3">
            <F label="EPL Date"><input type="date" value={form.epl_date} onChange={e => setForm({ ...form, epl_date: e.target.value })} className={inp} /></F>
            <F label="Export SO *" className="col-span-2">
              <Select value={form.export_order_id} onValueChange={onEsoPick}>
                <SelectTrigger className={selCls}><SelectValue placeholder="Select ESO" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                  {/* Mar 1, 2026 · Guard: only Export SOs (doc_no ESO-*) appear here. */}
                  {orders.filter(o => (!o.doc_no || String(o.doc_no).toUpperCase().startsWith("ESO")) && o.order_against !== "stock").map(o => <SelectItem key={o.id} value={o.id}>{o.doc_no} — {o.buyer_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Container No"><input value={form.container_no} onChange={e => setForm({ ...form, container_no: e.target.value })} className={inp} /></F>
            <F label="Seal No"><input value={form.seal_no} onChange={e => setForm({ ...form, seal_no: e.target.value })} className={inp} /></F>
            <F label="Container Type">
              <Select value={form.container_type} onValueChange={v => setForm({ ...form, container_type: v })}>
                <SelectTrigger className={selCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  {CONTAINER_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Package Type"><input value={form.package_type} onChange={e => setForm({ ...form, package_type: e.target.value })} className={inp} /></F>
            <F label="Shipping Marks" className="col-span-2"><input value={form.shipping_marks} onChange={e => setForm({ ...form, shipping_marks: e.target.value })} className={inp} /></F>
          </div>

          {/* Mar 2, 2026 · Dispatch Source toggle — Path A (Packing Floor) vs Path B (FG-STORE) */}
          <div className="mt-3 border border-[var(--border)] rounded-sm bg-[var(--bg-2)]/40 p-2 flex items-center gap-2 flex-wrap">
            <span className="overline text-[10px] pl-1 pr-2 text-zinc-500">Dispatch From</span>
            <button
              type="button"
              data-testid="epl-dispatch-packing-floor"
              onClick={() => setDispatchSource("PACKING-FLOOR")}
              className={`px-3 py-1.5 rounded-sm text-xs font-semibold tracking-wide transition-colors ${dispatchSource === "PACKING-FLOOR" ? "bg-amber-500 text-black" : "text-zinc-400 hover:bg-[var(--bg-1)]"}`}
              title="Path A · Pick packing slips directly from the packing floor"
            >Packing Floor</button>
            <button
              type="button"
              data-testid="epl-dispatch-fg-store"
              onClick={() => setDispatchSource("FG-STORE")}
              className={`px-3 py-1.5 rounded-sm text-xs font-semibold tracking-wide transition-colors ${dispatchSource === "FG-STORE" ? "bg-emerald-500 text-black" : "text-zinc-400 hover:bg-[var(--bg-1)]"}`}
              title="Path B · Standard dispatch from FG store"
            >FG-STORE</button>
            <span className="ml-auto text-[10px] font-mono text-zinc-500" data-testid="epl-source-godown-hint">
              Outward godown → <span className="text-zinc-300">{dispatchSource === "FG-STORE" ? "FG-STORE" : "PACK-1FLR"}</span>
            </span>
          </div>

          {/* Source Mode toggle — mirror of SO Packing List's Manual|Slips flow */}
          <div className="mt-3 border border-[var(--border)] rounded-sm bg-[var(--bg-2)]/40 p-2 flex items-center gap-2 flex-wrap">
            <span className="overline text-[10px] pl-1 pr-2 text-zinc-500">Boxes Source</span>
            <button
              type="button"
              data-testid="epl-source-manual"
              onClick={() => setSourceMode("manual")}
              className={`px-3 py-1.5 rounded-sm text-xs font-semibold tracking-wide transition-colors ${sourceMode === "manual" ? "bg-[var(--accent)] text-white" : "text-zinc-400 hover:bg-[var(--bg-1)]"}`}
            >Manual Entry</button>
            <button
              type="button"
              data-testid="epl-source-slips"
              onClick={() => setSourceMode("slips")}
              className={`px-3 py-1.5 rounded-sm text-xs font-semibold tracking-wide transition-colors ${sourceMode === "slips" ? "bg-sky-500 text-white" : "text-zinc-400 hover:bg-[var(--bg-1)]"}`}
            >Pull from Packing Slips</button>
            <span className="text-[10px] text-zinc-500 ml-2 italic">
              {sourceMode === "manual"
                ? "Type each box yourself"
                : "Tick Export-linked Packing Slips and their boxes flow in automatically · use Select All to pick every eligible slip"}
            </span>
          </div>

          {sourceMode === "slips" && (
            <div className="mt-3 space-y-3" data-testid="epl-slips-panel">
              {/* Product code chip filter — mirrors SO PL. Empty selection ⇒ show all. */}
              {form.export_order_id && esoProductCodes.length > 0 && (
                <div className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/30">
                  <div className="overline mb-2 flex items-center gap-2">
                    <span>Product Code Filter</span>
                    <span className="text-[10px] text-zinc-500 normal-case tracking-normal">Tick any to narrow slips · empty = show all</span>
                  </div>
                  <div className="flex flex-wrap gap-2" data-testid="epl-product-code-chips">
                    {esoProductCodes.map(pc => {
                      const on = productFilter.has(pc);
                      return (
                        <button
                          key={pc}
                          type="button"
                          data-testid={`epl-pc-chip-${pc}`}
                          onClick={() => toggleProductCode(pc)}
                          className={`px-2 py-1 rounded-sm text-[11px] font-mono border transition-colors ${on ? "bg-[var(--accent)]/20 border-[var(--accent)] text-[var(--accent)]" : "border-[var(--border)] text-zinc-400 hover:border-zinc-500"}`}
                        >{pc}</button>
                      );
                    })}
                    {productFilter.size > 0 && (
                      <button type="button" onClick={clearProductCodes} className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300 ml-2">Clear</button>
                    )}
                  </div>
                </div>
              )}

              {/* Available Packing Slips — proper table layout matching SO PL screenshot */}
              <div className="border border-[var(--border)] rounded-sm">
                <div className="flex items-center justify-between px-3 py-2 border-b border-[var(--border)] bg-[var(--bg-2)]/30">
                  <div className="overline">
                    Available Packing Slips
                    <span className="text-zinc-500 normal-case ml-2 tracking-normal">
                      {form.export_order_id ? `${filteredSlips.length} of ${availableSlips.length} · ${selectedSlipIds.size} selected` : "Pick an Export SO first"}
                    </span>
                    {usedElsewhereCount > 0 && (
                      <span
                        className="text-[10px] text-amber-400 normal-case ml-2 tracking-normal"
                        title="These packing slips are already linked to another EPL and are hidden to prevent duplicate pulls."
                        data-testid="epl-used-elsewhere-hint"
                      >· {usedElsewhereCount} slip{usedElsewhereCount === 1 ? "" : "s"} already used in another EPL — hidden</span>
                    )}
                  </div>
                  {availableSlips.length > 0 && (
                    <div className="flex items-center gap-2">
                      {/* Mar 1, 2026 · Kept as a small text-only Clear affordance
                          alongside the new header-row bulk checkbox that
                          mirrors SO PL. Select All lives inside the table
                          header now for visual parity. */}
                      <button data-testid="epl-slips-clear" onClick={clearAllSlips} className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm border border-[var(--border)] text-zinc-400 hover:bg-[var(--bg-2)]">Clear</button>
                    </div>
                  )}
                </div>
                {filteredSlips.length === 0 ? (
                  <div className="text-center py-8 text-xs text-zinc-500">
                    {form.export_order_id
                      ? (productFilter.size > 0 ? "No slips match the product filter — untick chips to widen." : "No Export-linked Packing Slips found for this ESO — create some in the Packing Slips module first.")
                      : "Pick an Export SO to see its Packing Slips."}
                  </div>
                ) : (
                  <div className="overflow-x-auto max-h-72 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-[var(--bg-2)]">
                        <tr className="uppercase tracking-widest text-[10px] text-zinc-500">
                          {/* Mar 1, 2026 · Left-side bulk-select checkbox — parity with
                              SO PL. Checked when every filtered slip is selected;
                              indeterminate when partially selected. */}
                          <th className="w-8 px-2 py-2">
                            <input
                              type="checkbox"
                              data-testid="epl-slips-select-all"
                              checked={filteredSlips.length > 0 && filteredSlips.every(s => selectedSlipIds.has(s.id))}
                              ref={el => { if (el) el.indeterminate = filteredSlips.some(s => selectedSlipIds.has(s.id)) && !filteredSlips.every(s => selectedSlipIds.has(s.id)); }}
                              onChange={() => filteredSlips.every(s => selectedSlipIds.has(s.id)) ? clearAllSlips() : selectAllSlips()}
                              className="w-3.5 h-3.5 accent-[var(--accent)] cursor-pointer"
                              title="Select all filtered slips"
                            />
                          </th>
                          <th className="text-left px-2 py-2">EPS No</th>
                          <th className="text-left px-2 py-2">Date</th>
                          <th className="text-left px-2 py-2">Product Code(s)</th>
                          <th className="text-left px-2 py-2">Article Description</th>
                          <th className="text-left px-2 py-2">Box IDs</th>
                          <th className="text-right px-2 py-2">Boxes</th>
                          <th className="text-right px-2 py-2">Meters</th>
                          <th className="text-right px-2 py-2">Net Wt</th>
                          <th className="text-right px-2 py-2">Gross Wt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSlips.map(ps => {
                          const isSel = selectedSlipIds.has(ps.id);
                          const nb = (ps.boxes || []).length;
                          const tot = (ps.boxes || []).reduce((a, b) => ({
                            m: a.m + Number(b.meters || 0),
                            n: a.n + Number(b.net_weight || 0),
                            g: a.g + Number(b.gross_weight || 0),
                          }), { m: 0, n: 0, g: 0 });
                          const pcs = Array.from(new Set((ps.boxes || []).map(b => b.product_code).filter(Boolean))).join(", ");
                          const desc = Array.from(new Set((ps.boxes || []).map(b => descFor(b.product_code)).filter(Boolean))).join(" · ") || "—";
                          const boxIds = (ps.boxes || []).map(b => b.box_no).filter(Boolean).join(", ") || "—";
                          return (
                            <tr key={ps.id} data-testid={`epl-slip-row-${ps.slip_no}`} onClick={() => toggleSlipSel(ps.id)} className={`border-t border-[var(--border)] cursor-pointer hover:bg-[var(--bg-2)]/40 ${isSel ? "bg-[var(--accent)]/10" : ""}`}>
                              <td className="px-2 py-2"><input type="checkbox" data-testid={`epl-slip-cb-${ps.slip_no}`} checked={isSel} onChange={() => toggleSlipSel(ps.id)} onClick={(e) => e.stopPropagation()} className="w-3.5 h-3.5 accent-[var(--accent)]" /></td>
                              <td className="px-2 py-2 font-mono text-[var(--accent)] font-semibold">
                                <span data-testid={`epl-slip-eps-${ps.slip_no}`}>{ps.slip_no}</span>
                                {isSel && (
                                  <span
                                    className="ml-1.5 inline-flex items-center gap-1 px-1 py-[1px] rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/40 text-[8px] uppercase tracking-widest align-middle"
                                    data-testid={`epl-slip-included-${ps.slip_no}`}
                                    title="Included in this EPL — locked from other lists on save"
                                  >INCLUDED</span>
                                )}
                              </td>
                              <td className="px-2 py-2 text-zinc-400">{ps.pack_date}</td>
                              <td className="px-2 py-2 font-mono text-zinc-300 truncate max-w-[200px]" title={pcs}>{pcs || "—"}</td>
                              <td className="px-2 py-2 text-zinc-300 truncate max-w-[220px]" title={desc} data-testid={`epl-slip-desc-${ps.slip_no}`}>{desc}</td>
                              <td className="px-2 py-2 font-mono text-zinc-400 truncate max-w-[220px]" title={boxIds} data-testid={`epl-slip-boxids-${ps.slip_no}`}>{boxIds}</td>
                              <td className="px-2 py-2 text-right font-mono text-zinc-300">{nb}</td>
                              <td className="px-2 py-2 text-right font-mono text-zinc-300">{tot.m.toFixed(0)}</td>
                              <td className="px-2 py-2 text-right font-mono text-zinc-300">{tot.n.toFixed(2)}</td>
                              <td className="px-2 py-2 text-right font-mono text-emerald-400">{tot.g.toFixed(2)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      {selectedSlipIds.size > 0 && (
                        <tfoot className="sticky bottom-0 bg-[var(--bg-2)]">
                          <tr className="font-medium border-t border-[var(--border)]">
                            <td colSpan={6} className="px-2 py-2 text-right text-[10px] uppercase tracking-widest text-zinc-500">Selected Totals</td>
                            <td className="px-2 py-2 text-right font-mono bg-pink-500/10" data-testid="epl-selected-total-boxes">{selectedTotals.boxes}</td>
                            <td className="px-2 py-2 text-right font-mono">{selectedTotals.meters.toFixed(0)} m</td>
                            <td className="px-2 py-2 text-right font-mono">{selectedTotals.net.toFixed(2)}</td>
                            <td className="px-2 py-2 text-right font-mono bg-emerald-500/10">{selectedTotals.gross.toFixed(2)}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                )}
              </div>

              {/* Generated Packing List preview — box-wise, mirrors SO PL */}
              {selectedSlipIds.size > 0 && (form.boxes || []).length > 0 && (
                <div>
                  <div className="overline mb-2 flex items-center gap-2 flex-wrap">
                    <span>Generated Packing List · {form.boxes.length} boxes</span>
                    <span className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-[var(--accent)]/15 text-[var(--accent)]" data-testid="epl-box-range">
                      BOX-{String(eplBoxStartIdx).padStart(4, "0")} → BOX-{String(eplBoxStartIdx + form.boxes.length - 1).padStart(4, "0")}
                    </span>
                    {eplBoxStartIdx > 1 && (
                      <span className="text-[10px] text-zinc-500">(continues from previous packing lists · box #{eplBoxStartIdx - 1})</span>
                    )}
                  </div>
                  <div className="border border-[var(--border)] rounded-sm overflow-x-auto max-h-64 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-[var(--bg-2)]">
                        <tr className="uppercase tracking-widest text-[10px] text-zinc-500">
                          <th className="px-2 py-2 text-left">Box No</th>
                          <th className="px-2 py-2 text-left">Slip · Box</th>
                          <th className="px-2 py-2 text-left">Product Code</th>
                          <th className="px-2 py-2 text-left">Style No</th>
                          <th className="px-2 py-2 text-right">Meter</th>
                          <th className="px-2 py-2 text-right">Joints</th>
                          <th className="px-2 py-2 text-right">Net Wt</th>
                          <th className="px-2 py-2 text-right">Tare Wt</th>
                          <th className="px-2 py-2 text-right">Gross Wt</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(form.boxes || []).map((b, i) => (
                          <tr key={`${b.box_no}-${i}`} data-testid={`epl-preview-${b.box_no}`} className="border-t border-[var(--border)]">
                            <td className="px-2 py-1 font-mono font-semibold">{b.box_no}</td>
                            <td className="px-2 py-1 font-mono text-[11px] text-zinc-400">{b.source_slip_no || "—"} · {b.source_slip_box_no || "—"}</td>
                            <td className="px-2 py-1 font-mono text-[11px]">{b.article_no || <span className="text-zinc-500">—</span>}</td>
                            <td className="px-2 py-1 font-mono text-[11px]">{b.style_no || <span className="text-zinc-500">—</span>}</td>
                            <td className="px-2 py-1 text-right font-mono">{Number(b.qty || 0).toFixed(0)}</td>
                            <td className="px-2 py-1 text-right font-mono text-zinc-400">{Number(b.joints || 0)}</td>
                            <td className="px-2 py-1 text-right font-mono">{Number(b.net_weight_kg || 0).toFixed(2)}</td>
                            <td className="px-2 py-1 text-right font-mono text-zinc-400">{Number(b.tare_weight_kg || 0).toFixed(2)}</td>
                            <td className="px-2 py-1 text-right font-mono">{Number(b.gross_weight_kg || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {sourceMode === "manual" && (
          <div className="mt-3">
            <div className="flex justify-between items-center mb-2">
              <div className="overline">Boxes</div>
              <button onClick={addBox} className="text-xs h-7 px-2 rounded-sm border border-[var(--border)] text-zinc-400 hover:text-white flex items-center gap-1"><PlusIcon size={11} /> Add Box</button>
            </div>
            <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[var(--border)] text-zinc-500">
                    <th className="text-left px-2 py-2 min-w-[110px]">Box No</th>
                    <th className="text-left px-2 py-2 min-w-[120px]">Article</th>
                    <th className="text-left px-2 py-2 min-w-[120px]">Style No</th>
                    <th className="text-left px-2 py-2 min-w-[100px]">HS Code</th>
                    <th className="text-right px-2 py-2 min-w-[70px]">Qty</th>
                    <th className="text-left px-2 py-2 min-w-[50px]">UOM</th>
                    <th className="text-right px-2 py-2 min-w-[70px]">Net kg</th>
                    <th className="text-right px-2 py-2 min-w-[70px]">Gross kg</th>
                    <th className="text-left px-2 py-2 min-w-[90px]">L×W×H cm</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {(form.boxes || []).map((b, i) => (
                    <tr key={i} className="border-b border-[var(--border)]">
                      <td className="p-1"><input value={b.box_no} onChange={e => setBox(i, "box_no", e.target.value)} placeholder="Auto" className={inp + " h-8 font-mono"} /></td>
                      <td className="p-1"><input value={b.article_no} onChange={e => setBox(i, "article_no", e.target.value)} className={inp + " h-8"} /></td>
                      <td className="p-1"><input data-testid={`epl-box-style-${i}`} value={b.style_no || ""} onChange={e => setBox(i, "style_no", e.target.value)} maxLength={20} className={inp + " h-8 font-mono"} /></td>
                      <td className="p-1"><input value={b.hs_code} onChange={e => setBox(i, "hs_code", e.target.value)} className={inp + " h-8 font-mono"} /></td>
                      <td className="p-1"><FormattedNumberInput data-nouppercase step="0.01" value={b.qty} onChange={e => setBox(i, "qty", parseFloat(e.target.value) || 0)} className={inp + " h-8 text-right font-mono"} /></td>
                      <td className="p-1"><input value={b.uom} onChange={e => setBox(i, "uom", e.target.value)} className={inp + " h-8"} /></td>
                      <td className="p-1"><FormattedNumberInput data-nouppercase step="0.001" value={b.net_weight_kg} onChange={e => setBox(i, "net_weight_kg", parseFloat(e.target.value) || 0)} className={inp + " h-8 text-right font-mono"} /></td>
                      <td className="p-1"><FormattedNumberInput data-nouppercase step="0.001" value={b.gross_weight_kg} onChange={e => setBox(i, "gross_weight_kg", parseFloat(e.target.value) || 0)} className={inp + " h-8 text-right font-mono"} /></td>
                      <td className="p-1"><input value={b.dimensions} onChange={e => setBox(i, "dimensions", e.target.value)} className={inp + " h-8"} /></td>
                      <td className="p-1"><button onClick={() => rmBox(i)} className="text-zinc-500 hover:text-red-400"><TrashIcon size={12} /></button></td>
                    </tr>
                  ))}
                  {(form.boxes || []).length === 0 && <tr><td colSpan={10} className="py-6 text-center text-zinc-500">No boxes — click "+ Add Box"</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          )}

          <div className="grid grid-cols-4 gap-3 mt-3">
            <F label="Total Boxes"><div className="h-9 px-3 flex items-center bg-[var(--bg-2)] rounded-sm font-mono text-white">{totals.count}</div></F>
            <F label="Total Qty"><div className="h-9 px-3 flex items-center bg-[var(--bg-2)] rounded-sm font-mono text-white">{totals.qty.toFixed(2)}</div></F>
            <F label="Total Net kg"><div className="h-9 px-3 flex items-center bg-[var(--bg-2)] rounded-sm font-mono text-white">{totals.net.toFixed(3)}</div></F>
            <F label="Total Gross kg"><div className="h-9 px-3 flex items-center bg-[var(--bg-2)] rounded-sm font-mono text-emerald-400" data-testid="epl-total-gross">{totals.gross.toFixed(3)}</div></F>
          </div>

          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-400">Cancel</button>
            <button
              data-testid="save-epl"
              onClick={() => { if (hasExcessSelected) { alert(EXCESS_BLOCK_MSG); return; } save(); }}
              disabled={hasExcessSelected}
              title={hasExcessSelected ? EXCESS_BLOCK_MSG : ""}
              className={`px-4 py-2 rounded-sm text-sm font-semibold text-white ${hasExcessSelected ? "bg-zinc-700 cursor-not-allowed opacity-60" : "bg-[var(--accent)] hover:bg-blue-600"}`}
            >{hasExcessSelected ? "Blocked · Excess Dispatch in selection" : `${editing ? "Update" : "Create"} EPL`}</button>
          </DialogFooter>
          {hasExcessSelected && (
            <div className="mt-2 p-3 rounded-sm border border-red-500/60 bg-red-500/10 text-[11px] text-red-200" data-testid="epl-excess-block-banner">
              <div className="font-semibold mb-1 text-red-300">Packing List Lock · {unauthorizedExcessSlips.length} un-authorized excess slip{unauthorizedExcessSlips.length === 1 ? "" : "s"} in selection</div>
              <div>{EXCESS_BLOCK_MSG}</div>
              <div className="mt-1.5 font-mono text-[10px] text-red-300/80">Un-authorized: {unauthorizedExcessSlips.map(s => s.slip_no).join(", ")}</div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Feb 2026 · Read-only View dialog — mirrors the ESO pattern. */}
      <Dialog open={!!viewing} onOpenChange={(v) => !v && setViewing(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-[90vw] w-[90vw] max-h-[90vh] overflow-y-auto" data-testid="epl-view-dialog" description="Export document action — Proforma / Packing List / Sales Order / Delivery Challan.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              <span>Export Packing List · {viewing?.doc_no}</span>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-sm bg-[var(--bg-2)] text-zinc-400 tracking-widest uppercase">VIEW</span>
            </DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-4 gap-3">
                <div><div className="overline mb-0.5">EPL Date</div><div className="font-mono">{viewing.epl_date}</div></div>
                <div><div className="overline mb-0.5">Export SO</div><div className="font-mono">{viewing.export_order_no || "—"}</div></div>
                <div><div className="overline mb-0.5">Container</div><div className="font-mono">{viewing.container_no || "—"} · {viewing.container_type}</div></div>
                <div><div className="overline mb-0.5">Seal</div><div className="font-mono">{viewing.seal_no || "—"}</div></div>
                <div><div className="overline mb-0.5">Package Type</div><div className="font-mono">{viewing.package_type || "—"}</div></div>
                <div>
                  <div className="overline mb-0.5">Commercial Invoice</div>
                  <div className="font-mono" data-testid="epl-view-ci">
                    {(() => {
                      const invNo = viewing.export_commercial_invoice_no
                        || (viewing.linked_invoice_kind === "commercial" ? viewing.linked_invoice_no : "");
                      if (invNo) {
                        return (
                          <button
                            type="button"
                            data-testid="epl-view-ci-link"
                            onClick={() => { setViewing(null); openInvoiceTab(invNo); }}
                            className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-sky-500/10 text-sky-300 hover:bg-sky-500/20 border border-sky-500/30 text-[11px] uppercase tracking-widest"
                          >
                            <LinkIcon size={11} />{invNo}
                          </button>
                        );
                      }
                      return (
                        <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-amber-500/10 text-amber-400 border border-amber-500/30 text-[11px] uppercase tracking-widest">
                          <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />INV PENDING
                        </span>
                      );
                    })()}
                  </div>
                </div>
                <div className="col-span-2"><div className="overline mb-0.5">Shipping Marks</div><div className="font-mono truncate">{viewing.shipping_marks || "—"}</div></div>
              </div>
              <DocumentInfo docNo={viewing.doc_no} createdAt={viewing.created_at} updatedAt={viewing.updated_at} createdBy={viewing.created_by} docLabel="Export PL No." testidPrefix="epl" className="" />
              <div className="grid grid-cols-4 gap-3">
                <div className="tactical-card p-3"><div className="overline mb-0.5">Total Boxes</div><div className="font-mono text-white text-lg">{viewing.total_boxes ?? (viewing.boxes || []).length}</div></div>
                <div className="tactical-card p-3"><div className="overline mb-0.5">Total Qty</div><div className="font-mono text-white text-lg">{fmtQtyInt(viewing.total_qty || (viewing.boxes || []).reduce((a, b) => a + Number(b.qty || 0), 0))}</div></div>
                <div className="tactical-card p-3"><div className="overline mb-0.5">Net kg</div><div className="font-mono text-white text-lg">{Number(viewing.total_net_weight_kg || 0).toFixed(2)}</div></div>
                <div className="tactical-card p-3"><div className="overline mb-0.5">Gross kg</div><div className="font-mono text-emerald-400 text-lg">{Number(viewing.total_gross_weight_kg || 0).toFixed(2)}</div></div>
              </div>
              <div>
                <div className="overline mb-2">Boxes · {(viewing.boxes || []).length}</div>
                <div className="border border-[var(--border)] rounded-sm overflow-x-auto max-h-[50vh] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[var(--bg-2)]">
                      <tr className="uppercase tracking-widest text-[10px] text-zinc-500">
                        <th className="text-left px-2 py-2">Box No</th>
                        <th className="text-left px-2 py-2">Slip · Box</th>
                        <th className="text-left px-2 py-2">Product Code</th>
                        <th className="text-left px-2 py-2">Style No</th>
                        <th className="text-left px-2 py-2">HS Code</th>
                        <th className="text-right px-2 py-2">Qty</th>
                        <th className="text-left px-2 py-2">UOM</th>
                        <th className="text-right px-2 py-2">Joints</th>
                        <th className="text-right px-2 py-2">Net kg</th>
                        <th className="text-right px-2 py-2">Tare kg</th>
                        <th className="text-right px-2 py-2">Gross kg</th>
                        <th className="text-left px-2 py-2">L×W×H cm</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(viewing.boxes || []).map((b, i) => {
                        const tare = (b.tare_weight_kg != null && b.tare_weight_kg !== "")
                          ? Number(b.tare_weight_kg)
                          : Math.max(0, Number(b.gross_weight_kg || 0) - Number(b.net_weight_kg || 0));
                        return (
                          <tr key={i} className="border-t border-[var(--border)]" data-testid={`epl-view-box-${b.box_no || i}`}>
                            <td className="px-2 py-1 font-mono font-semibold text-white">{b.box_no || "—"}</td>
                            <td className="px-2 py-1 font-mono text-[11px] text-zinc-400">{b.source_slip_no || "—"} · {b.source_slip_box_no || "—"}</td>
                            <td className="px-2 py-1 font-mono">{b.article_no || "—"}</td>
                            <td className="px-2 py-1 font-mono" data-testid={`epl-view-box-style-${i}`}>{b.style_no || eplStyles[i] || "—"}</td>
                            <td className="px-2 py-1 font-mono text-zinc-400">{b.hs_code || "—"}</td>
                            <td className="px-2 py-1 text-right font-mono">{Number(b.qty || 0).toFixed(2)}</td>
                            <td className="px-2 py-1 font-mono text-zinc-400">{b.uom || "—"}</td>
                            <td className="px-2 py-1 text-right font-mono">{Number(b.joints || 0)}</td>
                            <td className="px-2 py-1 text-right font-mono">{Number(b.net_weight_kg || 0).toFixed(2)}</td>
                            <td className="px-2 py-1 text-right font-mono text-zinc-400">{tare.toFixed(2)}</td>
                            <td className="px-2 py-1 text-right font-mono">{Number(b.gross_weight_kg || 0).toFixed(2)}</td>
                            <td className="px-2 py-1 font-mono text-zinc-400">{b.dimensions || "—"}</td>
                          </tr>
                        );
                      })}
                      {(viewing.boxes || []).length === 0 && <tr><td colSpan={12} className="py-8 text-center text-zinc-500">No boxes</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
              {viewing.remarks && <div><div className="overline mb-0.5">Remarks</div><div className="text-zinc-300">{viewing.remarks}</div></div>}
            </div>
          )}
          <DialogFooter>
            <button data-testid="epl-view-pdf" onClick={() => viewing && printPdf(viewing.id)} className="px-4 py-2 rounded-sm border border-sky-500/40 text-sm text-sky-300 hover:bg-sky-500/10 flex items-center gap-2"><PrinterIcon size={14} weight="bold" />Browser Print</button>
            {viewing?.id && <DocDownloadButtons type="EPL" id={viewing.id} testPrefix="epl-view" />}
            <button onClick={() => setViewing(null)} className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-sm font-semibold text-white">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
//  Export Commercial Invoices
// ============================================================================
// ECI-MANAGEMENT-AUTH-CONTROL · Phase 2 · Feb 2026 — canonical Path B
// helpers. Sealed `export_invoices` field vocabulary is UNCHANGED;
// `approval_status` is the single source of truth for the Management
// Authorisation lifecycle. Row + View mode share the SAME
// `submitForApproval` handler (canonical single-handler contract).
const ECI_APPROVAL_TONE = {
  "Not Submitted":    "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  "Pending Approval": "bg-amber-500/15 text-amber-300 border-amber-500/30",
  "Rejected":         "bg-red-500/15 text-red-300 border-red-500/30",
  "Authorized":       "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  // ECI-CANCEL-H01-UAT-02G · Mar 2026 · Owner Rule — terminal
  // Invalidated lifecycle. Distinct badge tone from Cancelled
  // (Authorised-then-cancelled) so the register can visually
  // distinguish "never reached Authorised" from "was Authorised
  // then cancelled".
  "Invalidated":      "bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/40",
};
const ECI_APPROVAL_LABEL = {
  "Not Submitted":    "Draft",
  "Pending Approval": "Pending Authorisation",
  "Rejected":         "Rejected",
  "Authorized":       "Authorised",
  "Invalidated":      "Invalidated",
};
const eciState = (o) => ((o?.approval_status || "").trim() || "Not Submitted");
const isEciNotSubmitted = (o) => eciState(o) === "Not Submitted";
const isEciPending      = (o) => eciState(o) === "Pending Approval";
const isEciRejected     = (o) => eciState(o) === "Rejected";
const isEciAuthorised   = (o) => ["Authorized", "Approved", "Authorised"].includes(eciState(o));
// ECI-CANCEL-H01-UAT-02G · Mar 2026 · Owner Rule — Invalidated is a
// terminal state applied to Draft/Rejected ECIs declared unusable
// before ever reaching Authorised commercial lifecycle. No Edit /
// Submit / Amend / Cancel-request / Delete actions are exposed once
// the ECI is Invalidated.
const isEciInvalidated  = (o) => eciState(o) === "Invalidated";

function InvoicesTab({ perms }) {
  const [rows, setRows] = useState([]);
  const [orders, setOrders] = useState([]);
  const [epls, setEpls] = useState([]);
  const [articles, setArticles] = useState([]);
  const [itemsMaster, setItemsMaster] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  // Feb 2026 · read-only View dialog on the Actions column.
  const [viewing, setViewing] = useState(null);
  const eciStyles = useDocStyles(viewing?.id ? "ECI" : null, viewing?.id);
  // ECI-AMEND-H01 · Phase 2 · Feb 2026 — amendment prompt state
  // (View-mode inline). ONE canonical `raiseAmendment(row, cls, rsn)`
  // drives both the row-level and view-level Amend actions so the UI
  // never drifts (parity with SO Phase 2B UI Uniformity).
  const [amendPrompt, setAmendPrompt] = useState(false);
  const [amendClass, setAmendClass] = useState("A");
  const [amendReason, setAmendReason] = useState("");
  // ECI-CANCEL-H01 · Phase 2 · Mar 2026 — Cancellation Request state.
  // One canonical `requestEciCancellation(row, reason)` handler drives
  // both the row-level Cancel-Document button AND the View-mode
  // footer Confirm button (parity with SO Phase 2B / ECI Amend UI-
  // Uniformity). The pre-existing dialog-dismiss "Cancel" buttons
  // (Amend prompt Cancel, dialog Close, etc.) are UNCHANGED — they
  // close a form, they do not cancel a document. The document-level
  // action is deliberately labelled "Request ECI Cancellation" so no
  // operator confuses the two.
  const [cancelDocPrompt, setCancelDocPrompt] = useState(false);
  const [cancelDocReason, setCancelDocReason] = useState("");
  // ECI-CANCEL-H01-UAT-02G · Mar 2026 — Draft ECI Invalidation
  // prompt state. Reuses the View-mode footer pattern established
  // by cancel-request; single canonical handler drives both row-
  // level and View-mode entry points.
  const [invalidatePrompt, setInvalidatePrompt] = useState(false);
  const [invalidateReason, setInvalidateReason] = useState("");
  // DP-ECI-PRINT-EDC-LINK-01 · Phase 2 · Mar 2026 — on View-dialog open,
  // re-fetch the single ECI so the backend's shared `resolve_eci_link_refs`
  // helper (in routes/exports/_core.py) supplies the CURRENT operational
  // `linked_epl_nos` + `linked_edc_no`. The list endpoint is deliberately
  // kept lean; this GET is the source of truth for both the tiles below
  // and the "Linked Delivery Challan" row printed by Browser Print, so
  // View Mode and Print cannot drift.
  useEffect(() => {
    if (!viewing?.id) return;
    if (viewing.linked_edc_no !== undefined) return; // already enriched
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/exports/invoices/${viewing.id}`);
        if (cancelled || !data) return;
        setViewing(v => (v && v.id === data.id ? { ...v, ...data } : v));
      } catch { /* silent — tiles fall back to "—" */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewing?.id]);
  const empty = {
    inv_date: new Date().toISOString().slice(0, 10),
    export_order_id: "", export_order_no: "", packing_list_ids: [],
    buyer_name: "", buyer_address: "", consignee_name: "", consignee_address: "",
    incoterms: "FOB", port_of_loading: "", port_of_discharge: "",
    country_of_origin: "INDIA", country_of_final_destination: "",
    currency: "USD", fx_rate_to_inr: 83.0,
    items: [], freight_usd: 0, insurance_usd: 0, other_charges_usd: 0,
    gst_type: "LUT", drawback_scheme: "", drawback_pct: 0,
    lut_no: "", lut_date: "",
    payment_terms: "TT", lc_no: "", lc_date: "", remarks: "",
    // Phase UI-EPI/ECI.1 · Optional shipping/packing header fields
    number_of_cartons: 0, net_weight: 0, gross_weight: 0,
    // Phase UI-EPI/ECI.2 · EPI parity · Order Reference / Delivery / Shipping Marks
    delivery_location: "", delivery_address: "", credit_days: 0,
    carton_no_from: "", carton_no_to: "", made_in: "MADE IN INDIA",
  };
  const [form, setForm] = useState(empty);

  const load = async () => {
    // Mar 2026 · ECI picker uses `?unbilled_only=true` so already-billed
    // EPLs don't show up as pickable rows. The main dashboard / EDC
    // drill-in still fetch the FULL list (see PackingTab + OrderExtras).
    const [i, o, e, a, im] = await Promise.all([
      api.get("/exports/invoices/"),
      api.get("/exports/orders/"),
      api.get("/exports/packing-lists/?unbilled_only=true"),
      api.get("/articles/"),
      api.get("/items/"),
    ]);
    setRows(i.data); setOrders(o.data); setEpls(e.data); setArticles(a.data); setItemsMaster(im.data);
  };
  useEffect(() => { load(); }, []);

  // ECI-EXPORT-NUM-OWNERSHIP · Phase 1 · Feb 2026 — fetch Company
  // Profile Export Invoice numbering config. Drives:
  //   • The "Next ECI No" preview tile on New (Auto=ON).
  //   • The controlled manual-number input (Auto=OFF).
  // Storage key is `invoice_numbering_export_gst`; display label is
  // "Export Invoice (ECI / EGSTI)" (see InvoiceNumberingPanel.jsx).
  const [exportNumCfg, setExportNumCfg] = useState({ auto_generation: true, prefix: "", starting_sequence: 1 });
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/invoice-numbering");
        if (data?.export_gst) setExportNumCfg(data.export_gst);
      } catch { /* silent — editor falls back to auto-on defaults */ }
    })();
  }, []);

  const openCreate = () => {
    // Feb 2026 · ECI doc_no auto-assigned on save (SO-style).
    setForm({ ...empty, doc_no: "" });
    setEditing(null); setPickedEdcId(""); setEciSourceCatalog([]); setAddLineSrcId(""); setOpen(true);
  };
  const openEdit = (r) => {
    // A1-D03 P2.2 · Feb 2026 — rebuild the source-EDC-line catalog
    // from the SAVED items so Add Line continues to work in Edit
    // mode even without re-running the EDC prefill. Historical
    // ECIs (no source genealogy) yield an empty catalog; the
    // operator can still Edit non-source fields but Add Line is
    // disabled until an EDC is re-picked.
    const _cat = []; const _seen = new Set();
    for (const it of (r.items || [])) {
      const _key = `${it.source_edc_id || ""}::${it.source_edc_line_id || it.article_no || ""}`;
      if (!it.source_edc_id || _seen.has(_key)) continue;
      _seen.add(_key);
      _cat.push({
        key: _key,
        source_edc_id: it.source_edc_id,
        source_edc_no: it.source_edc_no || "",
        source_edc_line_id: it.source_edc_line_id || it.article_no || "",
        article_no: it.article_no || "",
        style_no: it.style_no || "",
        hs_code: it.hs_code || "",
        description: it.description || "",
        uom: it.uom || "MTR",
        unit_price_usd: Number(it.unit_price_usd || 0),
        shipped_qty: Number(it.shipped_qty || 0),
      });
    }
    setEciSourceCatalog(_cat); setAddLineSrcId("");
    setForm({ ...r }); setEditing(r.id); setPickedEdcId(""); setOpen(true);
    // A1-D03 · ECI Edit-Mode EDC Self-Ownership · Feb 2026 — the
    // `ciEdcPicker` useEffect below reruns on `form.export_order_id`
    // / `editing`, now carrying `exclude_invoice_id` so the saved
    // EDC resurfaces in the picker list. A separate effect then
    // resolves the matching EDC against `form.packing_list_ids`
    // and sets `pickedEdcId` to render the radio as pre-selected
    // immediately after picker hydration.
  };

  // Aug 2026 · EDC picker for the Commercial Invoice dialog. Mirrors the
  // domestic GST-Invoice SO→DC flow: fetch unlinked EDCs whenever the
  // user picks an Export SO, single-select via radio, choice stamps
  // `packing_list_ids` on the invoice so the backend's atomic EPL claim
  // (iter34) can enforce the 1-to-1 rule at Save.
  // Feb 2026 · When an EDC is picked we also call
  // POST /exports/prefill-from-eso-edcs to lock line-item qty to shipped
  // amounts (parity with domestic GST /prefill-from-so-dcs).
  const [ciEdcPicker, setCiEdcPicker] = useState([]);
  const [pickedEdcId, setPickedEdcId] = useState("");
  // A1-D03 · ECI Edit-Mode EDC Self-Ownership · Feb 2026 — stale
  // response guard. When the user flips the ESO (or opens a
  // different ECI in Edit mode) while an older `/eso-edc-picker`
  // call is still in flight, this ref keeps the latest request's
  // identity so the completion handler can drop stale payloads
  // instead of overwriting the current picker list / pre-selection.
  const _edcPickerReqRef = useRef(0);
  // A1-D03 P2.2 · Feb 2026 — Source-EDC-line catalog built from the
  // prefill response. Powers the "Add Line against existing EDC source"
  // flow (Owner directive §2) and the live quantity-reconciliation
  // strip (§5). Each entry is one unique source EDC line
  // (keyed by `source_edc_line_id`) and carries the EDC-shipped qty
  // + identity needed to seed a new ECI line that references the
  // same source. Reset whenever a different EDC is picked.
  const [eciSourceCatalog, setEciSourceCatalog] = useState([]);
  const [addLineSrcId, setAddLineSrcId] = useState("");
  useEffect(() => {
    if (!form.export_order_id) { setCiEdcPicker([]); setPickedEdcId(""); return; }
    const _reqId = ++_edcPickerReqRef.current;
    (async () => {
      try {
        // A1-D03 · ECI Edit-Mode EDC Self-Ownership · Feb 2026 —
        // pass the editing invoice id so the server exempts THIS
        // ECI's EPLs from the "consumed" filter (and ONLY this
        // ECI's EPLs, verified server-side). Create mode omits the
        // query param → identical pre-fix behaviour.
        const _params = editing ? { exclude_invoice_id: editing } : {};
        const { data } = await api.get(`/exports/eso-edc-picker/${form.export_order_id}`, { params: _params });
        if (_reqId !== _edcPickerReqRef.current) return;   // stale drop
        const _edcs = data?.edcs || [];
        setCiEdcPicker(_edcs);
        // Resolve the saved EDC by matching `form.packing_list_ids`
        // against each picker row's `packing_list_ids`. Edit mode
        // only — Create mode never had a saved selection to
        // restore. If no match is found we leave `pickedEdcId`
        // alone (openEdit cleared it); a non-destructive toast is
        // only emitted when the EDC genuinely cannot be resolved
        // AND the ECI actually has saved packing_list_ids.
        if (editing) {
          const _invEpls = form.packing_list_ids || [];
          if (_invEpls.length) {
            const _match = _edcs.find(e =>
              (e.packing_list_ids || []).some(p => _invEpls.includes(p)));
            if (_match) {
              setPickedEdcId(_match.id);
            } else {
              toast.warning(
                "This invoice's EDC could not be resolved from the picker. " +
                "Lines remain intact. If you intend to repoint, pick an EDC below."
              );
            }
          }
        }
      } catch { if (_reqId === _edcPickerReqRef.current) setCiEdcPicker([]); }
    })();
  }, [form.export_order_id, editing, form.packing_list_ids]);

  const pickEdc = async (dc) => {
    // A1-D03 · ECI Edit-Mode EDC Self-Ownership · Feb 2026 —
    // Clicking the already-selected EDC must NOT fire a fresh
    // prefill: the invoice lines (and any Normal/FOC split the
    // operator has adjusted in Edit mode) are preserved verbatim.
    // A no-op click also means no server round-trip and therefore
    // zero chance of a self-consumption 409.
    if (dc.id === pickedEdcId) return;
    setPickedEdcId(dc.id);
    try {
      const { data } = await api.post("/exports/prefill-from-eso-edcs", {
        eso_id: form.export_order_id,
        edc_ids: [dc.id],
        // Pass the editing ECI id so the server-side ownership
        // verification can exempt its own EPLs from the fail-closed
        // "already consumed" guard. Create mode omits this field.
        ...(editing ? { exclude_invoice_id: editing } : {}),
      });
      // A1-D03 P2.2 · Feb 2026 — build the source-EDC-line catalog
      // from the prefill rows. Each unique `source_edc_line_id`
      // under a given `source_edc_id` is one source line in the
      // reconciliation ledger (shipped_qty is the EDC quantity to
      // reconcile against). Prefill lines come in Normal classification
      // by default so they also seed the starting ECI items list.
      const _cat = [];
      const _seen = new Set();
      for (const it of (data.items || [])) {
        const _key = `${it.source_edc_id || ""}::${it.source_edc_line_id || it.article_no || ""}`;
        if (_seen.has(_key)) continue;
        _seen.add(_key);
        _cat.push({
          key: _key,
          source_edc_id: it.source_edc_id || "",
          source_edc_no: it.source_edc_no || dc.dc_no || "",
          source_edc_line_id: it.source_edc_line_id || it.article_no || "",
          article_no: it.article_no || "",
          style_no: it.style_no || "",
          hs_code: it.hs_code || "",
          description: it.description || "",
          uom: it.uom || "MTR",
          unit_price_usd: Number(it.unit_price_usd || 0),
          shipped_qty: Number(it.shipped_qty || it.qty || 0),
        });
      }
      setEciSourceCatalog(_cat);
      setAddLineSrcId("");
      // A1-D03 P2.2 · unlock prefilled qty for editing (Owner §1).
      // `locked_from_edc` is retained only as an audit marker; the
      // grid no longer disables the Qty input.
      const _normalisedItems = (data.items || []).map(it => ({
        ...it,
        line_type: it.line_type || "Normal",
        line_remarks: it.line_remarks || "",
      }));
      setForm(f => ({
        ...f,
        packing_list_ids: data.packing_list_ids || [],
        items: _normalisedItems,
        freight_usd: data.freight_usd ?? f.freight_usd,
        insurance_usd: data.insurance_usd ?? f.insurance_usd,
        other_charges_usd: data.other_charges_usd ?? f.other_charges_usd,
      }));
      toast.success(`Prefilled ${_normalisedItems.length} line${_normalisedItems.length !== 1 ? "s" : ""} from ${dc.dc_no} · qty editable · reconcile Σ(Normal+FOC)=EDC qty`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "EDC prefill failed");
    }
  };

  const pickEso = (eid) => {
    const o = orders.find(x => x.id === eid);
    if (!o) { setForm(f => ({ ...f, export_order_id: eid, export_order_no: "" })); return; }
    setForm(f => ({
      ...f,
      export_order_id: eid,
      export_order_no: o.doc_no,
      buyer_name: o.buyer_name || "",
      buyer_address: o.buyer_address || "",
      buyer_po_no: o.buyer_po_no || "",
      buyer_po_date: o.buyer_po_date || "",
      pi_no: o.pi_no || "",
      pi_date: o.pi_date || "",
      consignee_name: o.consignee_name || "",
      consignee_address: o.consignee_address || "",
      incoterms: o.incoterms || "FOB",
      port_of_loading: o.port_of_loading || "",
      port_of_discharge: o.port_of_discharge || "",
      country_of_final_destination: o.country_of_final_destination || "",
      fx_rate_to_inr: o.fx_rate_to_inr || 83,
      payment_terms: o.payment_terms || "TT",
      lc_no: o.lc_no || "", lc_date: o.lc_date || "",
      items: (o.items || []).map(it => ({ ...it })),
      freight_usd: o.freight_usd || 0,
      insurance_usd: o.insurance_usd || 0,
      other_charges_usd: o.other_charges_usd || 0,
    }));
  };
  // A1-D03 P2.2 · Feb 2026 — Add Line against a specific EDC source
  // line (Owner §2 & §5). The user picks a source from
  // `eciSourceCatalog`; the new ECI line is seeded with that source's
  // identity + genealogy and starts as Normal/qty-0/rate-from-ESO.
  // Historical / Edit-mode docs without a loaded catalog fall back
  // to a no-op prompt (never create an off-ledger line, per §2).
  const addItem = () => {
    const _src = (eciSourceCatalog || []).find(c => c.key === addLineSrcId);
    if (!_src) {
      toast.error("Pick a source EDC line before clicking Add Line — no off-ledger lines allowed.");
      return;
    }
    setForm(f => ({
      ...f,
      items: [...(f.items || []), {
        article_no: _src.article_no,
        style_no: _src.style_no,
        hs_code: _src.hs_code,
        description: _src.description,
        qty: 0,
        uom: _src.uom,
        unit_price_usd: _src.unit_price_usd,
        amount_usd: 0,
        line_type: "Normal",
        line_remarks: "",
        source_edc_id: _src.source_edc_id,
        source_edc_no: _src.source_edc_no,
        source_edc_line_id: _src.source_edc_line_id,
        shipped_qty: _src.shipped_qty,
        locked_from_edc: true,
      }],
    }));
    setAddLineSrcId("");
  };
  const rmItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, x) => x !== i) }));
  // A1-D03 P2.2 · Feb 2026 — `line_type` toggles force the paired
  // financial fields so the UI cannot persist an inconsistent row:
  //   Normal → rate editable (user re-enters if needed);
  //   FOC    → rate / amount pinned to 0; qty + reason remain editable.
  const setItem = (i, k, v) => setForm(f => {
    const items = f.items.map((it, x) => x === i ? { ...it, [k]: v } : it);
    if (k === "line_type" && v === "FOC") {
      items[i].unit_price_usd = 0;
      items[i].amount_usd = 0;
    } else if (k === "line_type" && v === "Normal") {
      items[i].amount_usd = +(Number(items[i].qty || 0) * Number(items[i].unit_price_usd || 0)).toFixed(2);
    } else if (k === "qty" || k === "unit_price_usd") {
      // FOC rows ignore price edits (pinned to 0); Normal rows recompute.
      if ((items[i].line_type || "Normal") === "FOC") {
        items[i].unit_price_usd = 0;
        items[i].amount_usd = 0;
      } else {
        items[i].amount_usd = +(Number(items[i].qty || 0) * Number(items[i].unit_price_usd || 0)).toFixed(2);
      }
    }
    return { ...f, items };
  });

  const sub = useMemo(() => (form.items || []).reduce((a, it) => a + Number(it.amount_usd || 0), 0), [form.items]);
  // A1-D03 P2.2 · Feb 2026 — Live grid TOTAL + reconciliation ledger.
  //   • totalQty   Σ qty across every ECI line (Normal + FOC).
  //   • totalUom   Unified UOM when all lines share one; otherwise
  //                `null` (mixed-UOM) so the TOTAL row renders
  //                `— (mixed UOMs)` without aggregating incompatible qty.
  //   • eciRecon   Per-(source_edc_id, source_edc_line_id) breakdown
  //                against the EDC shipped qty. Powers the ledger
  //                strip below the grid (Owner §5) + badge coloring.
  //   • lineHints  Per-line structural warning (missing source,
  //                invalid rate, missing FOC reason).
  const gridTotals = useMemo(() => {
    const items = form.items || [];
    let totalQty = 0; let totalValue = 0;
    const uomSet = new Set();
    for (const it of items) {
      totalQty += Number(it.qty || 0);
      totalValue += Number(it.amount_usd || 0);
      const u = ((it.uom || "").trim() || "").toUpperCase();
      if (u) uomSet.add(u);
    }
    return {
      totalQty, totalValue,
      totalUom: uomSet.size === 1 ? [...uomSet][0] : (uomSet.size > 1 ? null : ""),
      mixed: uomSet.size > 1,
    };
  }, [form.items]);
  const eciRecon = useMemo(() => {
    const items = form.items || [];
    const buckets = new Map();
    for (const it of items) {
      const src_id = (it.source_edc_id || "").trim();
      const src_line = (it.source_edc_line_id || "").trim();
      if (!src_id || !src_line) continue;
      const key = `${src_id}::${src_line}`;
      const b = buckets.get(key) || {
        key, source_edc_id: src_id, source_edc_no: it.source_edc_no || "",
        source_edc_line_id: src_line, article_no: it.article_no || "",
        uom: it.uom || "", normal_qty: 0, foc_qty: 0,
        shipped_qty: 0, uoms: new Set(),
      };
      if ((it.line_type || "Normal") === "FOC") b.foc_qty += Number(it.qty || 0);
      else b.normal_qty += Number(it.qty || 0);
      if ((it.uom || "").trim()) b.uoms.add((it.uom || "").trim().toUpperCase());
      buckets.set(key, b);
    }
    // Overlay the EDC-shipped qty from the catalog (if loaded).
    for (const c of (eciSourceCatalog || [])) {
      const b = buckets.get(c.key);
      if (b) b.shipped_qty = Number(c.shipped_qty || 0);
    }
    return [...buckets.values()].map(b => {
      const total = Number(b.normal_qty) + Number(b.foc_qty);
      const diff = Number((Number(b.shipped_qty || 0) - total).toFixed(3));
      return { ...b, total_qty: total, diff, uoms: [...b.uoms] };
    });
  }, [form.items, eciSourceCatalog]);
  const lineHints = useMemo(() => {
    const items = form.items || [];
    return items.map(it => {
      const hints = [];
      const lt = (it.line_type || "Normal");
      if (!(it.source_edc_id || "").trim() || !(it.source_edc_line_id || "").trim())
        hints.push("Missing EDC source genealogy");
      if (Number(it.qty || 0) <= 0) hints.push("Qty must be > 0");
      if (lt === "Normal" && Number(it.unit_price_usd || 0) <= 0) hints.push("Normal rate must be > 0");
      if (lt === "FOC" && Number(it.unit_price_usd || 0) !== 0) hints.push("FOC rate must be 0");
      if (lt === "FOC" && (it.line_remarks || "").trim().replace(/\s+/g, "").length < 4) hints.push("FOC reason ≥ 4 chars required");
      return hints;
    });
  }, [form.items]);
  // ECI-VALUE-RULE-SUPERSEDE · Feb 2026 — Owner directive 2026-10-07
  // SUPERSEDES the earlier "Grand Total = Subtotal, Freight/Insurance
  // excluded" rule. The frozen identity is now:
  //   Line Total Value = Grand Total = Σ item.amount_usd
  //   FOB Value         = Grand Total − Freight − Insurance (derived)
  //   fob + freight + insurance ≡ grand_total.
  // Freight / Insurance are COMPONENTS already contained within the
  // Grand Total; they are displayed separately but NEVER added on top
  // (anti-double-add). The backend `_totals()` enforces the same rule
  // server-side so UI / backend / print.html / PDF all agree. See
  // /app/memory/ECI_00102_Owner_FOB_Rule_Fix.md.
  const totalUsd = sub;                                              // Grand Total (USD)
  const fobUsd = Math.max(0, sub - Number(form.freight_usd || 0)
                                 - Number(form.insurance_usd || 0)); // FOB Value (USD)
  const igstPct = { LUT: 0, IGST_5: 5, IGST_12: 12, IGST_18: 18 }[form.gst_type] || 0;
  // ECI-IGST-DISCLOSURE · Feb 2026 — Owner directive 2026-10-07:
  // IGST base is GRAND TOTAL (NOT FOB). IGST is a separate below-
  // the-line disclosure, never added to Grand Total.
  const igstUsd = totalUsd * igstPct / 100;
  const fobInr = fobUsd * Number(form.fx_rate_to_inr || 0);
  const totalInr = totalUsd * Number(form.fx_rate_to_inr || 0);
  const igstInr = igstUsd * Number(form.fx_rate_to_inr || 0);        // INR mirror
  const drawbackInr = fobInr * Number(form.drawback_pct || 0) / 100;

  const save = async (submitAfter = false) => {
    if (!form.buyer_name.trim()) { toast.error("Buyer name required"); return; }
    if (!(form.items || []).length) { toast.error("Add at least one line item"); return; }
    // ECI-EXPORT-NUM-OWNERSHIP · Phase 1 · Feb 2026 — Owner Rule
    // (frozen 2026-10-07). When Company Profile Export Invoice
    // Auto-Generation is OFF (Option A), the operator must enter the
    // controlled manual invoice number on the ECI before Save.
    // Backend `_next_eci_doc_no` also enforces this with a 400, but a
    // client-side gate avoids a wasted round-trip and preserves the
    // picked-EDC prefill state.
    if (!editing && !exportNumCfg.auto_generation && !((form.doc_no || "").trim())) {
      toast.error("Export Invoice Number is required (Company Profile · Auto-Generation is OFF).");
      return;
    }
    // Feb 2026 · Chain-of-Traceability guard · every ECI must be raised
    // against an authorized EDC. The EDC prefill step wires the linked
    // EPLs into `packing_list_ids`, so a non-empty list is our proxy for
    // "an EDC has been picked". Prevents silent hand-keyed invoices with
    // no shipping ledger backing them.
    if (!(form.packing_list_ids || []).length) {
      toast.error("Select an authorized EDC first — the EDC prefill locks the invoice items to the shipped ledger");
      return;
    }
    try {
      let id = editing;
      if (editing) await api.put(`/exports/invoices/${editing}`, form);
      else { const { data } = await api.post("/exports/invoices/", form); id = data.id; }
      if (submitAfter) {
        try {
          await api.post(`/exports/invoices/${id}/submit-for-approval`);
          toast.success("Export Commercial Invoice · Submitted for Authorisation");
        } catch (se) {
          toast.error("Invoice was saved as Draft, but Submit for Authorisation failed. You can submit it from the list.");
        }
      } else {
        toast.success(editing ? "Invoice updated" : "Invoice created");
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const remove = async (id) => {
    // ECI-CANCEL-H01-UAT-02G · Mar 2026 · Owner Rule — hard-delete
    // retired. Kept as a defensive stub so any stale caller surfaces
    // a clear message directing them to the Invalidate lifecycle.
    toast.error("Hard-delete is retired for Export Commercial Invoices. Use Invalidate to disposition Draft/Rejected ECIs.");
    return id;
  };
  // ECI-CANCEL-H01-UAT-02G · Mar 2026 — canonical Invalidate handler.
  // Row Actions AND the View-mode footer Confirm button invoke the
  // SAME function; parity with the cancel-request UI pattern.
  const invalidateEci = async (row, reason) => {
    if (!row?.id) return;
    const _rsn = (reason ?? invalidateReason ?? "").trim();
    if (_rsn.length < 4) {
      toast.error("Invalidation reason is required (minimum 4 characters)");
      return;
    }
    try {
      await api.post(`/exports/invoices/${row.id}/invalidate`, { reason: _rsn });
      toast.success(`${row.doc_no} · Invalidated — retained permanently for audit`);
      setInvalidatePrompt(false); setInvalidateReason("");
      setViewing(null); setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalidation failed");
    }
  };
  // ECI-MANAGEMENT-AUTH-CONTROL · Phase 2 · Feb 2026 — canonical
  // Submit / Resubmit handler. Row + View mode invoke the SAME
  // function so the UI has a single source of truth (parity with
  // SO Phase 2B UI Uniformity).
  const submitForApproval = async (row) => {
    if (!row?.id) return;
    const isResubmit = isEciRejected(row);
    const verb = isResubmit ? "Resubmit" : "Submit";
    const label = isResubmit
      ? `Resubmit ${row.doc_no} for Management Re-authorisation?`
      : `Submit ${row.doc_no} for Management Authorisation?`;
    if (!window.confirm(label)) return;
    try {
      await api.post(`/exports/invoices/${row.id}/submit-for-approval`);
      toast.success(`${row.doc_no} · ${isResubmit ? "resubmitted" : "submitted"} for approval`);
      setViewing(null); setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${verb} failed`);
    }
  };
  // ECI-AMEND-H01 · Phase 2 · Feb 2026 — canonical Amendment handler.
  // Row Actions AND the View-mode Confirm button invoke the SAME
  // function; the View dialog carries the class + reason via
  // `amendClass` + `amendReason` state so the row-level entry point
  // can open the dialog with the prompt pre-expanded (mirrors the ESO
  // viewIntent="amend" pattern).
  const raiseAmendment = async (row, cls, reason) => {
    if (!row?.id) return;
    const _cls = (cls || amendClass || "A").toUpperCase();
    const _rsn = (reason ?? amendReason ?? "").trim();
    if (_rsn.length < 4) {
      toast.error("Amendment reason is required (minimum 4 characters)");
      return;
    }
    if (!(_cls === "A" || _cls === "B")) {
      toast.error("Select amendment class (A · Packing/Logistics or B · Commercial)");
      return;
    }
    try {
      await api.post(`/exports/invoices/${row.id}/amend`, {
        amendment_class: _cls, reason: _rsn,
      });
      toast.success(`${row.doc_no} · R${(Number(row.amendment_no || 0) + 1)} amendment opened`);
      setAmendPrompt(false); setAmendReason(""); setAmendClass("A");
      setViewing(null); setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Amendment failed");
    }
  };
  // ECI-CANCEL-H01 · Phase 2 · Mar 2026 — canonical Cancel-Document
  // handler. Model B: this issues a Cancellation REQUEST which enters
  // the Management Approval Queue; the ECI is NOT immediately
  // Cancelled. Called from row action AND View-mode footer button.
  const requestEciCancellation = async (row, reason) => {
    if (!row?.id) return;
    const _rsn = (reason ?? cancelDocReason ?? "").trim();
    if (_rsn.length < 4) {
      toast.error("Cancellation reason is required (minimum 4 characters)");
      return;
    }
    try {
      await api.post(`/exports/invoices/${row.id}/cancel-request`, { reason: _rsn });
      toast.success(`${row.doc_no} · cancellation requested — awaiting Management authorisation`);
      setCancelDocPrompt(false); setCancelDocReason("");
      setViewing(null); setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancellation request failed");
    }
  };
  const printPdf = async (id) => {
    try {
      const { data } = await api.get(`/exports/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to render PDF"); }
  };
  // Feb 2026 · Browser-printable A4 HTML (mirrors EPI layout). Blob-URL
  // for Safari; opens in a new tab so operators can Ctrl+P from the
  // browser like they do with Export Proforma & Export GST Invoice.
  const printHtml = async (id) => {
    try {
      const res = await api.get(`/exports/invoices/${id}/print.html`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 8000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to open print view"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="text-sm text-zinc-500">{rows.length} export invoice{rows.length !== 1 ? "s" : ""}</div>
        {perms.create && (
          <button data-testid="new-eci-btn" onClick={openCreate} className="h-9 px-4 bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold rounded-sm flex items-center gap-2">
            <PlusIcon size={14} weight="bold" /> New Export Commercial Invoice
          </button>
        )}
      </div>

      <div className="tactical-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
              <th className="text-left px-4 py-3">Invoice No</th>
              <th className="text-left px-4 py-3">Date</th>
              <th className="text-left px-4 py-3">Export SO</th>
              <th className="text-left px-4 py-3">Buyer</th>
              <th className="text-right px-4 py-3">Boxes</th>
              <th className="text-right px-4 py-3">Meters</th>
              <th className="text-right px-4 py-3">Total USD</th>
              <th className="text-right px-4 py-3">Total INR</th>
              <th className="text-left px-4 py-3">GST</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b border-[var(--border)] hover:bg-[var(--bg-2)]/40" data-testid={`eci-row-${r.doc_no}`}>
                <td className="px-4 py-3 font-mono text-white">
                  {r.doc_no}
                  {/* ECI-AMEND-H01 · Phase 2 · Feb 2026 — revision
                      badge on the primary doc-no cell. Mirrors the
                      ESO grid `R{amendment_no}` suffix pattern. */}
                  {Number(r.amendment_no || 0) > 0 && (
                    <span data-testid={`eci-rev-badge-${r.doc_no}`}
                          title={`Revision ${r.amendment_no}`}
                          className="ml-2 text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-amber-500/40 text-amber-300 bg-amber-500/10">
                      R{r.amendment_no}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-zinc-400">{r.inv_date}</td>
                <td className="px-4 py-3 text-right"><TwoLineDocNo value={r.export_order_no || "—"} className="text-zinc-300" /></td>
                <td className="px-4 py-3 text-white">{r.buyer_name}</td>
                {/* A2 · Phase 2 (Oct 2026) — ECI register Box Count.
                    Backend `/exports/invoices/` now stamps `box_count`
                    on each row from a batched EPL lookup, independent
                    of the `?unbilled_only=true` picker filter (which
                    legitimately excludes already-billed EPLs from the
                    creation eligibility list but used to make this
                    cell render blank). Precedence:
                      1. Authoritative backend `r.box_count`
                      2. Live join via the picker's `epls` state
                         (works only for not-yet-billed ECIs)
                      3. Historical `r.number_of_cartons` snapshot
                      4. Dash
                    `null` from the backend means "linked EPL refs
                    exist but none resolved" — surface a dash rather
                    than a fabricated zero (Owner directive §3). */}
                {(() => {
                  const linked = (r.packing_list_ids || []).map(pid => epls.find(e => e.id === pid)).filter(Boolean);
                  const livejoin = linked.reduce((s, e) => s + Number(e.box_count || (e.boxes || []).length || 0), 0);
                  const boxes = (r.box_count != null)
                    ? r.box_count
                    : (livejoin || Number(r.number_of_cartons || 0));
                  const meters = linked.length
                    ? linked.reduce((s, e) => s + Number(e.total_qty || (e.boxes || []).reduce((a, b) => a + Number(b.qty || 0), 0)), 0)
                    : (r.items || []).reduce((s, it) => s + Number(it.qty || 0), 0);
                  return (
                    <>
                      <td className="px-4 py-3 text-right font-mono text-white" data-testid={`eci-boxes-${r.doc_no}`}>{boxes || "—"}</td>
                      <td className="px-4 py-3 text-right font-mono text-[var(--accent)] tabular-nums" data-testid={`eci-meters-${r.doc_no}`}>{meters ? meters.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "—"}</td>
                    </>
                  );
                })()}
                <td className="px-4 py-3 text-right font-mono text-white">${Number(r.total_usd || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td className="px-4 py-3 text-right font-mono text-emerald-400">₹{Number(r.total_inr || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</td>
                <td className="px-4 py-3 font-mono text-[10px] uppercase tracking-widest text-zinc-400">{r.gst_type}</td>
                <td className="px-4 py-3" data-testid={`eci-status-cell-${r.doc_no}`}>
                  {/* ECI-MANAGEMENT-AUTH-CONTROL · Phase 2 · Feb 2026 —
                      Status column gates on the canonical Path B
                      `approval_status` axis (parity with sealed SO/WO
                      MA-INV-10 presentation-truth). Four unique badges
                      + unique testids for e2e assertability. */}
                  {(() => {
                    const s = eciState(r);
                    const label = ECI_APPROVAL_LABEL[s] || s;
                    const tone = ECI_APPROVAL_TONE[s] || "";
                    const kind = isEciNotSubmitted(r) ? "notsubmitted"
                              : isEciPending(r) ? "pending"
                              : isEciRejected(r) ? "rejected"
                              : isEciAuthorised(r) ? "authorised" : "other";
                    return (
                      <span data-testid={`eci-approval-badge-${kind}-${r.doc_no}`}
                            title={isEciRejected(r) ? (r.rejection_comments || "") : ""}
                            className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${tone}`}>{label}</span>
                    );
                  })()}
                </td>
                <td className="px-4 py-3">
                  <RowActionsMenu testId={`eci-actions-${r.doc_no}`}>
                    <button data-testid={`eci-view-${r.doc_no}`} onClick={() => setViewing(r)} className="p-1.5 rounded-sm border border-[var(--border)] text-[var(--accent)] hover:bg-[var(--bg-2)]" title="View"><EyeIcon size={14} /></button>
                    <button data-testid={`eci-print-html-${r.doc_no}`} onClick={() => printHtml(r.id)} className="p-1.5 rounded-sm text-sky-300 hover:bg-sky-500/10 border border-sky-500/30" title="Browser Print (A4 HTML)"><PrinterIcon size={14} weight="bold" /></button>
                    <DocDownloadIcons type="ECI" id={r.id} testPrefix={`eci-row-${r.doc_no}`} />
                    {/* ECI-AMEND-H01 · Phase 2 · Feb 2026 — Amend
                        pencil on Authorised rows. Opens the View
                        dialog with the amendment prompt pre-expanded
                        (single canonical handler). Only Authorised +
                        not-Cancelled rows show this. */}
                    {perms.edit && isEciAuthorised(r) && (r.status || "").toLowerCase() !== "cancelled" && (
                      <button
                        data-testid={`eci-amend-${r.doc_no}`}
                        onClick={() => { setAmendClass("A"); setAmendReason(""); setAmendPrompt(true); setViewing(r); }}
                        className="p-1.5 rounded-sm border border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                        title="Raise Amendment · Authorised ECI"
                      >
                        <PencilSimpleIcon size={14} weight="bold" />
                      </button>
                    )}
                    {/* ECI-CANCEL-H01 · Phase 2 · Mar 2026 — Cancel
                        Document button. Only visible on stable
                        Authorised ECIs (excludes: Not Submitted,
                        Pending, Rejected, Cancellation Pending,
                        Cancelled). Opens the View dialog with the
                        cancel prompt expanded — same canonical
                        handler as the View-mode footer button. */}
                    {perms.cancel && isEciAuthorised(r) && (r.status || "").toLowerCase() !== "cancelled" && (
                      <button
                        data-testid={`eci-cancel-doc-${r.doc_no}`}
                        onClick={() => { setCancelDocReason(""); setCancelDocPrompt(true); setAmendPrompt(false); setViewing(r); }}
                        className="p-1.5 rounded-sm border border-red-500/40 text-red-300 hover:bg-red-500/10"
                        title="Request ECI Cancellation"
                        aria-label="Request cancellation of Export Commercial Invoice"
                      >
                        <ProhibitIcon size={14} weight="bold" />
                      </button>
                    )}
                    {(isEciNotSubmitted(r) || isEciRejected(r)) && (
                      <>
                        {perms.submit && <button data-testid={`eci-submit-${r.doc_no}`} onClick={() => submitForApproval(r)} className="p-1.5 rounded-sm border border-sky-500/50 text-sky-300 hover:bg-sky-500/10" title={isEciRejected(r) ? "Resubmit for Management Authorisation" : "Submit for Management Authorisation"}><PaperPlaneTiltIcon size={14} weight="bold" /></button>}
                        {perms.edit && <button data-testid={`eci-edit-${r.doc_no}`} onClick={() => openEdit(r)} className="p-1.5 rounded-sm border border-[var(--border)] text-zinc-400 hover:text-white" title={isEciRejected(r) ? "Edit / Correct" : "Edit"}><PencilSimpleIcon size={14} /></button>}
                        {/* ECI-CANCEL-H01-UAT-02G · Mar 2026 · Owner Rule —
                            hard-delete retired. Draft/Rejected ECIs are
                            dispositioned via the controlled Invalidate
                            lifecycle. Uses ProhibitIcon (not TrashIcon)
                            so the action is visually distinct from a
                            data delete and mirrors the Cancel-Request
                            iconography vocabulary. */}
                        {perms.cancel && (<button
                          data-testid={`eci-invalidate-${r.doc_no}`}
                          onClick={() => { setInvalidateReason(""); setInvalidatePrompt(true); setCancelDocPrompt(false); setAmendPrompt(false); setViewing(r); }}
                          className="p-1.5 rounded-sm border border-fuchsia-500/50 text-fuchsia-300 hover:bg-fuchsia-500/10"
                          title="Invalidate · retain permanently for audit, release EPL for correct downstream flow"
                          aria-label="Invalidate Draft Export Commercial Invoice"
                        >
                          <ProhibitIcon size={14} weight="bold" />
                        </button>)}
                      </>
                    )}
                  </RowActionsMenu>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={11} className="py-16 text-center text-zinc-500">No export invoices yet</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="eci-editor-dialog" className="bg-[var(--bg-1)] border-[var(--border)] text-white w-[min(98vw,1920px)] max-w-[min(98vw,1920px)] max-h-[95vh] overflow-y-auto overflow-x-hidden space-y-3" onInteractOutside={(e) => e.preventDefault()} description="Export document action — Proforma / Packing List / Sales Order / Delivery Challan." onEscapeKeyDown={(e) => e.preventDefault()}>
          <DialogHeader><DialogTitle className="font-display flex items-center gap-3">
            <FileTextIcon size={18} weight="bold" className="text-[var(--accent)]" />
            <span>{editing ? "Edit Export Commercial Invoice" : "New Export Commercial Invoice"} · {form.doc_no || <span className="text-zinc-500 text-sm">Auto · assigned on save</span>}</span>
            <span className="text-[10px] font-mono uppercase tracking-widest text-amber-300 border border-amber-500/40 bg-amber-500/5 px-2 py-0.5 rounded-sm">Export</span>
            <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-sm border border-zinc-500/40 bg-zinc-500/5 text-zinc-300 text-[10px] font-mono uppercase tracking-widest">{form.doc_status || "Draft"}</span>
          </DialogTitle></DialogHeader>

          {/* ECI-EXPORT-NUM-OWNERSHIP · Phase 1 · Feb 2026 — Numbering
              strip. Owner Rule frozen 2026-10-07: the Company Profile
              `invoice_numbering_export_gst` config drives ECI first
              (customer-facing primary); EGSTI inherits ECI.doc_no.
              Auto=ON renders the inheritance-source tile (locked);
              Auto=OFF renders a controlled manual input gated to new
              drafts only (ECI.doc_no becomes immutable post-create). */}
          {!editing && exportNumCfg.auto_generation && (
            <div
              className="tactical-card p-3 flex flex-wrap items-center gap-4 border-sky-500/40 bg-sky-500/5"
              data-testid="eci-numbering-auto-strip"
            >
              <span className="text-[10px] font-mono uppercase tracking-widest text-sky-300 px-2 py-0.5 border border-sky-500/40 rounded-sm">
                Auto · Company Profile
              </span>
              <div className="flex items-baseline gap-2">
                <label className="text-[11px] uppercase tracking-widest text-zinc-400">Series Prefix:</label>
                <span className="font-mono text-sm text-zinc-200" data-testid="eci-numbering-prefix">
                  {exportNumCfg.prefix || "ECI-YY-YY-"}
                </span>
              </div>
              <span className="ml-auto text-[10px] font-mono text-zinc-500" title="ECI.doc_no is allocated server-side and EGSTI inherits it verbatim.">
                EGSTI will inherit this number
              </span>
            </div>
          )}
          {!editing && !exportNumCfg.auto_generation && (
            <div
              className="tactical-card p-3 flex flex-wrap items-center gap-4 border-amber-500/40 bg-amber-500/5"
              data-testid="eci-numbering-manual-strip"
            >
              <span className="text-[10px] font-mono uppercase tracking-widest text-amber-300 px-2 py-0.5 border border-amber-500/40 rounded-sm">
                Manual mode
              </span>
              <label className="text-xs text-zinc-400 whitespace-nowrap">Export Invoice Number *</label>
              <input
                data-testid="eci-manual-invoice-no"
                data-nouppercase
                value={form.doc_no || ""}
                onChange={e => setForm({ ...form, doc_no: e.target.value })}
                placeholder="e.g. EXP-26-27/E/00101"
                className={`${inp} max-w-[280px] font-mono ${!((form.doc_no || "").trim()) ? "border-amber-500/60 ring-1 ring-amber-500/30" : ""}`}
              />
              <span className="ml-auto text-[10px] font-mono text-zinc-500" title="Required by Company Profile · Auto-Generation OFF. EGSTI will inherit this number verbatim.">
                EGSTI will inherit this number
              </span>
            </div>
          )}
          {editing && (
            <div
              className="tactical-card p-3 flex flex-wrap items-center gap-4 border-zinc-500/40 bg-zinc-500/5"
              data-testid="eci-numbering-locked-strip"
            >
              <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-300 px-2 py-0.5 border border-zinc-500/40 rounded-sm">
                Immutable · post-create
              </span>
              <div className="flex items-baseline gap-2">
                <label className="text-[11px] uppercase tracking-widest text-zinc-400">Export Invoice No:</label>
                <span className="font-mono text-sm text-zinc-200" data-testid="eci-locked-invoice-no">
                  {form.doc_no || "—"}
                </span>
              </div>
              <span className="ml-auto text-[10px] font-mono text-zinc-500">
                Locked · cannot be renamed
              </span>
            </div>
          )}

          <div className="grid grid-cols-3 gap-3">
            <F label="Invoice Date"><input type="date" value={form.inv_date} onChange={e => setForm({ ...form, inv_date: e.target.value })} className={inp} /></F>
            <F label="Export SO (auto-copies buyer + terms)" className="col-span-2">
              <Select value={form.export_order_id} onValueChange={pickEso}>
                <SelectTrigger className={selCls}><SelectValue placeholder="Select ESO to prefill" /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)] max-h-64">
                  {/* Mar 1, 2026 · Guard: only Export SOs (doc_no ESO-*) appear here. */}
                  {orders.filter(o => (!o.doc_no || String(o.doc_no).toUpperCase().startsWith("ESO")) && o.order_against !== "stock").map(o => <SelectItem key={o.id} value={o.id}>{o.doc_no} — {o.buyer_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>

            {/* Aug 2026 · EDC picker (1-to-1 rule, mirrors GST Invoice SO→DC).
                Shown once an ESO is picked. Selecting an EDC copies its
                linked EPLs into `packing_list_ids` so the backend
                Commercial-Invoice guard can enforce the double-lock. */}
            {form.export_order_id && (
              <F label={<span>Select EDC · <span className="text-amber-300 font-mono normal-case tracking-normal">1-to-1 · exactly one EDC per Invoice</span></span>} className="col-span-3">
                {ciEdcPicker.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-zinc-500 border border-dashed border-[var(--border)] rounded-sm">
                    No unlinked EDCs for this ESO — create/deliver an EDC first, or an existing EDC is already tied to another Commercial Invoice.
                  </div>
                ) : (
                  <div className="max-h-40 overflow-y-auto border border-[var(--border)] rounded-sm">
                    {ciEdcPicker.map(dc => {
                      const picked = pickedEdcId === dc.id;
                      return (
                        <label key={dc.id} data-testid={`eci-edc-pick-${dc.dc_no}`} className={`flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-[var(--bg-1)]/60 ${picked ? "bg-[var(--accent)]/5" : ""}`}>
                          <input type="radio" name="eci-edc-pick" checked={picked} onChange={() => pickEdc(dc)} className="w-4 h-4 accent-[var(--accent)]" />
                          {/* Feb 2026 · Picker filter is EDC-only server-side, so
                              the raw `dc_no` already reads `EDC-…`. No cosmetic
                              swap needed — that only hid the "why is a domestic
                              DC here?" bug. */}
                          <span className="font-mono text-[var(--accent)]" data-testid={`eci-edc-label-${dc.dc_no}`}>{dc.dc_no}</span>
                          <span className="text-zinc-400 text-xs">{dc.dc_date || "—"}</span>
                          <span className="text-zinc-400 text-xs ml-auto tabular-nums">{Number(dc.quantity || 0).toFixed(2)} MTR</span>
                          <span className="text-zinc-500 text-[10px]">{[dc.vehicle_no, dc.lr_no].filter(Boolean).join(" · ") || ""}</span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </F>
            )}

            <F label="Buyer Name *" className="col-span-2"><input value={form.buyer_name} onChange={e => setForm({ ...form, buyer_name: e.target.value })} className={inp} /></F>
            <F label="FX → INR">
              <div className="flex items-center gap-1">
                <FormattedNumberInput data-nouppercase step="0.01" value={form.fx_rate_to_inr} onChange={e => setForm({ ...form, fx_rate_to_inr: parseFloat(e.target.value) || 0 })} className={inp} />
                <button
                  type="button"
                  data-testid="eci-fx-refresh"
                  onClick={async () => {
                    try {
                      const { data } = await api.get("/fx-rates/current", { params: { currency: form.currency || "USD" } });
                      if (data?.rate_to_inr) {
                        setForm(f => ({ ...f, fx_rate_to_inr: Number(data.rate_to_inr) }));
                        toast.success(`Fetched ${data.currency} @ ₹${data.rate_to_inr}`);
                      } else {
                        toast.info("No FX rate stamped for this currency yet");
                      }
                    } catch (_e) { toast.error("FX Master lookup failed"); }
                  }}
                  title="Refresh from FX Rate Master"
                  className="h-9 px-2 border border-[var(--border)] hover:border-[var(--accent)] text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white rounded-sm whitespace-nowrap"
                >
                  Sync
                </button>
              </div>
            </F>
            <F label="Buyer Address" className="col-span-3"><input data-nouppercase value={form.buyer_address} onChange={e => setForm({ ...form, buyer_address: e.target.value })} className={inp} /></F>

            <F label="Consignee" className="col-span-2"><input value={form.consignee_name} onChange={e => setForm({ ...form, consignee_name: e.target.value })} className={inp} /></F>
            <F label="Final Destination"><input value={form.country_of_final_destination} onChange={e => setForm({ ...form, country_of_final_destination: e.target.value })} className={inp} /></F>

            <F label="Incoterms">
              <Select value={form.incoterms} onValueChange={v => setForm({ ...form, incoterms: v })}>
                <SelectTrigger className={selCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  {INCOTERMS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="Port of Loading"><input value={form.port_of_loading} onChange={e => setForm({ ...form, port_of_loading: e.target.value })} className={inp} /></F>
            <F label="Port of Discharge"><input value={form.port_of_discharge} onChange={e => setForm({ ...form, port_of_discharge: e.target.value })} className={inp} /></F>
          </div>

          <div className="mt-3">
            <div className="flex justify-between items-center mb-2 gap-3 flex-wrap">
              <div className="overline">Line Items · USD</div>
              {/* A1-D03 P2.2 · Feb 2026 — Add Line against existing EDC source.
                  Dropdown pulls from `eciSourceCatalog` populated by the EDC
                  prefill; preserves source genealogy + prevents off-ledger
                  product entry (Owner §2). */}
              <div className="flex items-center gap-2">
                <Select value={addLineSrcId} onValueChange={setAddLineSrcId}>
                  <SelectTrigger data-testid="eci-addline-src-select" className={selCls + " h-8 min-w-[260px]"}>
                    <SelectValue placeholder={eciSourceCatalog.length ? "Select source EDC line…" : "Pick EDC above to enable"} />
                  </SelectTrigger>
                  <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                    {eciSourceCatalog.map(c => (
                      <SelectItem key={c.key} value={c.key}>
                        {c.source_edc_no} · {c.article_no} · {Number(c.shipped_qty).toFixed(2)} {c.uom}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <button
                  data-testid="eci-add-line-btn"
                  onClick={addItem}
                  disabled={!addLineSrcId}
                  className={"text-xs h-8 px-3 rounded-sm border border-[var(--border)] flex items-center gap-1 " + (addLineSrcId ? "text-white hover:bg-[var(--bg-2)]" : "text-zinc-600 cursor-not-allowed opacity-60")}
                >
                  <PlusIcon size={11} /> Add Line
                </button>
              </div>
            </div>
            <div className="border border-[var(--border)] rounded-sm overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-[var(--border)] text-zinc-500">
                    <th className="text-left px-2 py-2 w-6">#</th>
                    <th className="text-left px-2 py-2 min-w-[220px]">Article · Source EDC</th>
                    <th className="text-left px-2 py-2 min-w-[100px]">HS Code</th>
                    <th className="text-right px-2 py-2 min-w-[110px]">Qty</th>
                    <th className="text-left px-2 py-2 min-w-[60px]">UOM</th>
                    <th className="text-right px-2 py-2 min-w-[100px]">Rate USD</th>
                    <th className="text-left px-2 py-2 min-w-[100px]">Type</th>
                    <th className="text-left px-2 py-2 min-w-[200px]">FOC Reason / Remarks</th>
                    <th className="text-right px-2 py-2 min-w-[110px]">Amount USD</th>
                    <th className="w-8"></th>
                  </tr>
                </thead>
                <tbody>
                  {(form.items || []).map((it, i) => {
                    const _lt = (it.line_type || "Normal");
                    const _isFoc = _lt === "FOC";
                    const _hints = (lineHints[i] || []);
                    return (
                    <tr key={i} data-testid={`eci-line-row-${i}`} className={"border-b border-[var(--border)] " + (_isFoc ? "bg-amber-500/5" : "")}>
                      <td className="p-1 text-center text-zinc-500 font-mono">{i + 1}</td>
                      <td className="p-1" style={{ minWidth: "260px" }}>
                        <div data-testid={`eci-line-article-${i}`} className="font-mono text-[11px] text-white truncate">{it.article_no || "—"}</div>
                        <div className="text-[10px] text-zinc-500 font-mono truncate" title={it.description || ""}>
                          {it.source_edc_no ? <span className="text-sky-300">src {it.source_edc_no}</span> : <span className="text-amber-400">no source</span>}
                          {it.description ? ` · ${it.description}` : ""}
                        </div>
                      </td>
                      <td className="p-1 font-mono text-zinc-300">{it.hs_code || "—"}</td>
                      <td className="p-1">
                        <FormattedNumberInput
                          data-testid={`eci-line-qty-${i}`}
                          data-nouppercase max="9999999" step="1"
                          value={it.qty}
                          onChange={e => setItem(i, "qty", Math.max(0, Math.min(9999999, parseFloat(e.target.value) || 0)))}
                          title={it.shipped_qty ? `EDC shipped ${Number(it.shipped_qty).toFixed(2)} ${it.uom || ""} · splits across Normal + FOC must sum to EDC qty` : "Qty > 0"}
                          className={inp + " h-8 text-right font-mono tabular-nums"}
                        />
                      </td>
                      <td className="p-1 font-mono text-zinc-300">{it.uom || "—"}</td>
                      <td className="p-1">
                        <FormattedNumberInput
                          data-testid={`eci-line-rate-${i}`}
                          data-nouppercase step="0.0001"
                          value={it.unit_price_usd}
                          disabled={_isFoc}
                          onChange={e => setItem(i, "unit_price_usd", parseFloat(e.target.value) || 0)}
                          title={_isFoc ? "FOC lines are pinned to Rate = 0.00" : "Rate must be > 0 on Normal lines"}
                          className={inp + " h-8 text-right font-mono" + (_isFoc ? " opacity-70 cursor-not-allowed" : "")}
                        />
                      </td>
                      <td className="p-1">
                        <Select value={_lt} onValueChange={v => setItem(i, "line_type", v)}>
                          <SelectTrigger data-testid={`eci-line-type-${i}`} className={selCls + " h-8"}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                            <SelectItem value="Normal">Normal</SelectItem>
                            <SelectItem value="FOC">FOC · Free of Charge</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-1">
                        <input
                          data-testid={`eci-line-remarks-${i}`}
                          data-nouppercase
                          value={it.line_remarks || ""}
                          onChange={e => setItem(i, "line_remarks", e.target.value)}
                          placeholder={_isFoc ? "Mandatory FOC reason (≥ 4 chars)" : "Optional remarks"}
                          className={inp + " h-8" + (_isFoc && (it.line_remarks || "").trim().replace(/\s+/g, "").length < 4 ? " border-amber-500/60" : "")}
                        />
                      </td>
                      <td className="p-1 text-right">
                        <div className={"font-mono " + (_isFoc ? "text-amber-400" : "text-white")}>
                          {Number(it.amount_usd || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          {_isFoc && <span data-testid={`eci-line-foc-tag-${i}`} className="ml-1 text-[9px] uppercase tracking-widest text-amber-400 font-semibold">FOC</span>}
                        </div>
                        {_hints.length > 0 && (
                          <div data-testid={`eci-line-hint-${i}`} className="text-[9px] text-rose-400 mt-0.5 text-left">
                            {_hints[0]}
                          </div>
                        )}
                      </td>
                      <td className="p-1"><button onClick={() => rmItem(i)} className="text-zinc-500 hover:text-red-400"><TrashIcon size={12} /></button></td>
                    </tr>
                    );
                  })}
                  {(form.items || []).length === 0 && <tr><td colSpan={10} className="py-6 text-center text-zinc-500">No line items — pick an EDC above or Add Line from a source</td></tr>}
                  {(form.items || []).length > 0 && (
                    <tr data-testid="eci-line-total-row" className="border-t-2 border-[var(--border)] bg-[var(--bg-2)]/60 font-semibold">
                      <td className="p-1" colSpan={3}><span className="text-[10px] uppercase tracking-widest text-zinc-400">TOTAL</span></td>
                      <td className="p-1 text-right font-mono text-white" data-testid="eci-total-qty">
                        {gridTotals.mixed ? "— (mixed UOMs)" : Number(gridTotals.totalQty).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="p-1 font-mono text-zinc-300" data-testid="eci-total-uom">{gridTotals.mixed ? "" : (gridTotals.totalUom || "—")}</td>
                      <td className="p-1" colSpan={3}></td>
                      <td className="p-1 text-right font-mono text-emerald-400" data-testid="eci-total-value">
                        {"$" + Number(gridTotals.totalValue).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </td>
                      <td className="p-1"></td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            {/* A1-D03 P2.2 · Feb 2026 — Live per-source reconciliation ledger.
                Each row shows EDC shipped qty vs. Σ(Normal+FOC) with the
                remaining difference. Owner Q3-B requires strict equality
                at Save/Submit/Authorise; the strip surfaces the gap
                live so the operator can rebalance before saving. */}
            {eciRecon.length > 0 && (
              <div className="mt-2 border border-[var(--border)] rounded-sm bg-[var(--bg-2)]/40">
                <div className="px-3 py-1.5 border-b border-[var(--border)] overline text-zinc-400">Source EDC reconciliation · Σ(Normal+FOC) must equal EDC qty</div>
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="text-zinc-500">
                      <th className="text-left px-2 py-1">Source EDC</th>
                      <th className="text-left px-2 py-1">Article · Line</th>
                      <th className="text-right px-2 py-1">EDC Qty</th>
                      <th className="text-right px-2 py-1">Normal</th>
                      <th className="text-right px-2 py-1">FOC</th>
                      <th className="text-right px-2 py-1">Allocated</th>
                      <th className="text-right px-2 py-1">Diff</th>
                      <th className="text-left px-2 py-1">UOM</th>
                    </tr>
                  </thead>
                  <tbody>
                    {eciRecon.map(r => {
                      const _mixed = r.uoms.length > 1;
                      const _balanced = !_mixed && Number(r.diff) === 0;
                      return (
                        <tr key={r.key} data-testid={`eci-recon-row-${r.source_edc_line_id}`} className="border-t border-[var(--border)]/60">
                          <td className="px-2 py-1 font-mono text-sky-300">{r.source_edc_no || "—"}</td>
                          <td className="px-2 py-1 font-mono text-zinc-300 truncate">{r.article_no} <span className="text-zinc-500">· {r.source_edc_line_id}</span></td>
                          <td className="px-2 py-1 text-right font-mono text-white">{Number(r.shipped_qty).toFixed(2)}</td>
                          <td className="px-2 py-1 text-right font-mono text-white">{Number(r.normal_qty).toFixed(2)}</td>
                          <td className="px-2 py-1 text-right font-mono text-amber-400">{Number(r.foc_qty).toFixed(2)}</td>
                          <td className="px-2 py-1 text-right font-mono text-zinc-200">{Number(r.total_qty).toFixed(2)}</td>
                          <td data-testid={`eci-recon-diff-${r.source_edc_line_id}`} className={"px-2 py-1 text-right font-mono " + (_mixed ? "text-rose-400" : (_balanced ? "text-emerald-400" : "text-rose-400"))}>
                            {_mixed ? "— (mixed UOMs)" : Number(r.diff).toFixed(2)}
                          </td>
                          <td className="px-2 py-1 font-mono text-zinc-400">{_mixed ? r.uoms.join(" / ") : (r.uoms[0] || r.uom || "—")}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="grid grid-cols-4 gap-3 mt-3">
            <F label="Freight USD"><FormattedNumberInput data-nouppercase step="0.01" value={form.freight_usd} onChange={e => setForm({ ...form, freight_usd: parseFloat(e.target.value) || 0 })} className={inp} /></F>
            <F label="Insurance USD"><FormattedNumberInput data-nouppercase step="0.01" value={form.insurance_usd} onChange={e => setForm({ ...form, insurance_usd: parseFloat(e.target.value) || 0 })} className={inp} /></F>
            <F label="Other USD"><FormattedNumberInput data-nouppercase step="0.01" value={form.other_charges_usd} onChange={e => setForm({ ...form, other_charges_usd: parseFloat(e.target.value) || 0 })} className={inp} /></F>
            <F label="Grand Total USD"><div className="h-9 px-3 flex items-center bg-[var(--bg-2)] rounded-sm font-mono font-semibold text-white" data-testid="eci-total-usd">${Number(totalUsd).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></F>

            <F label="GST Treatment">
              <Select value={form.gst_type} onValueChange={v => setForm({ ...form, gst_type: v })}>
                <SelectTrigger className={selCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  {GST_TYPES.map(s => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="LUT No"><input value={form.lut_no} onChange={e => setForm({ ...form, lut_no: e.target.value })} className={inp} /></F>
            <F label="LUT Date"><input type="date" value={form.lut_date} onChange={e => setForm({ ...form, lut_date: e.target.value })} className={inp} /></F>
            <F label="Drawback %"><FormattedNumberInput data-nouppercase step="0.01" value={form.drawback_pct} onChange={e => setForm({ ...form, drawback_pct: parseFloat(e.target.value) || 0 })} className={inp} /></F>

            <F label="Payment Terms">
              <Select value={form.payment_terms} onValueChange={v => setForm({ ...form, payment_terms: v })}>
                <SelectTrigger className={selCls}><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[var(--bg-1)] border-[var(--border)]">
                  {PAY_TERMS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </F>
            <F label="LC No"><input value={form.lc_no} onChange={e => setForm({ ...form, lc_no: e.target.value })} className={inp} /></F>
            <F label="LC Date"><input type="date" value={form.lc_date} onChange={e => setForm({ ...form, lc_date: e.target.value })} className={inp} /></F>
            <F label="Drawback Scheme"><input value={form.drawback_scheme} onChange={e => setForm({ ...form, drawback_scheme: e.target.value })} className={inp} /></F>
          </div>

          <div className="mt-4 p-4 bg-[var(--bg-2)] rounded-sm">
            <div className="overline mb-2">USD Commercial Summary · ECI</div>
            <div className="grid grid-cols-4 gap-3 text-sm mb-3">
              <div><div className="text-[10px] text-zinc-500 uppercase">FOB Value USD</div><div className="font-mono text-white" data-testid="eci-fob-usd">${Number(fobUsd).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
              <div><div className="text-[10px] text-zinc-500 uppercase">Freight USD</div><div className="font-mono text-white">${Number(form.freight_usd || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
              <div><div className="text-[10px] text-zinc-500 uppercase">Insurance USD</div><div className="font-mono text-white">${Number(form.insurance_usd || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
              <div><div className="text-[10px] text-zinc-500 uppercase">Grand Total USD</div><div className="font-mono font-semibold text-emerald-400" data-testid="eci-grand-usd">${Number(totalUsd).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
              <div><div className="text-[10px] text-zinc-500 uppercase">IGST %</div><div className="font-mono text-amber-400" data-testid="eci-igst-pct">{Number(igstPct).toFixed(2)}%</div></div>
              <div><div className="text-[10px] text-zinc-500 uppercase">IGST Value USD</div><div className="font-mono text-amber-400" data-testid="eci-igst-usd">${Number(igstUsd).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
            </div>
            <div className="overline mb-2">INR Conversion & Tax</div>
            <div className="grid grid-cols-4 gap-3 text-sm">
              <div><div className="text-[10px] text-zinc-500 uppercase">FOB Value</div><div className="font-mono text-white">₹{fobInr.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div></div>
              <div><div className="text-[10px] text-zinc-500 uppercase">IGST @ {igstPct}%</div><div className="font-mono text-amber-400">₹{igstInr.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div></div>
              <div><div className="text-[10px] text-zinc-500 uppercase">Drawback @ {Number(form.drawback_pct || 0)}%</div><div className="font-mono text-blue-400">₹{drawbackInr.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div></div>
              <div><div className="text-[10px] text-zinc-500 uppercase">Total INR</div><div className="font-mono font-semibold text-emerald-400" data-testid="eci-total-inr">₹{totalInr.toLocaleString("en-IN", { maximumFractionDigits: 2 })}</div></div>
            </div>
          </div>

          <F label="Remarks" className="mt-3"><input data-nouppercase value={form.remarks} onChange={e => setForm({ ...form, remarks: e.target.value })} className={inp} /></F>

          {/* Phase UI-EPI/ECI.2 · Order Reference · Delivery · Shipping Marks
              — visual + field parity with `PiEditor`'s sky Order Reference
              section. Buyer PO / PO Date come from the ECI-prefilled ESO,
              so we surface them read-only where already populated but the
              remaining fields are freely editable at ECI stage. */}
          <div className="border border-sky-500/30 rounded-sm p-3 bg-sky-500/5 space-y-3 mt-3">
            <div className="text-[11px] font-mono uppercase tracking-widest text-sky-300">Order Reference · Delivery · Shipping Marks</div>
            <div className="grid grid-cols-4 gap-3">
              <F label="Buyer PO No"><input data-testid="eci-buyer-po-no" data-nouppercase value={form.buyer_po_no || ""} onChange={e => setForm({ ...form, buyer_po_no: e.target.value })} className={inp} placeholder="e.g. PO/2026/00123" /></F>
              <F label="Buyer PO Date"><input data-testid="eci-buyer-po-date" type="date" value={form.buyer_po_date || ""} onChange={e => setForm({ ...form, buyer_po_date: e.target.value })} className={inp} /></F>
              <F label="Delivery Location"><input data-testid="eci-delivery-location" data-nouppercase value={form.delivery_location || ""} onChange={e => setForm({ ...form, delivery_location: e.target.value })} placeholder="e.g. Buyer Warehouse · Hamburg" className={inp} /></F>
              <F label="Delivery Address"><textarea data-testid="eci-delivery-address" data-nouppercase rows="1" value={form.delivery_address || ""} onChange={e => setForm({ ...form, delivery_address: e.target.value })} placeholder="Multi-line address" className="w-full min-h-[36px] px-3 py-1.5 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm resize-y" /></F>
            </div>
            <div className="grid grid-cols-4 gap-3">
              <F label="Payment Terms" className="col-span-2"><input data-testid="eci-payment-terms-txt" data-nouppercase value={form.payment_terms || ""} onChange={e => setForm({ ...form, payment_terms: e.target.value })} placeholder="e.g. 30% Advance · 70% against BL" className={inp} /></F>
              <F label="Credit Days"><input data-testid="eci-credit-days" type="number" min="0" value={form.credit_days ?? 0} onChange={e => setForm({ ...form, credit_days: Math.max(0, parseInt(e.target.value) || 0) })} className={inp} /></F>
            </div>
            <div className="grid grid-cols-5 gap-3">
              <F label="Port of Destination"><input data-testid="eci-mark-port-dest" data-nouppercase value={form.port_of_discharge || ""} onChange={e => setForm({ ...form, port_of_discharge: e.target.value })} placeholder="e.g. Hamburg" className={inp} /></F>
              <F label="Country of Final Destination"><input data-testid="eci-mark-country" data-nouppercase value={form.country_of_final_destination || ""} onChange={e => setForm({ ...form, country_of_final_destination: e.target.value })} placeholder="e.g. Germany" className={inp} /></F>
              <F label="Carton No · From"><input data-testid="eci-mark-carton-from" data-nouppercase value={form.carton_no_from || ""} onChange={e => setForm({ ...form, carton_no_from: e.target.value })} placeholder="e.g. 1/10" className={inp} /></F>
              <F label="Carton No · To"><input data-testid="eci-mark-carton-to" data-nouppercase value={form.carton_no_to || ""} onChange={e => setForm({ ...form, carton_no_to: e.target.value })} placeholder="e.g. 10/10" className={inp} /></F>
              <F label="Made In"><input data-testid="eci-mark-made-in" value={form.made_in || "MADE IN INDIA"} onChange={e => setForm({ ...form, made_in: e.target.value.toUpperCase() })} className={inp} /></F>
            </div>
          </div>

          {/* Phase UI-EPI/ECI.1 · Shipping / Packing Details — declared
              commercial values. Blank allowed; Gross must be >= Net when
              both supplied. Matches the New Export PI packing section. */}
          <div className="border border-[var(--border)] rounded-sm p-3 bg-[var(--bg-2)]/30 space-y-3 mt-3">
            <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-400">Shipping / Packing Details</div>
            <div className="grid grid-cols-3 gap-3">
              {/* A2 · Phase 2 (Oct 2026) — Number of Cartons is now
                  AUTHORITATIVELY derived from the linked EPL `boxes[]`
                  at backend ECI create time (eci.py §A2). Manual entry
                  is disabled on new ECIs so operators cannot introduce
                  a value that contradicts the EPL source of truth. The
                  field is kept visible as a read-only preview; the live
                  backend aggregate wins at Save. */}
              <F label="Number of Cartons · from EPL"><FormattedNumberInput data-testid="eci-cartons" data-nouppercase step="1" min="0" readOnly disabled value={form.number_of_cartons || 0} className={inp + " opacity-70 cursor-not-allowed"} title="Derived from the linked EPL boxes — read-only (A2 Owner rule, Oct 2026)." /></F>
              <F label="Net Weight (kg)"><FormattedNumberInput data-testid="eci-net-weight" data-nouppercase step="0.001" min="0" value={form.net_weight || 0} onChange={e => setForm({ ...form, net_weight: Math.max(0, parseFloat(e.target.value) || 0) })} className={inp} /></F>
              <F label="Gross Weight (kg)"><FormattedNumberInput data-testid="eci-gross-weight" data-nouppercase step="0.001" min="0" value={form.gross_weight || 0} onChange={e => setForm({ ...form, gross_weight: Math.max(0, parseFloat(e.target.value) || 0) })} className={inp} /></F>
            </div>
            {Number(form.gross_weight || 0) > 0 && Number(form.net_weight || 0) > 0 && Number(form.gross_weight || 0) < Number(form.net_weight || 0) && (
              <div data-testid="eci-weight-error" className="text-[11px] text-red-400 font-mono">Gross Weight cannot be less than Net Weight.</div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <button data-testid="eci-cancel" onClick={() => setOpen(false)} className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-xs font-mono uppercase tracking-widest rounded-sm">Cancel</button>
            {/* Feb 2026 · Fix: Print/PDF button surfaced inside the Edit
                dialog so operators don't need to Cancel out and hunt for
                the tiny row-level icon. Only rendered when the invoice
                exists (Edit mode) — Drafts have no persisted id yet. */}
            {editing && form.doc_no && (
              <button data-testid="eci-edit-print-html" onClick={() => printHtml(editing)} className="h-9 px-4 border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 text-xs font-mono uppercase tracking-widest rounded-sm flex items-center gap-2">
                <PrinterIcon size={12} weight="bold" /> Browser Print
              </button>
            )}
            <button data-testid="save-eci" onClick={() => save(false)} className="h-9 px-4 border border-[var(--accent)] text-[var(--accent)] hover:bg-[var(--accent)]/10 text-xs font-mono uppercase tracking-widest rounded-sm">{editing ? "Update Invoice" : "Save as Draft"}</button>
            {!editing && perms.submit && (
              <button data-testid="eci-submit-authorisation" onClick={() => save(true)} className="h-9 px-4 bg-[var(--accent)] hover:bg-blue-600 text-white text-xs font-mono uppercase tracking-widest rounded-sm">Submit for Authorisation</button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Feb 2026 · Read-only View dialog for the ECI Actions column. */}
      <Dialog open={!!viewing} onOpenChange={(v) => { if (!v) { setViewing(null); setAmendPrompt(false); setAmendReason(""); setAmendClass("A"); setCancelDocPrompt(false); setCancelDocReason(""); } }}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white w-[min(98vw,1920px)] max-w-[min(98vw,1920px)] max-h-[95vh] overflow-y-auto overflow-x-hidden" data-testid="eci-view-dialog" description="Export document action — Proforma / Packing List / Sales Order / Delivery Challan.">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-3">
              <span>Commercial Invoice · {viewing?.doc_no}{Number(viewing?.amendment_no || 0) > 0 && (<span data-testid={`eci-view-rev-badge-${viewing?.doc_no}`} className="ml-2 text-[11px] font-mono px-1.5 py-0.5 rounded-sm border border-amber-500/40 text-amber-300 bg-amber-500/10 tracking-widest">R{viewing?.amendment_no}</span>)}</span>
              <span className="text-[10px] font-mono px-2 py-0.5 rounded-sm bg-[var(--bg-2)] text-zinc-400 tracking-widest uppercase">VIEW</span>
              {/* ECI-MANAGEMENT-AUTH-CONTROL · Phase 2 · Feb 2026 —
                  View mode surfaces the current Path B state and (in
                  the footer) the legitimate next lifecycle action via
                  the SAME `submitForApproval` handler used by the row. */}
              {viewing && (
                <span data-testid={`eci-view-approval-badge-${viewing.doc_no}`}
                      className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border ${ECI_APPROVAL_TONE[eciState(viewing)] || ""}`}>
                  {ECI_APPROVAL_LABEL[eciState(viewing)] || eciState(viewing)}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-4 gap-3">
                <div><div className="overline mb-0.5">Invoice Date</div><div className="font-mono">{viewing.inv_date}</div></div>
                <div><div className="overline mb-0.5">Export SO</div><div className="font-mono">{viewing.export_order_no || "—"}</div></div>
                {/* DP-ECI-PRINT-EDC-LINK-01 · Phase 2 · Mar 2026 — operational
                    linkage tiles. Values come from the backend's shared
                    `resolve_eci_link_refs` (see routes/exports/_core.py) so
                    View Mode and Browser Print never diverge. Historical
                    reversed EDCs remain preserved in `delivery_challans` but
                    are excluded here (ECI-CANCEL-H01 seal untouched). */}
                <div data-testid={`eci-view-linked-epls-${viewing.doc_no}`}>
                  <div className="overline mb-0.5">Linked Packing List(s)</div>
                  <div className="font-mono">{(viewing.linked_epl_nos || []).length ? (viewing.linked_epl_nos || []).join(", ") : "—"}</div>
                </div>
                <div data-testid={`eci-view-linked-edc-${viewing.doc_no}`}>
                  <div className="overline mb-0.5">Linked Delivery Challan</div>
                  <div className="font-mono">{viewing.linked_edc_no || "—"}</div>
                </div>
                <div><div className="overline mb-0.5">Currency</div><div className="font-mono">{viewing.currency || "USD"} @ ₹{Number(viewing.fx_rate_to_inr || 0).toFixed(2)}</div></div>
                <div><div className="overline mb-0.5">Incoterms</div><div className="font-mono">{viewing.incoterms || "—"}</div></div>
                <div className="col-span-2"><div className="overline mb-0.5">Buyer</div><div className="text-white">{viewing.buyer_name || "—"}</div><div className="text-[11px] text-zinc-400 truncate">{viewing.buyer_address || ""}</div></div>
                <div className="col-span-2"><div className="overline mb-0.5">Consignee</div><div className="text-white">{viewing.consignee_name || "—"}</div><div className="text-[11px] text-zinc-400 truncate">{viewing.consignee_address || ""}</div></div>
                <div><div className="overline mb-0.5">Loading Port</div><div className="font-mono">{viewing.port_of_loading || "—"}</div></div>
                <div><div className="overline mb-0.5">Discharge Port</div><div className="font-mono">{viewing.port_of_discharge || "—"}</div></div>
                <div><div className="overline mb-0.5">Country of Origin</div><div className="font-mono">{viewing.country_of_origin || "—"}</div></div>
                <div><div className="overline mb-0.5">Country of Destination</div><div className="font-mono">{viewing.country_of_final_destination || "—"}</div></div>
                <div><div className="overline mb-0.5">Payment</div><div className="font-mono">{viewing.payment_terms || "—"} {viewing.lc_no ? `· ${viewing.lc_no}` : ""}</div></div>
                <div><div className="overline mb-0.5">GST</div><div className="font-mono">{viewing.gst_type || "—"}</div></div>
                <div><div className="overline mb-0.5">Drawback</div><div className="font-mono">{viewing.drawback_scheme || "—"} · {Number(viewing.drawback_pct || 0).toFixed(2)}%</div></div>
                <div><div className="overline mb-0.5">LUT</div><div className="font-mono">{viewing.lut_no || "—"} {viewing.lut_date ? `· ${viewing.lut_date}` : ""}</div></div>
              </div>
              <DocumentInfo docNo={viewing.doc_no} createdAt={viewing.created_at} updatedAt={viewing.updated_at} createdBy={viewing.created_by} docLabel="Invoice No." testidPrefix="eci" className="" />
              <div>
                <div className="overline mb-2">Line Items · {(viewing.items || []).length}</div>
                <div className="border border-[var(--border)] rounded-sm overflow-x-auto max-h-[40vh] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-[var(--bg-2)]">
                      <tr className="uppercase tracking-widest text-[10px] text-zinc-500">
                        <th className="text-left px-2 py-2">Product Code</th>
                        <th className="text-left px-2 py-2">Style No</th>
                        <th className="text-left px-2 py-2">HS</th>
                        <th className="text-right px-2 py-2">Qty</th>
                        <th className="text-left px-2 py-2">UOM</th>
                        <th className="text-right px-2 py-2">Unit $</th>
                        <th className="text-left px-2 py-2">Type</th>
                        <th className="text-left px-2 py-2">Remarks / FOC Reason</th>
                        <th className="text-right px-2 py-2">Amount $</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(viewing.items || []).map((it, i) => {
                        const _lt = it.line_type || "Normal";
                        const _isFoc = _lt === "FOC";
                        return (
                        <tr key={i} className={"border-t border-[var(--border)] " + (_isFoc ? "bg-amber-500/5" : "")} data-testid={`eci-view-item-${i}`}>
                          <td className="px-2 py-1 font-mono">{it.article_no || "—"}</td>
                          <td className="px-2 py-1 font-mono" data-testid={`eci-view-item-style-${i}`}>{it.style_no || eciStyles[i] || "—"}</td>
                          <td className="px-2 py-1 font-mono text-zinc-400">{it.hs_code || "—"}</td>
                          <td className="px-2 py-1 text-right font-mono">{Number(it.qty || 0).toFixed(2)}</td>
                          <td className="px-2 py-1 font-mono text-zinc-400">{it.uom || "—"}</td>
                          <td className="px-2 py-1 text-right font-mono">${Number(it.unit_price_usd || 0).toFixed(4)}</td>
                          <td className="px-2 py-1" data-testid={`eci-view-type-${i}`}>
                            {_isFoc
                              ? <span className="text-[10px] font-semibold tracking-widest text-amber-400 px-1.5 py-0.5 border border-amber-500/40 rounded-sm">FOC</span>
                              : <span className="text-[10px] font-mono text-zinc-400">Normal</span>}
                          </td>
                          <td className="px-2 py-1 font-mono text-zinc-300 truncate" data-testid={`eci-view-remarks-${i}`}>{it.line_remarks || "—"}</td>
                          <td className={"px-2 py-1 text-right font-mono " + (_isFoc ? "text-amber-400" : "")}>${Number(it.amount_usd || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        </tr>
                        );
                      })}
                      {(viewing.items || []).length === 0 && <tr><td colSpan={9} className="py-8 text-center text-zinc-500">No line items</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div className="tactical-card p-3"><div className="overline mb-0.5">Freight $</div><div className="font-mono text-white">${Number(viewing.freight_usd || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
                <div className="tactical-card p-3"><div className="overline mb-0.5">Insurance $</div><div className="font-mono text-white">${Number(viewing.insurance_usd || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
                <div className="tactical-card p-3"><div className="overline mb-0.5">Other $</div><div className="font-mono text-white">${Number(viewing.other_charges_usd || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
                <div className="tactical-card p-3"><div className="overline mb-0.5">Total USD</div><div className="font-mono text-white text-lg" data-testid="eci-view-total-usd">${Number(viewing.total_usd || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div></div>
                <div className="tactical-card p-3 col-span-2"><div className="overline mb-0.5">Total INR</div><div className="font-mono text-emerald-400 text-lg" data-testid="eci-view-total-inr">₹{Number(viewing.total_inr || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</div></div>
                <div className="tactical-card p-3"><div className="overline mb-0.5">IGST ₹</div><div className="font-mono text-white">₹{Number(viewing.igst_inr || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</div></div>
                <div className="tactical-card p-3"><div className="overline mb-0.5">Drawback ₹</div><div className="font-mono text-white">₹{Number(viewing.drawback_inr || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 })}</div></div>
              </div>
              {viewing.remarks && <div><div className="overline mb-0.5">Remarks</div><div className="text-zinc-300">{viewing.remarks}</div></div>}

              {/* ECI-AMEND-H01 · Phase 2 · Feb 2026 — Amendment
                  History panel. Reads `viewing.amendments[]`. */}
              {(viewing.amendments || []).length > 0 && (
                <div data-testid="eci-amendment-history" className="border border-amber-500/20 rounded-sm p-3 bg-amber-500/[0.03]">
                  <div className="overline mb-2 text-amber-300">Amendment History · {(viewing.amendments || []).length}</div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="uppercase tracking-widest text-[10px] text-zinc-500 border-b border-[var(--border)]">
                          <th className="text-left px-2 py-1.5">Rev</th>
                          <th className="text-left px-2 py-1.5">Class</th>
                          <th className="text-left px-2 py-1.5">Reason</th>
                          <th className="text-left px-2 py-1.5">Raised by / at</th>
                          <th className="text-left px-2 py-1.5">Re-authorised</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(viewing.amendments || []).map((a, i) => (
                          <tr key={i} className="border-b border-[var(--border)]/60" data-testid={`eci-amendment-row-${a.amendment_no}`}>
                            <td className="px-2 py-1.5 font-mono text-amber-300">R{a.amendment_no}</td>
                            <td className="px-2 py-1.5 font-mono">{a.amendment_class || "—"}</td>
                            <td className="px-2 py-1.5 text-zinc-300">{a.reason || "—"}</td>
                            <td className="px-2 py-1.5 font-mono text-[11px] text-zinc-400">{a.raised_by || "—"}<br/>{(a.raised_at || "").slice(0, 19)}</td>
                            <td className="px-2 py-1.5 font-mono text-[11px] text-zinc-400">{a.reauthorised_by ? (<><span className="text-emerald-400">{a.reauthorised_by}</span><br/>{(a.reauthorised_at || "").slice(0, 19)}</>) : (i === (viewing.amendments || []).length - 1 && isEciRejected(viewing) ? <span className="text-red-400">Rejected</span> : (i === (viewing.amendments || []).length - 1 && (isEciNotSubmitted(viewing) || isEciPending(viewing)) ? <span className="text-sky-400">Pending re-authorisation</span> : "—"))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {amendPrompt && perms.edit && isEciAuthorised(viewing) && (viewing.status || "").toLowerCase() !== "cancelled" && (
                <div className="border border-amber-500/40 rounded-sm p-3 bg-amber-500/[0.05] space-y-3" data-testid="eci-amend-prompt">
                  <div className="overline text-amber-300">Raise Amendment · Authorised ECI {viewing.doc_no}</div>
                  <div>
                    <div className="overline mb-1.5">Amendment Class (required)</div>
                    <div className="flex flex-wrap gap-3 text-xs">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio" name="eci-amend-class"
                          data-testid="eci-amend-class-a"
                          checked={amendClass === "A"}
                          onChange={() => setAmendClass("A")}
                          className="w-4 h-4 accent-amber-400"
                        />
                        <span><span className="font-mono text-amber-300">A</span> · Packing / Logistics (cartons, weights, marks, ports)</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio" name="eci-amend-class"
                          data-testid="eci-amend-class-b"
                          checked={amendClass === "B"}
                          onChange={() => setAmendClass("B")}
                          className="w-4 h-4 accent-amber-400"
                        />
                        <span><span className="font-mono text-amber-300">B</span> · Commercial (qty / rate / GST / LC / parties)</span>
                      </label>
                    </div>
                  </div>
                  <div>
                    <div className="overline mb-1.5">Amendment Reason (required · ≥ 4 chars)</div>
                    <textarea
                      data-testid="eci-amend-reason"
                      value={amendReason}
                      onChange={e => setAmendReason(e.target.value)}
                      className="w-full h-20 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm px-3 py-2 text-sm text-white"
                      placeholder="e.g. LC amendment received · unit price revised on line 1"
                    />
                  </div>
                </div>
              )}

              {/* ECI-CANCEL-H01 · Phase 2 · Mar 2026 — inline
                  Cancellation Request prompt. Distinct from the
                  existing dialog-dismiss "Cancel" buttons — this one
                  cancels the DOCUMENT (issues a Cancellation Request
                  that enters Management Authorisation). */}
              {cancelDocPrompt && perms.cancel && isEciAuthorised(viewing) && (viewing.status || "").toLowerCase() !== "cancelled" && (
                <div className="border border-red-500/40 rounded-sm p-3 bg-red-500/[0.05] space-y-3" data-testid="eci-cancel-doc-prompt">
                  <div className="overline text-red-300">Request ECI Cancellation · Authorised ECI {viewing.doc_no}</div>
                  <div className="text-xs text-zinc-400 leading-relaxed">
                    This issues a <span className="text-red-300 font-semibold">Cancellation Request</span> that Management must Authorise before the ECI is Cancelled. The ECI number ({viewing.doc_no}) will be preserved permanently for audit. Reject != Cancel != Amend — this action does not delete the document, does not alter its commercial content until Management Authorises.
                  </div>
                  <div>
                    <div className="overline mb-1.5">Cancellation Reason (required · &gt;= 4 chars)</div>
                    <textarea
                      data-testid="eci-cancel-doc-reason"
                      value={cancelDocReason}
                      onChange={e => setCancelDocReason(e.target.value)}
                      className="w-full h-20 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm px-3 py-2 text-sm text-white"
                      placeholder="e.g. duplicate ECI · buyer rescinded the shipment order"
                    />
                  </div>
                </div>
              )}
              {/* ECI-CANCEL-H01-UAT-02G · Mar 2026 — inline Draft
                  Invalidation prompt. Distinct from cancel-request:
                  invalidate applies to Draft (Not Submitted) or
                  Rejected ECIs that never reached Authorised. */}
              {invalidatePrompt && perms.cancel && (isEciNotSubmitted(viewing) || isEciRejected(viewing)) && (
                <div className="border border-fuchsia-500/40 rounded-sm p-3 bg-fuchsia-500/[0.05] space-y-3" data-testid="eci-invalidate-prompt">
                  <div className="overline text-fuchsia-300">Invalidate Draft ECI · {viewing.doc_no}</div>
                  <div className="text-xs text-zinc-400 leading-relaxed">
                    Invalidate declares this Draft/Rejected ECI <span className="text-fuchsia-300 font-semibold">unusable</span> before it ever reaches Authorised commercial status. Its number ({viewing.doc_no}) is <span className="text-fuchsia-300 font-semibold">permanently reserved</span> — the document is retained for audit and never reused. Linked Packing Lists are released so they can proceed through the correct downstream flow. Invalidate != Cancel (which applies only to Authorised ECIs) and != Delete (retired).
                  </div>
                  <div>
                    <div className="overline mb-1.5">Invalidation Reason (required · &gt;= 4 chars)</div>
                    <textarea
                      data-testid="eci-invalidate-reason"
                      value={invalidateReason}
                      onChange={e => setInvalidateReason(e.target.value)}
                      className="w-full h-20 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm px-3 py-2 text-sm text-white"
                      placeholder="e.g. UAT defect artifact · created via corrected picker · never usable"
                    />
                  </div>
                </div>
              )}
              {/* ECI-CANCEL-H01-UAT-02G · Mar 2026 — Invalidated
                  terminal-state banner in View mode. */}
              {isEciInvalidated(viewing) && (
                <div data-testid="eci-invalidated-banner" className="border border-fuchsia-500/40 rounded-sm p-3 bg-fuchsia-500/[0.08]">
                  <div className="overline text-fuchsia-300">INVALIDATED</div>
                  <div className="text-xs text-zinc-300 mt-1">
                    {viewing.invalidation?.invalidated_by ? <>Invalidated by <span className="text-fuchsia-300">{viewing.invalidation.invalidated_by}</span> at <span className="font-mono">{(viewing.invalidation.invalidated_at || "").slice(0, 19)}</span></> : "Invalidated"}
                    {viewing.invalidation?.reason ? <div className="mt-1 text-zinc-400 italic">"{viewing.invalidation.reason}"</div> : null}
                    <div className="mt-1 text-[10px] text-zinc-500">ECI number retained permanently · linked EPL(s) released for correct downstream flow.</div>
                  </div>
                </div>
              )}
              {/* ECI-CANCEL-H01 · Phase 2 · Mar 2026 — Cancelled
                  terminal-state banner in View mode. */}
              {(viewing.status || "").toLowerCase() === "cancelled" && (
                <div data-testid="eci-cancelled-banner" className="border border-red-500/40 rounded-sm p-3 bg-red-500/[0.08]">
                  <div className="overline text-red-300">CANCELLED</div>
                  <div className="text-xs text-zinc-300 mt-1">
                    {viewing.cancelled?.by ? <>Cancelled by <span className="text-red-300">{viewing.cancelled.by}</span> at <span className="font-mono">{(viewing.cancelled.at || "").slice(0, 19)}</span></> : "Cancelled"}
                    {viewing.cancelled?.reason ? <div className="mt-1 text-zinc-400 italic">"{viewing.cancelled.reason}"</div> : null}
                  </div>
                </div>
              )}
              {/* Cancellation Pending Approval indicator */}
              {(viewing.approval_status || "") === "Cancellation Pending Approval" && (
                <div data-testid="eci-cancel-pending-banner" className="border border-red-500/30 rounded-sm p-3 bg-red-500/[0.05]">
                  <div className="overline text-red-300">CANCELLATION PENDING MANAGEMENT AUTHORISATION</div>
                  <div className="text-xs text-zinc-300 mt-1">
                    Requested by <span className="text-red-300">{viewing.cancellation_request?.requested_by || "—"}</span> at <span className="font-mono">{(viewing.cancellation_request?.requested_at || "").slice(0, 19)}</span>
                    {viewing.cancellation_request?.reason ? <div className="mt-1 text-zinc-400 italic">"{viewing.cancellation_request.reason}"</div> : null}
                  </div>
                </div>
              )}


            </div>
          )}
          <DialogFooter>
            {/* ECI-AMEND-H01 · Phase 2 · Feb 2026 — View-mode Amend
                surface + Confirm/Cancel while the prompt is open.
                Same canonical `raiseAmendment` handler as the row
                pencil (single source of truth). */}
            {viewing && perms.edit && isEciAuthorised(viewing) && (viewing.status || "").toLowerCase() !== "cancelled" && !amendPrompt && (
              <button
                data-testid="eci-amend-btn"
                onClick={() => { setAmendClass("A"); setAmendReason(""); setAmendPrompt(true); }}
                className="px-4 py-2 rounded-sm border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 text-sm font-semibold flex items-center gap-2"
              >
                <PencilSimpleIcon size={14} weight="bold" />
                Raise Amendment
              </button>
            )}
            {viewing && amendPrompt && (
              <>
                <button
                  onClick={() => { setAmendPrompt(false); setAmendReason(""); setAmendClass("A"); }}
                  className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-300 hover:bg-[var(--bg-2)]"
                >
                  Cancel
                </button>
                <button
                  data-testid="eci-amend-confirm"
                  disabled={amendReason.trim().length < 4}
                  onClick={() => raiseAmendment(viewing, amendClass, amendReason)}
                  className="px-4 py-2 rounded-sm bg-amber-500 hover:bg-amber-600 text-sm font-semibold text-white disabled:opacity-40 flex items-center gap-2"
                >
                  <PencilSimpleIcon size={14} weight="bold" />
                  Confirm Amendment
                </button>
              </>
            )}
            {/* ECI-CANCEL-H01 · Phase 2 · Mar 2026 — View-mode
                Request ECI Cancellation button + Confirm/Cancel while
                the prompt is open. Deliberately labelled "Request
                ECI Cancellation" to distinguish from the dialog-
                dismiss "Cancel" button. */}
            {viewing && perms.cancel && isEciAuthorised(viewing) && (viewing.status || "").toLowerCase() !== "cancelled" && !cancelDocPrompt && !amendPrompt && (
              <button
                data-testid="eci-cancel-doc-btn"
                onClick={() => { setCancelDocReason(""); setCancelDocPrompt(true); }}
                className="px-4 py-2 rounded-sm border border-red-500/40 text-red-300 hover:bg-red-500/10 text-sm font-semibold flex items-center gap-2"
                aria-label="Request cancellation of Export Commercial Invoice"
              >
                <ProhibitIcon size={14} weight="bold" />
                Request ECI Cancellation
              </button>
            )}
            {viewing && cancelDocPrompt && (
              <>
                <button
                  onClick={() => { setCancelDocPrompt(false); setCancelDocReason(""); }}
                  className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-300 hover:bg-[var(--bg-2)]"
                >
                  Cancel
                </button>
                <button
                  data-testid="eci-cancel-doc-confirm"
                  disabled={cancelDocReason.trim().length < 4}
                  onClick={() => requestEciCancellation(viewing, cancelDocReason)}
                  className="px-4 py-2 rounded-sm bg-red-500 hover:bg-red-600 text-sm font-semibold text-white disabled:opacity-40 flex items-center gap-2"
                >
                  <ProhibitIcon size={14} weight="bold" />
                  Confirm Cancellation Request
                </button>
              </>
            )}
            {/* ECI-CANCEL-H01-UAT-02G · Mar 2026 — View-mode Invalidate
                surface + Confirm/Cancel while the prompt is open. Only
                visible on Draft (Not Submitted) or Rejected ECIs.
                Deliberately labelled "Invalidate" (not Delete) so
                operators understand the retention semantics. */}
            {viewing && perms.cancel && (isEciNotSubmitted(viewing) || isEciRejected(viewing)) && !invalidatePrompt && !amendPrompt && !cancelDocPrompt && (
              <button
                data-testid="eci-invalidate-btn"
                onClick={() => { setInvalidateReason(""); setInvalidatePrompt(true); }}
                className="px-4 py-2 rounded-sm border border-fuchsia-500/40 text-fuchsia-300 hover:bg-fuchsia-500/10 text-sm font-semibold flex items-center gap-2"
                aria-label="Invalidate Draft Export Commercial Invoice"
              >
                <ProhibitIcon size={14} weight="bold" />
                Invalidate ECI
              </button>
            )}
            {viewing && invalidatePrompt && (
              <>
                <button
                  onClick={() => { setInvalidatePrompt(false); setInvalidateReason(""); }}
                  className="px-4 py-2 rounded-sm border border-[var(--border)] text-sm text-zinc-300 hover:bg-[var(--bg-2)]"
                >
                  Cancel
                </button>
                <button
                  data-testid="eci-invalidate-confirm"
                  disabled={invalidateReason.trim().length < 4}
                  onClick={() => invalidateEci(viewing, invalidateReason)}
                  className="px-4 py-2 rounded-sm bg-fuchsia-500 hover:bg-fuchsia-600 text-sm font-semibold text-white disabled:opacity-40 flex items-center gap-2"
                >
                  <ProhibitIcon size={14} weight="bold" />
                  Confirm Invalidate
                </button>
              </>
            )}
            {/* ECI-MANAGEMENT-AUTH-CONTROL · Phase 2 · Feb 2026 —
                View-mode Submit / Resubmit surface. Uses the SAME
                `submitForApproval(row)` handler as the grid row so
                the UI has a single source of truth. */}
            {viewing && perms.submit && (isEciNotSubmitted(viewing) || isEciRejected(viewing)) && (
              <button
                data-testid={`eci-view-submit-${viewing.doc_no}`}
                onClick={() => submitForApproval(viewing)}
                className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-sm font-semibold text-white flex items-center gap-2"
              >
                <PaperPlaneTiltIcon size={14} weight="bold" />
                {isEciRejected(viewing) ? "Resubmit for Authorisation" : "Submit for Authorisation"}
              </button>
            )}
            <button data-testid="eci-view-print-html" onClick={() => viewing && printHtml(viewing.id)} className="px-4 py-2 rounded-sm border border-sky-500/30 text-sm text-sky-300 hover:bg-sky-500/10 flex items-center gap-2"><PrinterIcon size={14} weight="bold" />Browser Print</button>
            {viewing?.id && <DocDownloadButtons type="ECI" id={viewing.id} testPrefix="eci-view" />}
            <button onClick={() => setViewing(null)} className="px-4 py-2 rounded-sm bg-[var(--accent)] hover:bg-blue-600 text-sm font-semibold text-white">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function F({ label, children, className = "", style }) {
  return <div className={className} style={style}><div className="overline mb-1.5">{label}</div>{children}</div>;
}
